import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  ArrowDownUp,
  BookOpen,
  CheckCircle2,
  ClipboardList,
  Copy,
  Crown,
  Database,
  Eye,
  ExternalLink,
  Filter,
  GripVertical,
  HelpCircle,
  Image,
  Images,
  LayoutDashboard,
  LockKeyhole,
  ListChecks,
  MoveDown,
  MoveRight,
  MoveUp,
  Palette,
  Pencil,
  Play,
  Plus,
  RefreshCcw,
  Rocket,
  RotateCcw,
  Save,
  Search,
  Settings,
  Shield,
  Sparkles,
  Star,
  Wrench
} from "lucide-react";
import defaultVisualsJson from "../data/public/site-visuals.json";
import {
  adminLogin,
  adminLogout,
  createAdminSeries,
  getAdminCatalog,
  getAdminDashboard,
  getAdminGithubHealth,
  getAdminMe,
  getAdminPatreonReviewQueue,
  getAdminPatreonSyncStatus,
  getAdminSiteVisuals,
  getCatalog,
  getEpisode,
  getEpisodeLink,
  getMe,
  getSiteVisuals,
  logout,
  mockLogin,
  refreshAuth,
  saveAdminEpisode,
  saveAdminPatreonReview,
  saveAdminSeries,
  saveAdminSiteVisuals,
  triggerAdminPatreonSync
} from "./lib/api";
import { AdminMe, CatalogResponse, Episode, MeResponse, Series, SiteVisuals, TierRequired } from "./lib/types";
import { getEffectiveEpisodeAccess } from "./lib/catalog-access";
import { getEpisodePrimaryAction } from "./lib/episode-actions";
import { PATREON_CONNECT_URL, PATREON_JOIN_URL, joinLabelForTier, safePatreonPostHref, upgradeLabelForTier } from "./lib/patreon-links";
import { Layout } from "./components/Layout";
import { EpisodeCard } from "./components/EpisodeCard";
import { VaultArtwork, imageForEpisode, imageSourceType, isRepeatedImportArt } from "./components/VaultArtwork";
import "./styles.css";

const defaultVisuals = defaultVisualsJson as SiteVisuals;
const safeAdminPathPattern = /^\/(?:patreon-assets\/[a-z0-9][a-z0-9-]{2,180}\/[a-f0-9]{16}\/(?:thumb|blur|poster|cover)\.webp|assets\/[a-z0-9/_-]+\.(?:svg|webp|png|jpg|jpeg))$/i;

function PatreonExternalIcon() {
  return <ExternalLink size={18} aria-hidden="true" />;
}

function pathParts() {
  return window.location.pathname.split("/").filter(Boolean);
}

function App() {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [admin, setAdmin] = useState<AdminMe | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMe().then(setMe).catch(() => setMe({ authenticated: false, accessLevel: "visitor", user: null })).finally(() => setLoading(false));
    getAdminMe().then(setAdmin).catch(() => setAdmin({ authenticated: false, csrf: null, configured: false }));
  }, []);

  if (loading) return <div className="loading">Opening the vault...</div>;

  const parts = pathParts();
  const route = parts[0] || "";

  if (route === "login") return <LoginPage me={me} admin={admin} setMe={setMe} />;
  if (route === "logout") return <LogoutPage setMe={setMe} />;
  if (route === "admin" && parts[1] === "login") return <AdminLogin />;

  return (
    <Layout me={me} admin={admin}>
      {route === "" ? <Home me={me} admin={admin} /> : null}
      {route === "start-here" ? <StartHere me={me} admin={admin} /> : null}
      {route === "library" ? <LibraryPage admin={admin} /> : null}
      {route === "series" && !parts[1] ? <SeriesPage admin={admin} /> : null}
      {route === "series" && parts[1] ? <SeriesDetail slug={parts[1]} admin={admin} /> : null}
      {route === "episode" && parts[1] ? <EpisodePage slug={parts[1]} me={me} admin={admin} /> : null}
      {route === "account" ? <Account me={me} admin={admin} setMe={setMe} /> : null}
      {route === "admin-manual" || route === "admin-manual.html" ? <PublicManualPage /> : null}
      {route === "admin" && parts[1] !== "help" ? <AdminPage /> : null}
      {route === "admin" && parts[1] === "help" ? <AdminHelpPage /> : null}
      {!["", "library", "series", "episode", "start-here", "account", "admin", "admin-manual", "admin-manual.html"].includes(route) ? <NotFound /> : null}
    </Layout>
  );
}

function useCatalog() {
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    getCatalog().then(setCatalog).catch((err) => setError(err.message));
  }, []);
  return { catalog, error };
}

function useSiteVisuals() {
  const [visuals, setVisuals] = useState<SiteVisuals>(defaultVisuals);
  useEffect(() => {
    getSiteVisuals().then((result) => setVisuals(result.visuals)).catch(() => setVisuals(defaultVisuals));
  }, []);
  useEffect(() => {
    document.documentElement.style.setProperty("--accent", visuals.accentColor);
    document.documentElement.style.setProperty("--accent-2", visuals.secondaryAccentColor);
    document.body.dataset.glow = visuals.premiumGlow;
  }, [visuals]);
  return visuals;
}

function tierLabel(tier: TierRequired) {
  if (tier === "free") return "Free preview";
  if (tier === "paid") return "Members+";
  return "Platinum";
}

function episodeBadgeLabel(episode: Episode) {
  return getEffectiveEpisodeAccess(episode).accessBadgeLabel;
}

function episodeBadgeTier(episode: Episode) {
  return getEffectiveEpisodeAccess(episode).effectiveTierRequired;
}

function episodeUnlockCopy(episode: Episode) {
  const effective = getEffectiveEpisodeAccess(episode);
  if (effective.isProgressiveCut) return "Members+ unlocks this Progressive Cut after Patreon confirms membership.";
  if (effective.isPlatinumEarlyAccess) return `Platinum unlocks this Early Access release now. It opens to All Paid Members on ${dateLabel(effective.platinumUnlocksForAllPaidMembersOn || "")}.`;
  if (effective.isAllPaidMembers) return "Released from Platinum Early Access after 5 months; Members+ and Platinum can unlock it.";
  return "Free preview available. Join Patreon when you want the full vault.";
}

function episodeSupportingNote(episode: Episode) {
  const effective = getEffectiveEpisodeAccess(episode);
  if (effective.isProgressiveCut) return "A distinct Members+ category for progressive member cuts.";
  if (effective.isPlatinumEarlyAccess) return `Moves to All Paid Members on ${dateLabel(effective.platinumUnlocksForAllPaidMembersOn || "")}.`;
  if (effective.isAllPaidMembers) return "Released from Platinum Early Access after 5 months.";
  return "No public video playback is shown unless a real public preview exists.";
}

function dateLabel(value?: string) {
  if (!value) return "";
  return new Date(value).toLocaleDateString();
}

function publicSeriesTitle(slug?: string, title?: string) {
  if (slug === "uncategorized-review" || /needs mapping review/i.test(title || "")) return "Vault Updates";
  return title || "Vault Updates";
}

function publicSeriesDescription(series: Pick<Series, "slug" | "title" | "description">) {
  if (series.slug === "uncategorized-review" || /needs mapping review/i.test(series.title)) {
    return "Creator notes, orientation posts, and uncategorized vault releases kept in one clean shelf.";
  }
  return series.description
    .replace(/Imported posts that need manual character or source mapping before they belong in a curated shelf\./gi, "Creator updates and uncategorized vault releases.")
    .replace(/source mapping/gi, "vault placement");
}

function publicEpisodeSeriesTitle(episode: Pick<Episode, "series_slug" | "series">) {
  return publicSeriesTitle(episode.series_slug, episode.series);
}

function accessCountsForEpisodes(episodes: Episode[]) {
  return episodes.reduce((counts, episode) => {
    const effective = getEffectiveEpisodeAccess(episode);
    if (effective.isProgressiveCut) counts.progressive += 1;
    else if (effective.isPlatinumEarlyAccess) counts.platinum += 1;
    else if (effective.isAllPaidMembers) counts.allPaid += 1;
    else counts.free += 1;
    return counts;
  }, { free: 0, progressive: 0, platinum: 0, allPaid: 0 });
}

function AccessBenefitChips() {
  return (
    <div className="benefit-chip-row" aria-label="Vault access benefits">
      <span><Eye size={15} />Free Previews</span>
      <span><Sparkles size={15} />Members+ Progressive Cuts</span>
      <span><Crown size={15} />Platinum Early Access</span>
    </div>
  );
}

function PublicConversionBand({ title, text, joinLabel = "Join Patreon" }: { title: string; text: string; joinLabel?: string }) {
  return (
    <section className="conversion-band premium-conversion-band" aria-label="Join Patreon">
      <div>
        <span className="eyebrow">Unlock the vault</span>
        <h2>{title}</h2>
        <p>{text}</p>
        <AccessBenefitChips />
        <p className="aging-note">Members+ unlocks Progressive Cuts and aged Platinum releases. Platinum unlocks newest Early Access plus everything Members+ gets. Platinum Early Access becomes All Paid Members after five months.</p>
      </div>
      <div className="button-row compact-actions conversion-actions">
        <a className="primary hero-primary-cta" href={PATREON_JOIN_URL}><PatreonExternalIcon />{joinLabel}</a>
        <a className="secondary" href={PATREON_CONNECT_URL}><Shield size={18} />Connect Patreon</a>
        <a className="secondary" href="/library?tier=free"><Sparkles size={18} />Browse Free Previews</a>
      </div>
    </section>
  );
}

function TierCountChips({ episodes }: { episodes: Episode[] }) {
  const counts = accessCountsForEpisodes(episodes);
  return (
    <div className="tier-count-grid" aria-label="Access counts">
      <span><strong>{counts.free}</strong>Free Preview</span>
      <span><strong>{counts.progressive}</strong>Members+</span>
      <span><strong>{counts.platinum}</strong>Platinum Early Access</span>
      <span><strong>{counts.allPaid}</strong>All Paid Members</span>
    </div>
  );
}

function realPath(path?: string) {
  return Boolean(path && /^\/patreon-assets\//i.test(path) && !isRepeatedImportArt(path));
}

function realArtworkPaths(episode: Episode) {
  return [episode.thumbnail_image, episode.poster_image, episode.blurred_preview_image, episode.series_cover_image].filter(realPath) as string[];
}

function allArtworkPaths(episode: Episode) {
  return [
    episode.thumbnail_image,
    episode.poster_image,
    episode.blurred_preview_image,
    episode.series_cover_image,
    episode.public_thumbnail,
    episode.member_thumbnail
  ].filter(Boolean) as string[];
}

function adminReorderActive(admin?: AdminMe | null) {
  if (!admin?.authenticated || typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("adminReorder") === "1";
}

function applySlugOrder<T extends { slug: string }>(items: T[], order: string[] = []) {
  if (!order.length) return items;
  const rank = new Map(order.map((slug, index) => [slug, index]));
  return [...items].sort((a, b) => {
    const aRank = rank.has(a.slug) ? rank.get(a.slug)! : Number.MAX_SAFE_INTEGER;
    const bRank = rank.has(b.slug) ? rank.get(b.slug)! : Number.MAX_SAFE_INTEGER;
    return aRank - bRank;
  });
}

function representativeArtwork(episodes: Episode[]) {
  const real = episodes.flatMap(realArtworkPaths)[0];
  if (real) return real;
  const fallback = episodes.find((episode) => episode.series_cover_image || episode.poster_image || episode.thumbnail_image || episode.blurred_preview_image);
  return fallback?.series_cover_image || fallback?.poster_image || fallback?.thumbnail_image || fallback?.blurred_preview_image || "";
}

function visualEpisodeOverride(episode: Episode, visuals: SiteVisuals) {
  return visuals.episodeArtOverrides[episode.slug] || "";
}

function visualSeriesArtwork(slug: string, episodes: Episode[], visuals: SiteVisuals) {
  return visuals.seriesCoverOverrides[slug] || representativeArtwork(episodes) || "";
}

function heroArtwork(episodes: Episode[], visuals: SiteVisuals) {
  if (realPath(visuals.heroBackgroundPath)) return visuals.heroBackgroundPath;
  const catalogHero = episodes.flatMap((episode) => [episode.poster_image, episode.thumbnail_image, episode.series_cover_image]).find(realPath);
  return catalogHero || visuals.heroBackgroundPath || visuals.heroBannerPath;
}

function featuredEpisodes(episodes: Episode[], visuals: SiteVisuals, count = 8) {
  const configured = visuals.featuredEpisodeSlugs.map((slug) => episodes.find((episode) => episode.slug === slug)).filter(Boolean) as Episode[];
  return [...configured, ...episodes.filter((episode) => episode.featured || episode.starter_pick), ...episodes]
    .filter((episode, index, all) => all.findIndex((item) => item.slug === episode.slug) === index)
    .slice(0, count);
}

function newestEpisodes(episodes: Episode[], count = 10) {
  return [...episodes].sort((a, b) => b.release_date.localeCompare(a.release_date)).slice(0, count);
}

interface DiscoveryShelfSpec {
  title: string;
  shelfKey: string;
  subtitle: string;
  episodes: Episode[];
  actionHref: string;
  actionLabel: string;
}

const discoveryShelfControls = [
  { shelfKey: "featured_vault_picks", title: "Featured Vault Picks", subtitle: "Curated safe previews from across the vault." },
  { shelfKey: "free_preview_picks", title: "Start With Free Previews", subtitle: "Public-safe previews before login." },
  { shelfKey: "recently_added", title: "Newest Drops", subtitle: "Fresh vault entries, newest first." },
  { shelfKey: "progressive_cuts", title: "Members+ Progressive Cuts", subtitle: "Member cuts available to Members+." },
  { shelfKey: "platinum_early_access", title: "Platinum Early Access", subtitle: "Newest Early Access releases for Platinum members." },
  { shelfKey: "all_paid_members", title: "Aged Unlocks", subtitle: "Former Platinum entries now open to all paid members." }
];

function orderDiscoveryShelves(shelves: DiscoveryShelfSpec[], visuals: SiteVisuals) {
  const hidden = new Set(visuals.hiddenDiscoveryShelves || []);
  const rank = new Map((visuals.discoveryShelfOrder || []).map((key, index) => [key, index]));
  return shelves
    .filter((shelf) => !hidden.has(shelf.shelfKey))
    .sort((a, b) => {
      const aRank = rank.has(a.shelfKey) ? rank.get(a.shelfKey)! : Number.MAX_SAFE_INTEGER;
      const bRank = rank.has(b.shelfKey) ? rank.get(b.shelfKey)! : Number.MAX_SAFE_INTEGER;
      if (aRank !== bRank) return aRank - bRank;
      return discoveryShelfControls.findIndex((item) => item.shelfKey === a.shelfKey) - discoveryShelfControls.findIndex((item) => item.shelfKey === b.shelfKey);
    });
}

function discoveryShelves(episodes: Episode[], visuals: SiteVisuals, count = 8): DiscoveryShelfSpec[] {
  const shelves: DiscoveryShelfSpec[] = [
    {
      title: "Featured Vault Picks",
      shelfKey: "featured_vault_picks",
      subtitle: "Curated safe previews from across the vault.",
      episodes: featuredEpisodes(episodes, visuals, count),
      actionHref: "/library?sort=featured",
      actionLabel: "Open featured"
    },
    {
      title: "Start With Free Previews",
      shelfKey: "free_preview_picks",
      subtitle: "Public-safe previews before login.",
      episodes: applySlugOrder(previewEpisodes(episodes, count + 6), visuals.homepageShelfOrders.free_preview_picks).slice(0, count),
      actionHref: "/library?tier=free",
      actionLabel: "Browse previews"
    },
    {
      title: "Newest Drops",
      shelfKey: "recently_added",
      subtitle: "Fresh vault entries, newest first.",
      episodes: applySlugOrder(newestEpisodes(episodes, count + 6), visuals.homepageShelfOrders.recently_added).slice(0, count),
      actionHref: "/library?sort=newest",
      actionLabel: "Open newest"
    },
    {
      title: "Members+ Progressive Cuts",
      shelfKey: "progressive_cuts",
      subtitle: "Member cuts available to Members+ after Patreon confirms access.",
      episodes: applySlugOrder(episodes.filter((episode) => getEffectiveEpisodeAccess(episode).isProgressiveCut), visuals.homepageShelfOrders.progressive_cuts).slice(0, count),
      actionHref: "/library?tier=progressive",
      actionLabel: "Browse Members+"
    },
    {
      title: "Platinum Early Access",
      shelfKey: "platinum_early_access",
      subtitle: "Newest Early Access releases for Platinum members.",
      episodes: applySlugOrder(episodes.filter((episode) => getEffectiveEpisodeAccess(episode).isPlatinumEarlyAccess), visuals.homepageShelfOrders.platinum_early_access).slice(0, count),
      actionHref: "/library?tier=platinum_early",
      actionLabel: "Browse Platinum"
    },
    {
      title: "Aged Unlocks",
      shelfKey: "all_paid_members",
      subtitle: "Former Platinum entries now open to all paid members.",
      episodes: applySlugOrder(episodes.filter((episode) => getEffectiveEpisodeAccess(episode).isAllPaidMembers), visuals.homepageShelfOrders.all_paid_members).slice(0, count),
      actionHref: "/library?tier=all_paid",
      actionLabel: "Browse aged unlocks"
    }
  ];
  return orderDiscoveryShelves(shelves.filter((shelf) => shelf.episodes.length), visuals);
}

function libraryTierView(tier: string) {
  if (tier === "free") return {
    eyebrow: "Free previews",
    title: "Free Preview Collection",
    description: "Only public preview pages are shown here.",
    bandTitle: "Start with public previews.",
    bandText: "These pages are open before joining or connecting Patreon."
  };
  if (tier === "progressive") return {
    eyebrow: "Members+",
    title: "Members+ Progressive Cuts",
    description: "Only Progressive Cuts for Members+ and Platinum patrons are shown here.",
    bandTitle: "Members+ collection.",
    bandText: "Join Patreon, then connect when access needs to be checked."
  };
  if (tier === "platinum_early") return {
    eyebrow: "Platinum",
    title: "Platinum Early Access",
    description: "Only fresh Platinum Early Access releases are shown here.",
    bandTitle: "Platinum Early Access collection.",
    bandText: "These releases stay Platinum-only until they age into All Paid Members."
  };
  if (tier === "all_paid") return {
    eyebrow: "All Paid Members",
    title: "All Paid Members Collection",
    description: "Only aged Platinum releases now open to Members+ and Platinum patrons are shown here.",
    bandTitle: "All Paid Members collection.",
    bandText: "These posts have already moved out of Platinum Early Access in the portal."
  };
  return null;
}

function previewEpisodes(episodes: Episode[], count = 8) {
  const previews = episodes.filter((episode) => episode.tier_required === "free" || episode.visibility === "public_preview");
  return [
    ...previews.filter((episode) => realArtworkPaths(episode).length),
    ...previews.filter((episode) => !realArtworkPaths(episode).length)
  ].slice(0, count);
}

function HeroPosterMontage({ episodes, visuals }: { episodes: Episode[]; visuals: SiteVisuals }) {
  const picks = uniqueEpisodes([
    ...featuredEpisodes(episodes, visuals, 8),
    ...previewEpisodes(episodes, 8),
    ...newestEpisodes(episodes, 8)
  ]).slice(0, 7);
  if (!picks.length) return null;
  return (
    <div className="hero-montage" aria-label="Featured safe vault previews">
      {picks.map((episode, index) => (
        <a className={`hero-montage-card montage-card-${index + 1}`} href={`/episode/${episode.slug}`} key={episode.slug} aria-label={`Open ${episode.title}`}>
          <VaultArtwork episode={episode} src={visualEpisodeOverride(episode, visuals)} title={episode.title} unlocked={episode.access.unlocked} tier={episodeBadgeTier(episode)} variant={index === 0 ? "poster" : "cover"} className="hero-montage-art" fallbackSrc={visuals.fallbackEpisodeArtPath} lockedFallbackSrc={visuals.lockedPreviewArtPath} seed={`hero-${episode.slug}`} />
          <span>{episodeBadgeLabel(episode)}</span>
        </a>
      ))}
    </div>
  );
}

function VaultPreviewStrip({ episodes, visuals, label = "Safe preview examples" }: { episodes: Episode[]; visuals: SiteVisuals; label?: string }) {
  const picks = uniqueEpisodes(episodes).slice(0, 5);
  if (!picks.length) return null;
  return (
    <div className="vault-preview-strip" aria-label={label}>
      {picks.map((episode) => (
        <a href={`/episode/${episode.slug}`} key={episode.slug} aria-label={`Open ${episode.title}`}>
          <VaultArtwork episode={episode} src={visualEpisodeOverride(episode, visuals)} title={episode.title} unlocked={episode.access.unlocked} tier={episodeBadgeTier(episode)} variant="cover" className="preview-strip-art" fallbackSrc={visuals.fallbackEpisodeArtPath} lockedFallbackSrc={visuals.lockedPreviewArtPath} seed={`strip-${episode.slug}`} />
        </a>
      ))}
    </div>
  );
}

function SeriesHeroCollage({ episodes, visuals, title, tier }: { episodes: Episode[]; visuals: SiteVisuals; title: string; tier: TierRequired }) {
  const picks = uniqueEpisodes(episodes).slice(0, 5);
  if (!picks.length) {
    return <VaultArtwork src={visuals.defaultSeriesCoverPath} title={title} subtitle="Series shelf" tier={tier} variant="cover" className="series-hero-art" seed={title} />;
  }
  return (
    <div className={`series-hero-collage item-count-${Math.min(picks.length, 5)}`} aria-label={`${title} safe artwork collage`}>
      {picks.map((episode, index) => (
        <a href={`/episode/${episode.slug}`} className={`series-collage-tile series-collage-tile-${index + 1}`} key={episode.slug} aria-label={`Open ${episode.title}`}>
          <VaultArtwork episode={episode} src={visualEpisodeOverride(episode, visuals)} title={episode.title} unlocked={episode.access.unlocked} tier={episodeBadgeTier(episode)} variant={index === 0 ? "poster" : "cover"} className="series-hero-art" fallbackSrc={visuals.fallbackEpisodeArtPath} lockedFallbackSrc={visuals.lockedPreviewArtPath} seed={`series-${episode.slug}`} />
        </a>
      ))}
    </div>
  );
}

function sourceTypeForEpisode(episode: Episode, visuals: SiteVisuals) {
  return imageSourceType(episode, visualEpisodeOverride(episode, visuals));
}

function isSafeAdminPath(path: string) {
  return !path || safeAdminPathPattern.test(path);
}

function Home({ me, admin }: { me: MeResponse | null; admin?: AdminMe | null }) {
  const { catalog } = useCatalog();
  const visuals = useSiteVisuals();
  const episodes = catalog?.episodes || [];
  const homeShelves = discoveryShelves(episodes, visuals, 6);
  const series = catalog?.series || [];
  const vaultSize = catalog?.meta?.episode_count || episodes.length || 336;
  const counts = {
    free: episodes.filter((episode) => getEffectiveEpisodeAccess(episode).effectiveTierRequired === "free").length,
    progressive: episodes.filter((episode) => getEffectiveEpisodeAccess(episode).isProgressiveCut).length,
    platinumEarly: episodes.filter((episode) => getEffectiveEpisodeAccess(episode).isPlatinumEarlyAccess).length,
    allPaid: episodes.filter((episode) => getEffectiveEpisodeAccess(episode).isAllPaidMembers).length
  };
  const heroImage = heroArtwork(episodes, visuals);
  const newest = newestEpisodes(episodes, 1)[0];
  const newestRelease = newest?.release_date ? dateLabel(newest.release_date) : "Today";

  return (
    <section className="home page-stack">
      <div className="hero hero-premium">
        <img className="hero-media" src={heroImage} alt="" />
        <div className="hero-backdrop" />
        <div className="hero-copy">
          <span className="eyebrow">Premium animated yaoi streaming vault</span>
          <h1>{visuals.heroHeadline}</h1>
          <p className="value-line">Powered by Patreon. Curated for browsing before login.</p>
          <p>Preview the vault, join on Patreon, then connect only when access needs to be checked.</p>
          <div className="hero-actions">
            <a className="primary hero-primary-cta" href={PATREON_JOIN_URL}><PatreonExternalIcon />{visuals.homepagePrimaryCtaLabel}</a>
            <a className="secondary" href="/library?tier=free"><Sparkles size={18} />Preview First</a>
            <a className="secondary" href={PATREON_CONNECT_URL}><Shield size={18} />Connect Patreon</a>
          </div>
        </div>
        <div className="hero-visual-stack">
          <HeroPosterMontage episodes={episodes} visuals={visuals} />
          <div className="vault-board cinematic-vault-board" aria-label="Vault summary">
            <div><strong>{String(vaultSize)}</strong><span>episodes organized</span></div>
            <div><strong>{counts.progressive}</strong><span>Members+ cuts</span></div>
            <div><strong>{counts.platinumEarly}</strong><span>Platinum early</span></div>
            <div><strong>{counts.allPaid}</strong><span>aged unlocks</span></div>
            <div><strong>{counts.free}</strong><span>free previews</span></div>
            <div><strong>{newestRelease}</strong><span>newest drop</span></div>
          </div>
        </div>
      </div>

      <ConversionPath me={me} />
      {homeShelves.map((shelf) => (
        <Shelf key={shelf.shelfKey} title={shelf.title} shelfKey={shelf.shelfKey} subtitle={shelf.subtitle} episodes={shelf.episodes} visuals={visuals} admin={admin} actionHref={shelf.actionHref} actionLabel={shelf.actionLabel} />
      ))}
      <FeaturedSeries catalog={catalog} visuals={visuals} admin={admin} />
      <MembershipComparison visuals={visuals} />
      <HowItWorks />
      {series.length ? null : <EmptyState title="Catalog loading" message="The vault shelves will populate when catalog data is available." />}
    </section>
  );
}

function Shelf({ title, shelfKey, subtitle, episodes, visuals, admin, actionHref, actionLabel }: { title: string; shelfKey: string; subtitle: string; episodes: Episode[]; visuals: SiteVisuals; admin?: AdminMe | null; actionHref?: string; actionLabel?: string }) {
  if (!episodes.length) return null;
  return (
    <section className="shelf">
      <div className="section-head">
        <div>
          <span className="eyebrow">{subtitle}</span>
          <h2>{title}</h2>
        </div>
        <div className="button-row compact-actions">
          {actionHref && actionLabel ? <a className="secondary small-link" href={actionHref}>{actionLabel}</a> : null}
          {!/free_preview|recently_added|start_here/i.test(shelfKey) ? <a className="primary small-link" href={PATREON_JOIN_URL}><PatreonExternalIcon />Join Patreon</a> : null}
        </div>
      </div>
      <AdminReorderMode admin={admin} visuals={visuals} scope="homepage" orderKey={shelfKey} title={title} episodes={episodes} />
      <div className="shelf-row">{episodes.map((episode) => <EpisodeCard key={episode.slug} episode={episode} visuals={visuals} admin={admin} />)}</div>
    </section>
  );
}

type ReorderScope = "homepage" | "library" | "series";

function AdminReorderMode({ admin, visuals, scope, orderKey, title, episodes }: { admin?: AdminMe | null; visuals: SiteVisuals; scope: ReorderScope; orderKey: string; title: string; episodes: Episode[] }) {
  const enabled = adminReorderActive(admin);
  const [ordered, setOrdered] = useState(episodes);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [status, setStatus] = useState("");

  useEffect(() => {
    setOrdered(episodes);
  }, [episodes.map((episode) => episode.slug).join("|")]);

  if (!enabled) return null;

  function move(from: number, to: number) {
    if (to < 0 || to >= ordered.length) return;
    setOrdered((current) => {
      const next = [...current];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
    setStatus("Order changed. Save when it looks right.");
  }

  async function saveOrder() {
    if (!admin?.csrf) {
      setStatus("Admin session is missing. Log in again.");
      return;
    }
    try {
      const current = await getAdminSiteVisuals();
      const slugs = ordered.map((episode) => episode.slug);
      const next: SiteVisuals = {
        ...current.visuals,
        libraryFeaturedOrder: scope === "library" ? slugs : current.visuals.libraryFeaturedOrder,
        homepageShelfOrders: scope === "homepage"
          ? { ...current.visuals.homepageShelfOrders, [orderKey]: slugs }
          : current.visuals.homepageShelfOrders,
        seriesEpisodeOrders: scope === "series"
          ? { ...current.visuals.seriesEpisodeOrders, [orderKey]: slugs }
          : current.visuals.seriesEpisodeOrders
      };
      await saveAdminSiteVisuals(admin.csrf, next);
      setStatus("Saved. Refresh the page to see the persisted order everywhere.");
    } catch {
      setStatus("Could not save here. Settings may be read-only in this environment.");
    }
  }

  return (
    <div className="admin-reorder-panel">
      <div className="admin-reorder-head">
        <div>
          <strong><GripVertical size={18} />Reorder mode: {title}</strong>
          <span>Drag tiles or use Move up / Move down. Normal card clicks stay unchanged outside reorder mode.</span>
        </div>
        <div className="button-row compact-actions">
          <button className="primary" type="button" onClick={saveOrder}><Save size={17} />Save order</button>
          <a className="secondary" href={window.location.pathname}><RotateCcw size={16} />Cancel</a>
        </div>
      </div>
      {status ? <div className={status.startsWith("Saved") ? "inline-success" : "inline-warning"}>{status}</div> : null}
      <div className="admin-reorder-grid">
        {ordered.map((episode, index) => (
          <div
            className="admin-reorder-tile"
            draggable
            key={episode.slug}
            onDragStart={() => setDragIndex(index)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => {
              if (dragIndex !== null) move(dragIndex, index);
              setDragIndex(null);
            }}
          >
            <VaultArtwork episode={episode} src={visualEpisodeOverride(episode, visuals)} title={episode.title} unlocked={false} tier={episodeBadgeTier(episode)} variant="thumb" className="reorder-tile-art" fallbackSrc={visuals.fallbackEpisodeArtPath} seed={episode.slug} />
            <span className="reorder-handle"><GripVertical size={20} />{index + 1}</span>
            <strong>{episode.title}</strong>
            <em>{episodeBadgeLabel(episode)}</em>
            <div className="tile-move-buttons">
              <button type="button" onClick={() => move(index, index - 1)} disabled={index === 0}><MoveUp size={16} />Move up</button>
              <button type="button" onClick={() => move(index, index + 1)} disabled={index === ordered.length - 1}><MoveDown size={16} />Move down</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminSeriesReorderMode({ admin, visuals, title, series }: { admin?: AdminMe | null; visuals: SiteVisuals; title: string; series: Series[] }) {
  const enabled = adminReorderActive(admin);
  const [ordered, setOrdered] = useState(series);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [status, setStatus] = useState("");

  useEffect(() => {
    setOrdered(series);
  }, [series.map((item) => item.slug).join("|")]);

  if (!enabled) return null;

  function move(from: number, to: number) {
    if (to < 0 || to >= ordered.length) return;
    setOrdered((current) => {
      const next = [...current];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
    setStatus("Series order changed. Save when it looks right.");
  }

  async function saveOrder() {
    if (!admin?.csrf) {
      setStatus("Admin session is missing. Log in again.");
      return;
    }
    try {
      const current = await getAdminSiteVisuals();
      await saveAdminSiteVisuals(admin.csrf, { ...current.visuals, featuredSeriesSlugs: ordered.map((item) => item.slug) });
      setStatus("Saved series order.");
    } catch {
      setStatus("Could not save series order. Settings may be read-only in this environment.");
    }
  }

  return (
    <div className="admin-reorder-panel">
      <div className="admin-reorder-head">
        <div>
          <strong><GripVertical size={18} />Reorder mode: {title}</strong>
          <span>These series tiles behave like large tablet icons while Admin reorder mode is on.</span>
        </div>
        <button className="primary" type="button" onClick={saveOrder}><Save size={17} />Save series order</button>
      </div>
      {status ? <div className={status.startsWith("Saved") ? "inline-success" : "inline-warning"}>{status}</div> : null}
      <div className="admin-reorder-grid">
        {ordered.map((item, index) => (
          <div
            className="admin-reorder-tile"
            draggable
            key={item.slug}
            onDragStart={() => setDragIndex(index)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => {
              if (dragIndex !== null) move(dragIndex, index);
              setDragIndex(null);
            }}
          >
            <VaultArtwork src={visuals.seriesCoverOverrides[item.slug] || visuals.defaultSeriesCoverPath} title={item.title} subtitle="Series shelf" tier="paid" variant="cover" className="reorder-tile-art" seed={item.slug} />
            <span className="reorder-handle"><GripVertical size={20} />{index + 1}</span>
            <strong>{item.title}</strong>
            <em>Series shelf</em>
            <div className="tile-move-buttons">
              <button type="button" onClick={() => move(index, index - 1)} disabled={index === 0}><MoveUp size={16} />Move up</button>
              <button type="button" onClick={() => move(index, index + 1)} disabled={index === ordered.length - 1}><MoveDown size={16} />Move down</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AccessExplainer() {
  return (
    <section className="access-explainer" aria-label="Access types">
      <InfoBlock icon={<Eye />} title="Preview" text="Open public preview pages and confirm the vault style before joining." />
      <InfoBlock icon={<Sparkles />} title="Members+" text="Progressive Cuts plus aged Platinum releases after the five-month unlock." />
      <InfoBlock icon={<Crown />} title="Platinum" text="Fresh Early Access releases first, plus everything Members+ can open." />
      <InfoBlock icon={<Shield />} title="Protected" text="Member-only releases open only after Patreon confirms access." />
    </section>
  );
}

function ConversionPath({ me }: { me: MeResponse | null }) {
  const connected = Boolean(me?.authenticated);
  return (
    <section className="conversion-path" aria-label="Simple membership path">
      <a className="conversion-step" href="/library?tier=free">
        <span><Eye size={22} /></span>
        <strong>1. Preview</strong>
        <em>Browse first</em>
      </a>
      <a className="conversion-step featured-step" href={PATREON_JOIN_URL}>
        <span><Crown size={22} /></span>
        <strong>2. Join</strong>
        <em>Pay on Patreon</em>
      </a>
      <a className="conversion-step" href={connected ? "/account" : PATREON_CONNECT_URL}>
        <span><Shield size={22} /></span>
        <strong>3. Connect</strong>
        <em>{connected ? "Account ready" : "Verify access"}</em>
      </a>
    </section>
  );
}

function FeaturedSeries({ catalog, visuals, admin }: { catalog: CatalogResponse | null; visuals: SiteVisuals; admin?: AdminMe | null }) {
  if (!catalog?.series?.length) return null;
  const selected = applySlugOrder([
    ...visuals.featuredSeriesSlugs.map((slug) => catalog.series.find((series) => series.slug === slug)).filter(Boolean) as Series[],
    ...catalog.series.filter((series) => series.featured || series.starter_pick),
    ...catalog.series
  ].filter((series, index, all) => all.findIndex((item) => item.slug === series.slug) === index), visuals.featuredSeriesSlugs).slice(0, 8);
  return (
    <section className="shelf">
      <div className="section-head">
        <div>
          <span className="eyebrow">Featured series and shelves</span>
          <h2>Series Worth Browsing</h2>
        </div>
        <a className="secondary small-link" href="/series">View series</a>
      </div>
      <AdminSeriesReorderMode admin={admin} visuals={visuals} title="Series Worth Browsing" series={selected} />
      <div className="series-strip">
        {selected.map((series) => {
          const episodes = catalog.episodes.filter((episode) => episode.series_slug === series.slug);
          const safeTitle = publicSeriesTitle(series.slug, series.title);
          return (
            <a className="source-tile" href={`/series/${series.slug}`} key={series.slug}>
              <VaultArtwork src={visualSeriesArtwork(series.slug, episodes, visuals)} title={safeTitle} subtitle={`${episodes.length} episodes`} tier={episodes.some((episode) => getEffectiveEpisodeAccess(episode).effectiveTierRequired === "platinum") ? "platinum" : "paid"} variant="cover" className="series-cover" seed={series.slug} />
              <strong>{safeTitle}</strong>
              <span>{episodes.length} episodes</span>
            </a>
          );
        })}
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="how-it-works">
      <div className="section-head">
        <div>
          <span className="eyebrow">How it works</span>
          <h2>Browse first, connect only when access matters.</h2>
        </div>
      </div>
      <div className="start-grid">
        <InfoBlock icon={<BookOpen />} title="Browse previews" text="Use Library, Series, filters, and search to inspect public preview pages before joining." />
        <InfoBlock icon={<Crown />} title="Join Patreon" text="Membership, tier changes, pricing, and billing stay on Patreon." />
        <InfoBlock icon={<Shield />} title="Connect Patreon" text="Existing members connect here so the portal can check membership securely." />
        <InfoBlock icon={<Play />} title="Open releases" text="Eligible releases open after Patreon confirms membership." />
      </div>
    </section>
  );
}

function MembershipComparison({ visuals }: { visuals: SiteVisuals }) {
  return (
    <section className="membership-panel" id="membership-options">
      <div className="section-head">
        <div>
          <span className="eyebrow">Membership path</span>
          <h2>Preview pages stay public. Unlocks stay Patreon-led.</h2>
          <p>Pricing and billing remain on Patreon. This portal only organizes the vault and checks the access tier when you connect.</p>
        </div>
        <div className="button-row compact-actions">
          <a className="primary" href={PATREON_JOIN_URL}><PatreonExternalIcon />Join Patreon</a>
          <a className="secondary" href={PATREON_CONNECT_URL}><Shield size={18} />Connect Patreon</a>
        </div>
      </div>
      <div className="membership-grid">
        <div className="info-block membership-option">
          <Eye />
          <h2>Free preview</h2>
          <p>Browse public preview pages first, then connect Patreon only when you want access checked.</p>
          <a className="secondary small-link" href="/library?tier=free">Browse previews</a>
        </div>
        <div className="info-block membership-option">
          <Sparkles />
          <h2>Members+ Progressive Cuts</h2>
          <p>Members+ unlocks Progressive Cuts and aged Platinum releases after Patreon confirms eligibility.</p>
          <a className="secondary small-link" href={PATREON_JOIN_URL}><PatreonExternalIcon />Join Patreon</a>
        </div>
        <div className="info-block membership-option">
          <Crown />
          <h2>Platinum Early Access</h2>
          <p>Platinum unlocks newest Early Access releases plus everything Members+ can open.</p>
          <a className="secondary small-link" href={PATREON_JOIN_URL}><PatreonExternalIcon />Join Patreon</a>
        </div>
      </div>
    </section>
  );
}

function LibraryPage({ admin }: { admin?: AdminMe | null }) {
  const { catalog, error } = useCatalog();
  const visuals = useSiteVisuals();
  const params = new URLSearchParams(window.location.search);
  const [query, setQuery] = useState("");
  const [series, setSeries] = useState(params.get("series") || "all");
  const [tier, setTier] = useState(params.get("tier") || "all");
  const [tag, setTag] = useState(params.get("tag") || "all");
  const [sort, setSort] = useState(params.get("sort") || "newest");
  const [message, setMessage] = useState("");
  const [visibleCount, setVisibleCount] = useState(48);

  const filtered = useMemo(() => {
    const all = catalog?.episodes || [];
    const q = query.toLowerCase().trim();
    return all
      .filter((episode) => series === "all" || episode.series_slug === series)
      .filter((episode) => {
        const effective = getEffectiveEpisodeAccess(episode);
        if (tier === "all") return true;
        if (tier === "free") return effective.effectiveTierRequired === "free";
        if (tier === "progressive") return effective.isProgressiveCut;
        if (tier === "platinum_early") return effective.isPlatinumEarlyAccess;
        if (tier === "all_paid") return effective.isAllPaidMembers;
        return effective.effectiveTierRequired === tier;
      })
      .filter((episode) => tag === "all" || episode.tags.includes(tag))
      .filter((episode) => !q || [episode.title, episode.series, episode.pairing, ...episode.characters, ...episode.tags].join(" ").toLowerCase().includes(q))
      .sort((a, b) => {
        if (sort === "oldest") return a.release_date.localeCompare(b.release_date);
        if (sort === "title") return a.title.localeCompare(b.title);
        if (sort === "tier") return episodeBadgeLabel(a).localeCompare(episodeBadgeLabel(b)) || b.release_date.localeCompare(a.release_date);
        if (sort === "featured") {
          const rank = new Map(visuals.libraryFeaturedOrder.map((slug, index) => [slug, index]));
          const aRank = rank.has(a.slug) ? rank.get(a.slug)! : Number.MAX_SAFE_INTEGER;
          const bRank = rank.has(b.slug) ? rank.get(b.slug)! : Number.MAX_SAFE_INTEGER;
          if (aRank !== bRank) return aRank - bRank;
          return Number(Boolean(b.featured || b.starter_pick)) - Number(Boolean(a.featured || a.starter_pick)) || b.release_date.localeCompare(a.release_date);
        }
        return b.release_date.localeCompare(a.release_date);
      });
  }, [catalog, query, series, tier, tag, sort, visuals.libraryFeaturedOrder]);

  useEffect(() => {
    setVisibleCount(48);
  }, [query, series, tier, tag, sort]);

  function resetFilters() {
    setQuery("");
    setSeries("all");
    setTier("all");
    setTag("all");
    setSort("newest");
    setMessage("");
  }

  if (error) return <ErrorBlock message={error} />;
  if (!catalog) return <div className="loading">Loading Library...</div>;
  const activeFilters = Boolean(query || series !== "all" || tier !== "all" || tag !== "all" || sort !== "newest");
  const tags = [...new Set(catalog.episodes.flatMap((episode) => episode.tags))].sort();
  const visibleEpisodes = filtered.slice(0, visibleCount);
  const libraryShelves = discoveryShelves(catalog.episodes, visuals, 8);
  const focusedTierView = libraryTierView(tier);
  const showDiscoveryShelves = !activeFilters;

  return (
    <section className="library page-stack">
      <div className="library-hero">
        <div>
          <span className="eyebrow">{focusedTierView?.eyebrow || "Vault Library"}</span>
          <h1>{focusedTierView?.title || "Browse curated vault previews"}</h1>
          <p>{focusedTierView?.description || "Start with a focused shelf, then search the full vault when you know what you want."}</p>
          <div className="button-row compact-actions">
            <a className="primary hero-primary-cta" href={PATREON_JOIN_URL}><PatreonExternalIcon />{visuals.homepagePrimaryCtaLabel}</a>
            <a className="secondary" href="/library?tier=free"><Sparkles size={18} />Preview First</a>
            <a className="secondary" href={PATREON_CONNECT_URL}><Shield size={18} />Connect Patreon</a>
          </div>
        </div>
        <div className="library-stats">
          <span><strong>{activeFilters ? filtered.length : catalog.episodes.length}</strong>{focusedTierView ? "collection posts" : "episodes"}</span>
          <span><strong>{visibleEpisodes.length}</strong>shown now</span>
          <span className="access-chip">{catalog.accessLevel.replace("_", " ")}</span>
        </div>
      </div>

      <div className="filter-shell" aria-label="Library filters">
        <label className="search-box library-search"><Search size={18} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search title, series, or character" /></label>
        <div className="filters compact-filters">
          <label><BookOpen size={17} /><select value={series} onChange={(event) => setSeries(event.target.value)}><option value="all">All series</option>{catalog.series.map((item) => <option key={item.slug} value={item.slug}>{publicSeriesTitle(item.slug, item.title)}</option>)}</select></label>
          <label><Crown size={17} /><select value={tier} onChange={(event) => setTier(event.target.value)}><option value="all">All access types</option><option value="free">Free previews</option><option value="progressive">Progressive Cuts</option><option value="platinum_early">Platinum Early Access</option><option value="all_paid">All Paid Members</option></select></label>
          <label><ArrowDownUp size={17} /><select value={sort} onChange={(event) => setSort(event.target.value)}><option value="newest">Newest first</option><option value="featured">Featured order</option><option value="oldest">Oldest first</option><option value="title">Title A-Z</option><option value="tier">Tier</option></select></label>
          <button className="secondary filter-reset" type="button" onClick={resetFilters} disabled={!activeFilters}><RotateCcw size={16} />Reset</button>
        </div>
        <details className="more-filters">
          <summary><Filter size={17} />More filters</summary>
          <div className="filters compact-filters">
            <label><Filter size={17} /><select value={tag} onChange={(event) => setTag(event.target.value)}><option value="all">All tags</option>{tags.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
          </div>
        </details>
      </div>

      <PublicConversionBand
        title={focusedTierView?.bandTitle || visuals.libraryCtaHeadline || "Preview the vault. Join Patreon to unlock the full episodes."}
        text={focusedTierView?.bandText || "Browse free samples here, then join Patreon for the paid vault. Existing members can connect Patreon to unlock eligible releases."}
        joinLabel={visuals.homepagePrimaryCtaLabel}
      />

      {showDiscoveryShelves ? (
        <div className="library-quick-shelves" aria-label="Curated discovery shelves">
          {libraryShelves.map((shelf) => (
            <Shelf key={shelf.shelfKey} title={shelf.title} shelfKey={`library_${shelf.shelfKey}`} subtitle={shelf.subtitle} episodes={shelf.episodes} visuals={visuals} admin={admin} actionHref={shelf.actionHref} actionLabel={shelf.actionLabel} />
          ))}
        </div>
      ) : null}

      {message ? <div className="inline-warning">{message}</div> : null}
      <AdminReorderMode admin={admin} visuals={visuals} scope="library" orderKey="library_featured" title="Library Featured Order" episodes={filtered.slice(0, 24)} />
      {filtered.length ? (
        <>
          <div className="library-result-bar">
            <strong>Showing {visibleEpisodes.length} of {filtered.length}</strong>
            <span>Search and filters still check all {catalog.episodes.length} vault entries.</span>
          </div>
          <div className="episode-grid">{visibleEpisodes.map((episode) => <EpisodeCard key={episode.slug} episode={episode} onLinkError={setMessage} visuals={visuals} admin={admin} />)}</div>
          {visibleEpisodes.length < filtered.length ? (
            <div className="show-more-row">
              <button className="secondary" type="button" onClick={() => setVisibleCount((current) => current + 48)}><Plus size={17} />Show More</button>
            </div>
          ) : null}
        </>
      ) : (
        <EmptyState title="No matching episodes" message="Clear a filter or try a different title, series, character, or tag." action={<button className="secondary" type="button" onClick={resetFilters}><RotateCcw size={16} />Reset filters</button>} />
      )}
    </section>
  );
}

function SeriesPage({ admin }: { admin?: AdminMe | null }) {
  const { catalog, error } = useCatalog();
  const visuals = useSiteVisuals();
  if (error) return <ErrorBlock message={error} />;
  if (!catalog) return <div className="loading">Loading series...</div>;
  const orderedSeries = applySlugOrder(catalog.series, visuals.featuredSeriesSlugs);
  return (
    <section className="library series-index page-stack">
      <div className="series-index-hero">
        <div>
          <span className="eyebrow">Premium collections</span>
          <h1>Series shelves</h1>
          <p>Browse by world, pairing, and release style. Each shelf stays public-safe until Patreon confirms access.</p>
          <div className="button-row compact-actions">
            <a className="primary hero-primary-cta" href={PATREON_JOIN_URL}><PatreonExternalIcon />{visuals.homepagePrimaryCtaLabel}</a>
            <a className="secondary" href="/library?tier=free"><Sparkles size={18} />Start with previews</a>
            <a className="secondary" href={PATREON_CONNECT_URL}><Shield size={18} />Connect Patreon</a>
          </div>
        </div>
        <HeroPosterMontage episodes={catalog.episodes} visuals={visuals} />
      </div>
      <div className="series-grid">
        {orderedSeries.map((item) => {
          const episodes = catalog.episodes.filter((episode) => episode.series_slug === item.slug);
          const latest = episodes[0]?.release_date || "";
          const tiers = [...new Set(episodes.map(episodeBadgeLabel))];
          const artTier = episodes.some((episode) => episodeBadgeTier(episode) === "platinum") ? "platinum" : episodes.some((episode) => episodeBadgeTier(episode) === "paid") ? "paid" : "free";
          return (
            <a className="series-card rich-series-card premium-collection-card" href={`/series/${item.slug}`} key={item.slug}>
              <VaultArtwork src={visualSeriesArtwork(item.slug, episodes, visuals)} title={publicSeriesTitle(item.slug, item.title)} subtitle={`${episodes.length} episodes`} tier={artTier} variant="cover" className="series-cover" seed={item.slug} />
              <h2>{publicSeriesTitle(item.slug, item.title)}</h2>
              <p>{publicSeriesDescription(item)}</p>
              <span>{episodes.length} episodes{latest ? ` - newest ${dateLabel(latest)}` : ""}</span>
              <div className="tags">{tiers.map((tier) => <small key={tier}>{tier}</small>)}</div>
            </a>
          );
        })}
      </div>
    </section>
  );
}

function SeriesDetail({ slug, admin }: { slug: string; admin?: AdminMe | null }) {
  const { catalog, error } = useCatalog();
  const visuals = useSiteVisuals();
  if (error) return <ErrorBlock message={error} />;
  if (!catalog) return <div className="loading">Loading series...</div>;
  const series = catalog.series.find((item) => item.slug === slug);
  if (!series) return <NotFound />;
  const episodes = applySlugOrder(catalog.episodes.filter((episode) => episode.series_slug === slug), visuals.seriesEpisodeOrders[slug]);
  const tiers = [...new Set(episodes.map(episodeBadgeLabel))];
  const artTier = episodes.some((episode) => episodeBadgeTier(episode) === "platinum") ? "platinum" : "paid";
  const safeTitle = publicSeriesTitle(series.slug, series.title);
  const paidContent = episodes.some((episode) => getEffectiveEpisodeAccess(episode).effectiveTierRequired !== "free");
  const hasPlatinum = episodes.some((episode) => getEffectiveEpisodeAccess(episode).isPlatinumEarlyAccess || getEffectiveEpisodeAccess(episode).isAllPaidMembers);
  return (
    <section className="library page-stack">
      <a className="secondary small-link back-link" href="/series">Back to series</a>
      <div className="series-hero premium-series-hero">
        <SeriesHeroCollage episodes={episodes} visuals={visuals} title={safeTitle} tier={artTier} />
        <div>
          <span className="eyebrow">{episodes.length <= 3 ? "Curated mini collection" : "Series collection"}</span>
          <h1>{safeTitle}</h1>
          <p>{publicSeriesDescription(series)}</p>
          <div className="tags">{tiers.slice(0, 4).map((tag) => <span key={tag}>{tag}</span>)}</div>
          <div className="series-progress"><strong>{episodes.length}</strong><span>{episodes.length === 1 ? "episode" : "episodes"} ordered newest first</span></div>
        </div>
      </div>
      <section className="series-unlock-panel">
        <div>
          <span className="eyebrow">Unlock by membership</span>
          <h2>{(visuals.seriesCtaHeadline || "Unlock {series} releases on Patreon.").replace("{series}", safeTitle)}</h2>
          <TierCountChips episodes={episodes} />
          {hasPlatinum ? <p>Platinum Early Access opens to all paid members after five months.</p> : null}
        </div>
        <div className="button-row compact-actions">
          {paidContent ? <a className="primary hero-primary-cta" href={PATREON_JOIN_URL}><PatreonExternalIcon />{visuals.homepagePrimaryCtaLabel}</a> : null}
          <a className="secondary" href={PATREON_CONNECT_URL}><Shield size={18} />Connect Patreon</a>
          <a className="secondary" href="/library?tier=free"><Sparkles size={18} />Browse Free Previews</a>
        </div>
      </section>
      <AdminReorderMode admin={admin} visuals={visuals} scope="series" orderKey={slug} title={`${series.title} Episode Order`} episodes={episodes} />
      <div className="episode-grid">{episodes.map((episode) => <EpisodeCard key={episode.slug} episode={episode} visuals={visuals} admin={admin} />)}</div>
    </section>
  );
}

function EpisodePage({ slug, me, admin }: { slug: string; me: MeResponse | null; admin?: AdminMe | null }) {
  const [episode, setEpisode] = useState<Episode | null>(null);
  const [related, setRelated] = useState<Array<{ slug: string; title: string; release_date: string; tier_required: string; effectiveTierRequired?: string; accessBadgeLabel?: string; unlocked: boolean }>>([]);
  const [error, setError] = useState("");
  const [ctaError, setCtaError] = useState("");
  const [resolving, setResolving] = useState(false);
  const [adminCopyMessage, setAdminCopyMessage] = useState("");
  const visuals = useSiteVisuals();
  const { catalog: episodeCatalog } = useCatalog();

  useEffect(() => {
    getEpisode(slug).then((result) => { setEpisode(result.episode); setRelated(result.related); }).catch((err) => setError(err.message));
  }, [slug]);

  const moreLikeThis = useMemo(() => {
    if (!episode || !episodeCatalog?.episodes?.length) return [];
    const effective = getEffectiveEpisodeAccess(episode);
    const sameSeries = episodeCatalog.episodes.filter((item) => item.series_slug === episode.series_slug && item.slug !== episode.slug);
    const sameSource = episode.source_series_slug
      ? episodeCatalog.episodes.filter((item) => item.source_series_slug === episode.source_series_slug && item.slug !== episode.slug)
      : [];
    const sameAccess = episodeCatalog.episodes.filter((item) => {
      const itemEffective = getEffectiveEpisodeAccess(item);
      return item.slug !== episode.slug && itemEffective.accessBadgeLabel === effective.accessBadgeLabel;
    });
    return uniqueEpisodes([...sameSeries, ...sameSource, ...sameAccess]).slice(0, 8);
  }, [episode, episodeCatalog]);

  async function openUnlocked() {
    if (!episode) return;
    setResolving(true);
    setCtaError("");
    try {
      const result = await getEpisodeLink(episode.slug);
      const opened = window.open(result.url, "_blank", "noopener,noreferrer");
      if (!opened) window.location.assign(result.url);
    } catch (err) {
      const status = typeof err === "object" && err && "status" in err ? ` (${String(err.status)})` : "";
      setCtaError(`This release is not available for your current Patreon access${status}.`);
    } finally {
      setResolving(false);
    }
  }

  async function copyEpisodePost() {
    if (!postHref) return;
    await navigator.clipboard?.writeText(postHref).catch(() => undefined);
    setAdminCopyMessage("Copied");
    window.setTimeout(() => setAdminCopyMessage(""), 1400);
  }

  if (error) return <ErrorBlock message={error} />;
  if (!episode) return <div className="loading">Loading episode...</div>;
  const override = visualEpisodeOverride(episode, visuals);
  const effective = getEffectiveEpisodeAccess(episode);
  const accessLevel = episode.access.accessLevel || me?.accessLevel || "visitor";
  const primaryAction = getEpisodePrimaryAction(episode, accessLevel, "detail");
  const postHref = safePatreonPostHref(episode.post_id);
  const primaryHref = primaryAction.type === "navigate" ? primaryAction.href : "";
  const primaryExternal = /^https?:\/\//i.test(primaryHref);
  const isFreePreview = effective.originalTierRequired === "free";
  const joinTier = effective.isPlatinumEarlyAccess ? "platinum_early" : effective.isProgressiveCut ? "progressive" : effective.isAllPaidMembers ? "all_paid" : effective.effectiveTierRequired;
  const membershipLabel = accessLevel === "visitor" ? joinLabelForTier(joinTier) : upgradeLabelForTier(joinTier);
  const shouldShowMembershipAction = !episode.access.unlocked || isFreePreview;
  const shouldShowConnectAction = accessLevel === "visitor";

  return (
    <section className="episode-page cinematic-episode">
      <div className="episode-cinema-backdrop" aria-hidden="true">
        <VaultArtwork episode={episode} src={override} title={episode.title} unlocked={episode.access.unlocked} tier={effective.effectiveTierRequired} variant="poster" className="episode-backdrop-art" fallbackSrc={visuals.fallbackEpisodeArtPath} lockedFallbackSrc={visuals.lockedPreviewArtPath} seed={`backdrop-${episode.slug}`} backdropSrc={episode.blurred_preview_image} />
      </div>
      <div className="episode-poster">
        <VaultArtwork episode={episode} src={override} title={episode.title} unlocked={episode.access.unlocked} tier={effective.effectiveTierRequired} variant="poster" className={`episode-hero ${episode.access.unlocked ? "" : "locked-image"}`} fallbackSrc={visuals.fallbackEpisodeArtPath} lockedFallbackSrc={visuals.lockedPreviewArtPath} seed={`${episode.series_slug}-${episode.slug}`} backdropSrc={episode.blurred_preview_image} />
        {!episode.access.unlocked ? <div className="poster-lock"><LockKeyhole size={19} />Preview page. Full access unlocks through Patreon.</div> : null}
      </div>
      <div className="episode-detail">
        <span className={`tier tier-${effective.effectiveTierRequired}`}>{effective.accessBadgeLabel}</span>
        <h1>{episode.title}</h1>
        <p className="lead-copy">{episode.description_short}</p>
        {effective.isPlatinumEarlyAccess || effective.isAllPaidMembers || effective.isProgressiveCut ? <p className="access-note">{episodeSupportingNote(episode)}</p> : null}
        <div className="preview-callout">
          <Eye size={19} />
          <div>
            <strong>{episodeUnlockCopy(episode)}</strong>
            <span>Access is checked securely through Patreon.</span>
          </div>
        </div>
        <AccessBenefitChips />
        <div className="preview-meta">
          <a href={`/series/${episode.series_slug}`}>{publicEpisodeSeriesTitle(episode)}</a>
          {episode.pairing ? <span>{episode.pairing}</span> : null}
          <span>{dateLabel(episode.release_date)}</span>
        </div>
        <div className="tags">{episode.tags.filter((tag) => !/patreon import|platinum vault|members/i.test(tag)).slice(0, 4).map((tag) => <span key={tag}>{tag}</span>)}</div>
        <div className="unlock-panel">
          <h2>Membership benefits</h2>
          <ul>
            <li><CheckCircle2 size={16} />{effective.displayTierLabel} access after Patreon confirms membership</li>
            <li><CheckCircle2 size={16} />Members+ unlocks Progressive Cuts and aged Platinum releases</li>
            <li><CheckCircle2 size={16} />Platinum unlocks newest Early Access plus Members+ benefits</li>
          </ul>
        </div>
        {admin?.authenticated && episode.review_status === "needs_review" ? <div className="inline-warning">Admin note: this item needs a visual check.</div> : null}
        {!postHref ? <div className="inline-warning">Preview link is not available for this entry yet.</div> : null}
        {ctaError ? <div className="inline-error">{ctaError}</div> : null}
        <div className="button-row episode-actions">
          {primaryAction.type === "resolve" ? (
            <button className="primary" type="button" onClick={openUnlocked} disabled={resolving}><Play size={18} />{resolving ? "Resolving access..." : primaryAction.label}</button>
          ) : (
            <a className="primary" href={primaryHref || "/login"} target={primaryExternal ? "_blank" : undefined} rel={primaryExternal ? "noreferrer" : undefined}>
              {primaryExternal ? <PatreonExternalIcon /> : <LockKeyhole size={18} />}
              {primaryAction.label}
            </a>
          )}
          {postHref && primaryHref !== postHref ? (
            <a className="secondary" href={postHref} target="_blank" rel="noreferrer"><PatreonExternalIcon />View Patreon Post</a>
          ) : null}
          {shouldShowMembershipAction && primaryHref !== PATREON_JOIN_URL ? (
            <a className="secondary" href={PATREON_JOIN_URL}><PatreonExternalIcon />{isFreePreview ? "Join Patreon for full episodes" : membershipLabel}</a>
          ) : null}
          {shouldShowConnectAction ? <a className="secondary" href={PATREON_CONNECT_URL}><Shield size={18} />Connect Patreon</a> : null}
          <a className="secondary" href="/library?tier=free"><Sparkles size={18} />Browse free previews</a>
        </div>
        <p className="action-note">Access is checked securely through Patreon before member-only releases open.</p>
        {admin?.authenticated ? (
          <div className="admin-episode-actions">
            <strong><Settings size={17} />Admin quick controls</strong>
            <a className="secondary small-link" href={`/admin?tab=episodes&episode=${encodeURIComponent(episode.slug)}`}><Settings size={16} />Organize episode</a>
            <a className="secondary small-link" href="/admin?tab=series"><Star size={16} />Series</a>
            <a className="secondary small-link" href={`/admin?tab=episodes&episode=${encodeURIComponent(episode.slug)}`}><Image size={16} />Replace thumbnail</a>
            <a className="secondary small-link" href="/admin/fix"><Search size={16} />Fix</a>
            {postHref ? <a className="secondary small-link" href={postHref} target="_blank" rel="noreferrer"><PatreonExternalIcon />View Patreon Post</a> : null}
            {postHref ? <button className="secondary small-link" type="button" onClick={copyEpisodePost}><Copy size={16} />{adminCopyMessage || "Copy post URL"}</button> : null}
          </div>
        ) : null}
        {related.length && !moreLikeThis.length ? (
          <>
            <h2>More Like This</h2>
            <div className="related-list">
              {related.slice(0, 6).map((item) => <a key={item.slug} href={`/episode/${item.slug}`}><span>{item.title}</span><em>{item.accessBadgeLabel || tierLabel((item.effectiveTierRequired || item.tier_required) as TierRequired)}</em></a>)}
            </div>
          </>
        ) : null}
      </div>
      {moreLikeThis.length ? (
        <div className="episode-more-like-this">
          <Shelf title="More Like This" shelfKey="more_like_this" subtitle="Same series, source, or access path. No personalization claimed." episodes={moreLikeThis} visuals={visuals} admin={admin} actionHref={`/series/${episode.series_slug}`} actionLabel="Open series" />
        </div>
      ) : null}
    </section>
  );
}

function StartHere({ me, admin }: { me: MeResponse | null; admin?: AdminMe | null }) {
  const { catalog } = useCatalog();
  const visuals = useSiteVisuals();
  const previews = previewEpisodes(catalog?.episodes || [], 6);
  const vaultSize = Number(catalog?.meta?.episode_count || catalog?.episodes.length || 0);
  return (
    <section className="start-page page-stack">
      <div className="start-hero">
        <div>
          <span className="eyebrow">Start here</span>
          <h1>Preview. Join. Connect.</h1>
          <p>A short path into the vault: browse public-safe previews first, join on Patreon when ready, then connect only when access needs checking.</p>
        </div>
        <div className="button-row compact-actions">
          <a className="secondary" href="/library?tier=free"><Sparkles size={18} />Browse Previews</a>
          <a className="primary hero-primary-cta" href={PATREON_JOIN_URL}><PatreonExternalIcon />{visuals.homepagePrimaryCtaLabel}</a>
          <a className="secondary" href={PATREON_CONNECT_URL}><Shield size={18} />Connect Patreon</a>
        </div>
      </div>
      <VaultPreviewStrip episodes={previews} visuals={visuals} label="Free preview examples" />
      <div className="start-grid numbered-grid">
        <InfoBlock icon={<BookOpen />} title="1. Browse previews" text="Open Library or Series and inspect public preview pages before joining or connecting." />
        <InfoBlock icon={<Crown />} title="2. Join Patreon" text="Use Patreon for membership signup, billing, and tier changes." />
        <InfoBlock icon={<Shield />} title="3. Connect Patreon" text="Existing members connect here when they want this portal to check membership access." />
      </div>
      <div className="tier-explain start-tier-explain" aria-label="What each access level gets">
        <div><strong>Free Preview</strong><span>Browse public-safe preview pages first. The Library still shows the shape of the {vaultSize || "full"} release vault before you connect.</span></div>
        <div><strong>Members+ Progressive Cuts</strong><span>{visuals.membersTierExplanation}</span></div>
        <div><strong>Platinum Early Access</strong><span>{visuals.platinumTierExplanation}</span></div>
      </div>
      <details className="advanced-fields visitor-details">
        <summary>More ways to browse after you start</summary>
        <div className="start-grid numbered-grid">
          <InfoBlock icon={<Filter />} title="Browse by series or access" text="Use Library filters and Series pages to find the releases that match your membership." />
          <InfoBlock icon={<Play />} title="Open releases" text="Use the obvious action on each episode page after Patreon confirms your access." />
        </div>
      </details>
      <Shelf title="Free Preview Picks" shelfKey="start_here_free_preview" subtitle="A fast path into useful public preview pages." episodes={previews} visuals={visuals} admin={admin} actionHref="/library?tier=free" actionLabel="Open Library filters" />
      <div className="conversion-band">
        <div>
          <h2>Join and connect are separate steps.</h2>
          <p>Join or upgrade on Patreon first. Connect here only when you want the portal to verify your membership.</p>
        </div>
        <div className="button-row compact-actions">
          <a className="primary" href={PATREON_JOIN_URL}><PatreonExternalIcon />Join Patreon</a>
          <a className="secondary" href={me?.authenticated ? "/account" : PATREON_CONNECT_URL}><Shield size={18} />{me?.authenticated ? "Open Account" : "Connect Patreon"}</a>
        </div>
      </div>
    </section>
  );
}

function LoginPage({ me, admin, setMe }: { me: MeResponse | null; admin?: AdminMe | null; setMe: (next: MeResponse) => void }) {
  const visuals = useSiteVisuals();
  const { catalog } = useCatalog();
  const localToolsAvailable = ["localhost", "127.0.0.1"].includes(window.location.hostname);
  const examples = featuredEpisodes(catalog?.episodes || [], visuals, 5);
  const vaultSize = Number(catalog?.meta?.episode_count || catalog?.episodes.length || 0);
  async function chooseMock(state: MeResponse["accessLevel"]) {
    const next = await mockLogin(state);
    setMe(next);
    window.location.href = state === "visitor" ? "/" : "/library";
  }
  return (
    <Layout me={me} admin={admin}>
      <section className="narrow">
        <div className="notice login-notice">
          <span className="eyebrow">Membership access</span>
          <h1>Join or Connect Patreon</h1>
          <p>{vaultSize ? `Choose the path that matches what you need right now. The portal checks access for ${vaultSize} vault releases.` : "Choose the path that matches what you need right now."}</p>
          <VaultPreviewStrip episodes={examples} visuals={visuals} label="Public-safe vault examples" />
          <div className="login-choice-grid">
            <a className="login-choice primary-choice" href={PATREON_JOIN_URL} aria-label={visuals.loginJoinLabel || "Join Patreon"}>
              <Crown size={26} />
              <strong>{visuals.loginJoinLabel || "Join Patreon"}</strong>
              <span>Start or upgrade membership on Patreon to unlock paid releases.</span>
            </a>
            <a className="login-choice" href={PATREON_CONNECT_URL} aria-label="Already a member? Connect Patreon">
              <Shield size={26} />
              <strong>{visuals.loginConnectLabel || "Connect Existing Membership"}</strong>
              <span>Already a member? Connect so the portal can check access.</span>
            </a>
          </div>
          <div className="tier-explain">
            <div><strong>Free preview</strong><span>Browse preview pages first, then connect when you want access checked.</span></div>
            <div><strong>Members+ Progressive Cuts</strong><span>Unlock progressive member cuts and aged Platinum releases after Patreon confirms eligibility.</span></div>
            <div><strong>Platinum Early Access</strong><span>Open fresh Platinum releases before they move to All Paid Members after five calendar months.</span></div>
          </div>
          <p className="action-note">{visuals.loginPrivacyReassurance}</p>
          <div className="button-row">
            <a className="secondary" href="/library?tier=free">Browse previews first</a>
          </div>
        </div>
        {localToolsAvailable ? <div className="mock-panel">
          <h2>Local mock states</h2>
          <p>Only works when local mock auth is explicitly enabled. Production keeps mock auth disabled.</p>
          <div className="mock-grid">
            {(["visitor", "free_patron", "paid_patron", "platinum_patron"] as const).map((state) => (
              <button key={state} type="button" className="secondary" onClick={() => chooseMock(state)}>{state.replace("_", " ")}</button>
            ))}
          </div>
        </div> : null}
      </section>
    </Layout>
  );
}

function LogoutPage({ setMe }: { setMe: (next: MeResponse) => void }) {
  useEffect(() => {
    logout().catch(() => undefined).finally(() => {
      setMe({ authenticated: false, accessLevel: "visitor", user: null });
      window.location.href = "/";
    });
  }, [setMe]);
  return <div className="loading">Signing out...</div>;
}

function Account({ me, admin, setMe }: { me: MeResponse | null; admin?: AdminMe | null; setMe: (next: MeResponse) => void }) {
  const visuals = useSiteVisuals();
  const connected = Boolean(me?.authenticated);
  const eligible = me?.accessLevel === "paid_patron" || me?.accessLevel === "platinum_patron";
  const platinum = me?.accessLevel === "platinum_patron";
  async function refresh() {
    const next = await refreshAuth();
    setMe(next);
  }
  return (
    <section className="narrow">
      <div className="account-panel">
        <span className="eyebrow">Membership center</span>
        <h1>Account</h1>
        <p>{connected ? "Your Patreon access state is connected. Refresh when you have changed membership on Patreon." : "You are browsing as a visitor. Preview pages remain open; join on Patreon first, then connect when you want membership access checked."}</p>
        <dl>
          <dt>Patreon state</dt><dd>{connected ? "Connected" : "Visitor"}</dd>
          <dt>Access level</dt><dd>{me?.accessLevel || "visitor"}</dd>
          <dt>Email</dt><dd>{me?.user?.email || "Not provided"}</dd>
          <dt>Last refresh</dt><dd>{me?.user?.refreshedAt ? new Date(me.user.refreshedAt).toLocaleString() : "Not refreshed"}</dd>
        </dl>
        <div className="tier-explain">
          <div><strong>Free preview</strong><span>{visuals.freeTierExplanation}</span></div>
          <div><strong>Members+ Progressive Cuts</strong><span>{visuals.membersTierExplanation}</span></div>
          <div><strong>Platinum Early Access</strong><span>{visuals.platinumTierExplanation}</span></div>
        </div>
        <div className="button-row">
          {!connected ? <a className="primary" href={PATREON_JOIN_URL}><PatreonExternalIcon />Join Patreon</a> : null}
          {!connected ? <a className="secondary" href={PATREON_CONNECT_URL}><Shield size={18} />Connect Patreon</a> : null}
          {connected && !eligible ? <a className="primary" href={PATREON_JOIN_URL}><PatreonExternalIcon />Upgrade on Patreon</a> : null}
          {connected && eligible ? <a className="primary" href={platinum ? "/library?tier=platinum_early" : "/library?tier=progressive"}><BookOpen size={18} />Open eligible posts</a> : null}
          {connected ? <button className="secondary" type="button" onClick={refresh}><RefreshCcw size={17} />Refresh Patreon status</button> : null}
          <a className="secondary" href={eligible ? "/library" : "/library?tier=free"}>{eligible ? "Browse Library" : "Browse previews"}</a>
        </div>
      </div>
    </section>
  );
}

function AdminLogin() {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError("");
    try {
      await adminLogin(password);
      window.location.href = "/admin";
    } catch (err) {
      setError(err instanceof Error ? err.message : "Admin login failed");
    }
  }
  return (
    <section className="admin-login">
      <form className="admin-login-panel" onSubmit={submit}>
        <span className="eyebrow">Creator only</span>
        <h1>Admin Login</h1>
        <p>Creator catalog tools use a separate protected admin session.</p>
        <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Admin password" autoComplete="current-password" />
        {error ? <div className="inline-error">{error}</div> : null}
        <button className="primary" type="submit">Login</button>
      </form>
    </section>
  );
}

function AdminHelpPage() {
  const [admin, setAdmin] = useState<AdminMe | null>(null);
  useEffect(() => {
    getAdminMe().then((state) => {
      if (!state.authenticated) window.location.href = "/admin/login";
      setAdmin(state);
    }).catch(() => window.location.href = "/admin/login");
  }, []);
  if (!admin) return <div className="loading">Opening manual...</div>;
  return (
    <section className="admin page-stack">
      <div className="admin-header">
        <div>
          <span className="eyebrow">Creator manual</span>
          <h1>Admin Manual</h1>
          <p>Operational guide for running the portal without hunting through developer notes.</p>
        </div>
        <a className="secondary" href="/admin"><LayoutDashboard size={16} />Open Control Room</a>
      </div>
      <AdminManualContent />
    </section>
  );
}

function PublicManualPage() {
  return (
    <section className="admin page-stack">
      <div className="admin-header">
        <div>
          <span className="eyebrow">Operator guide</span>
          <h1>YaoiGen Admin Manual</h1>
          <p>A public static copy is also shipped at this path. Log in as Admin for the protected manual and live control bar.</p>
        </div>
        <a className="secondary" href="/admin/help"><HelpCircle size={16} />Protected Admin Manual</a>
      </div>
      <AdminManualContent />
    </section>
  );
}

function AdminManualContent() {
  return (
    <section className="manual-grid">
      <ManualLinkStep title="Control Room" text="What do I click? Start here, then choose the one task card that matches the job in front of you." href="/admin" label="Open Control Room" />
      <ManualLinkStep title="Launch Control" text="What do I click? Open Fix Center for visual warnings or Patreon Access for manual access tasks." href="/admin/launch" label="Open Launch" />
      <ManualLinkStep title="Organize Episodes" text="What do I click? Pick one episode thumbnail, choose Move to Series, select a series tile, then confirm." href="/admin?tab=episodes" label="Open Organize Episodes" />
      <ManualLinkStep title="Visual Series Organizer" text="What do I click? Select episode thumbnails, choose a series tile, then confirm the move. Use Add Series for one new title." href="/admin?tab=series" label="Open Series" />
      <ManualLinkStep title="Fix Center" text="What do I click? Use Move to Suggested Series, Choose Series, or Ignore for Launch after checking the thumbnail." href="/admin/fix" label="Open Fix Center" />
      <ManualLinkStep title="Patreon Access" text="What do I click? Open one Patreon post, update access on Patreon if safe, then mark the card updated here." href="/admin/patreon-aged-access" label="Open Patreon Access" />
      <ManualStep title="1. Visitor Path" text="Preview, Join Patreon, then Connect Patreon. Keep this path simple on every public page." />
      <ManualStep title="2. Membership Levels" text="Free Preview is browsing. Members+ unlocks Progressive Cuts and aged Platinum releases. Platinum unlocks fresh Early Access immediately." />
      <ManualStep title="3. Five Month Rule" text="Platinum Early Access opens to All Paid Members after five calendar months in the portal." />
      <ManualStep title="4. Move Episodes" text="Select the thumbnail, select the target series, read the confirmation, then move. Use Undo immediately if the target was wrong." />
      <ManualStep title="5. Add Or Rename Series" text="Use the visible title only. Confirm rename before saving so the old title and new title are clear." />
      <ManualStep title="6. Covers And Thumbnails" text="Pick from safe public art. Advanced details stay closed unless something is broken." />
      <ManualStep title="7. Launch Day" text="Open Launch Control, clear visual warnings, confirm Join Patreon and Connect Patreon, then verify private links stay protected." />
      <ManualStep title="8. What Not To Touch" text="Do not change Patreon pricing, billing, post creative text, private media URLs, raw imports, private link maps, secrets, generated media payloads, or public/patreon-assets directly." />
      <article id="developer-commands" className="manual-step wide-manual-step">
        <h2>Advanced / Developer Commands</h2>
        <p>Only open this if something is broken or a deployment check requires it.</p>
        <details className="advanced-fields developer-details">
          <summary>Developer Commands</summary>
          <div className="ops-links">
            <code>npm run patreon:browser-asset-sync -- --login</code>
            <code>npm run patreon:safe-thumbnail-sync:local</code>
            <code>npm run patreon:auto-sync</code>
            <code>npm run patreon:tier-map</code>
            <code>npm run patreon:aging-report</code>
            <code>npm run build</code>
            <code>npm run qa:browser-actions</code>
          </div>
        </details>
      </article>
    </section>
  );
}

function ManualStep({ title, text }: { title: string; text: string }) {
  const id = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return <article id={id} className="manual-step"><h2>{title}</h2><p>{text}</p></article>;
}

function ManualLinkStep({ title, text, href, label }: { title: string; text: string; href: string; label: string }) {
  const id = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return <article id={id} className="manual-step manual-link-step"><h2>{title}</h2><p>{text}</p><a className="secondary small-link" href={href}>{label}</a></article>;
}

type AdminTab =
  | "control"
  | "website"
  | "episodes"
  | "series"
  | "fix"
  | "launch"
  | "patreon-aging"
  | "manual"
  | "dashboard"
  | "sync"
  | "homepage"
  | "visuals"
  | "membership"
  | "featured"
  | "reorder"
  | "diagnostics";

interface PendingPatreonPost {
  post_id?: string;
  title?: string;
  publish_date?: string;
  detected_status?: string;
  review_status?: string;
  classification_state?: string;
  suggested_tier_required?: string;
  missing_fields?: string[];
  image_status?: string;
  image_candidate_status?: string;
  image_candidate_count?: number;
  gif_candidate_count?: number;
  admin_post_link_available?: boolean;
  safe_patreon_post_url_available?: boolean;
  detected_tier_ids_count?: number;
  tier_detection_source?: string;
  assignment_fields_required?: string[];
  browser_login_command?: string;
  cdp_command?: string;
  review_template_command?: string;
  next_action?: string;
}

const reviewSavingUnavailableMessage = "Saving is not ready yet. I need the site connection repaired before approvals can be saved.";

interface FixDraft {
  source_series: string;
  source_series_slug: string;
  charactersText: string;
  pairing: string;
}

const adminTabs: Array<{ key: AdminTab; label: string; icon: React.ReactNode }> = [
  { key: "control", label: "Control Room", icon: <LayoutDashboard size={16} /> },
  { key: "website", label: "Website", icon: <Palette size={16} /> },
  { key: "sync", label: "Update", icon: <RefreshCcw size={16} /> },
  { key: "episodes", label: "Organize Episodes", icon: <Play size={16} /> },
  { key: "series", label: "Series", icon: <BookOpen size={16} /> },
  { key: "fix", label: "Fix", icon: <Wrench size={16} /> },
  { key: "launch", label: "Launch", icon: <Rocket size={16} /> },
  { key: "patreon-aging", label: "Patreon Access", icon: <ClipboardList size={16} /> },
  { key: "reorder", label: "Reorder", icon: <GripVertical size={16} /> },
  { key: "manual", label: "Manual", icon: <HelpCircle size={16} /> }
];

function initialAdminTab(): AdminTab {
  if (typeof window === "undefined") return "control";
  if (window.location.pathname === "/admin/launch") return "launch";
  if (window.location.pathname === "/admin/fix") return "fix";
  if (window.location.pathname === "/admin/patreon-aged-access") return "patreon-aging";
  const tab = new URLSearchParams(window.location.search).get("tab") || "";
  if (tab === "organize") return "episodes";
  return adminTabs.some((item) => item.key === tab) ? tab as AdminTab : "control";
}

function AdminPage() {
  const [admin, setAdmin] = useState<AdminMe | null>(null);
  const [dashboard, setDashboard] = useState<Awaited<ReturnType<typeof getAdminDashboard>> | null>(null);
  const [patreonReviewQueue, setPatreonReviewQueue] = useState<Awaited<ReturnType<typeof getAdminPatreonReviewQueue>> | null>(null);
  const [githubHealth, setGithubHealth] = useState<Awaited<ReturnType<typeof getAdminGithubHealth>> | null>(null);
  const [githubHealthError, setGithubHealthError] = useState("");
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [siteVisuals, setSiteVisuals] = useState<SiteVisuals>(defaultVisuals);
  const [visualsWritable, setVisualsWritable] = useState(false);
  const [selected, setSelected] = useState<Episode | null>(null);
  const [selectedSeriesSlug, setSelectedSeriesSlug] = useState("");
  const [organizeQuery, setOrganizeQuery] = useState("");
  const [seriesQuery, setSeriesQuery] = useState("");
  const [selectedEpisodeSlugs, setSelectedEpisodeSlugs] = useState<string[]>([]);
  const [tab, setTab] = useState<AdminTab>(initialAdminTab);
  const [message, setMessage] = useState("");
  const [visualMessage, setVisualMessage] = useState("");
  const [editingFixSlug, setEditingFixSlug] = useState("");
  const [fixDrafts, setFixDrafts] = useState<Record<string, FixDraft>>({});
  const [dismissedFixSlugs, setDismissedFixSlugs] = useState<string[]>([]);
  const [seriesMessage, setSeriesMessage] = useState("");
  const [newSeriesDraft, setNewSeriesDraft] = useState({
    title: "",
    slug: "",
    description: "",
    source_series_slug: "yaoigen-originals",
    tagsText: "",
    starter_pick: false,
    featured: true
  });

  useEffect(() => {
    getAdminMe().then((state) => {
      if (!state.authenticated) window.location.href = "/admin/login";
      setAdmin(state);
    }).catch(() => window.location.href = "/admin/login");
    Promise.all([getAdminDashboard(), getAdminCatalog(), getAdminSiteVisuals()]).then(([dashboardResult, catalogResult, visualsResult]) => {
      setDashboard(dashboardResult);
      setCatalog(catalogResult);
      setSiteVisuals(visualsResult.visuals);
      setVisualsWritable(visualsResult.writable);
      const selectedSlug = new URLSearchParams(window.location.search).get("episode") || "";
      setSelected(catalogResult.episodes.find((episode) => episode.slug === selectedSlug) || catalogResult.episodes[0] || null);
      setSelectedSeriesSlug("");
    }).catch(() => window.location.href = "/admin/login");
    getAdminPatreonReviewQueue().then(setPatreonReviewQueue).catch(() => setPatreonReviewQueue(null));
    getAdminGithubHealth().then((health) => {
      setGithubHealth(health);
      setGithubHealthError("");
    }).catch((err) => {
      setGithubHealth(null);
      const body = err && typeof err === "object" && "body" in err ? (err as { body?: { error?: string; message?: string } }).body : null;
      setGithubHealthError(body?.message || body?.error || (err instanceof Error ? err.message : "Saving health check failed."));
    });
  }, []);

  function updateVisual<K extends keyof SiteVisuals>(key: K, value: SiteVisuals[K]) {
    setSiteVisuals((current) => ({ ...current, [key]: value }));
    setVisualMessage("");
  }

  function setSeriesOverride(slug: string, path: string) {
    setSiteVisuals((current) => ({ ...current, seriesCoverOverrides: { ...current.seriesCoverOverrides, [slug]: path } }));
  }

  function clearSeriesOverride(slug: string) {
    setSiteVisuals((current) => {
      const next = { ...current.seriesCoverOverrides };
      delete next[slug];
      return { ...current, seriesCoverOverrides: next };
    });
  }

  function setEpisodeOverride(slug: string, path: string) {
    setSiteVisuals((current) => ({ ...current, episodeArtOverrides: { ...current.episodeArtOverrides, [slug]: path } }));
  }

  function clearEpisodeOverride(slug: string) {
    setSiteVisuals((current) => {
      const next = { ...current.episodeArtOverrides };
      delete next[slug];
      return { ...current, episodeArtOverrides: next };
    });
  }

  async function saveVisuals() {
    if (!admin?.csrf) return;
    const invalid = [
      siteVisuals.heroBannerPath,
      siteVisuals.heroBackgroundPath,
      siteVisuals.fallbackEpisodeArtPath,
      siteVisuals.lockedPreviewArtPath,
      siteVisuals.defaultSeriesCoverPath,
      ...Object.values(siteVisuals.seriesCoverOverrides),
      ...Object.values(siteVisuals.episodeArtOverrides)
    ].filter((path) => path && !isSafeAdminPath(path));
    if (invalid.length) {
      setVisualMessage("One or more image paths are not safe. Use /patreon-assets/... or /assets/... only.");
      return;
    }
    const result = await saveAdminSiteVisuals(admin.csrf, siteVisuals);
    setSiteVisuals(result.visuals);
    setVisualMessage(`Saved visual settings to ${result.storage}.`);
  }

  function replaceCatalogEpisode(nextEpisode: Episode) {
    setCatalog((current) => current ? { ...current, episodes: current.episodes.map((episode) => episode.slug === nextEpisode.slug ? nextEpisode : episode) } : current);
    setSelected((current) => current?.slug === nextEpisode.slug ? nextEpisode : current);
  }

  function removeFromFixCenter(slugs: string[]) {
    const uniqueSlugs = [...new Set(slugs)].filter(Boolean);
    if (!uniqueSlugs.length) return;
    setDismissedFixSlugs((current) => [...new Set([...current, ...uniqueSlugs])]);
  }

  async function saveEpisode() {
    if (!selected || !admin?.csrf) return;
    const result = await saveAdminEpisode(selected.slug, admin.csrf, selected);
    setSelected(result.episode);
    replaceCatalogEpisode(result.episode);
    setMessage("Episode saved.");
  }

  function updateSelectedSeries(updates: Partial<Series>) {
    if (!selectedSeries) return;
    setCatalog((current) => current ? {
      ...current,
      series: current.series.map((series) => series.slug === selectedSeries.slug ? { ...series, ...updates } : series)
    } : current);
    setSeriesMessage("");
  }

  function seriesTagsText(series?: Series) {
    return series?.tags?.join(", ") || "";
  }

  function splitTags(value: string) {
    return value.split(",").map((item) => item.trim()).filter(Boolean).slice(0, 18);
  }

  async function refreshAdminCatalogAfterSeries(slug?: string) {
    const nextCatalog = await getAdminCatalog();
    setCatalog(nextCatalog);
    if (slug) setSelectedSeriesSlug(slug);
    const selectedSlug = selected?.slug;
    if (selectedSlug) setSelected(nextCatalog.episodes.find((episode) => episode.slug === selectedSlug) || nextCatalog.episodes[0] || null);
  }

  async function saveSelectedSeries() {
    if (!selectedSeries || !admin?.csrf) return;
    try {
      await saveAdminSeries(selectedSeries.slug, admin.csrf, { ...selectedSeries, updateEpisodes: true });
      await refreshAdminCatalogAfterSeries(selectedSeries.slug);
      setSeriesMessage("Series saved. Episode card labels using this series were updated too.");
    } catch (err) {
      const detail = err instanceof Error ? err.message : "save failed";
      setSeriesMessage(`Series could not be saved: ${detail}`);
    }
  }

  async function createSeriesFromDraft() {
    if (!admin?.csrf || !newSeriesDraft.title.trim()) {
      setSeriesMessage("Add a series title first.");
      return;
    }
    try {
      const result = await createAdminSeries(admin.csrf, {
        title: newSeriesDraft.title,
        slug: newSeriesDraft.slug,
        description: newSeriesDraft.description,
        source_series_slug: newSeriesDraft.source_series_slug,
        tags: splitTags(newSeriesDraft.tagsText),
        starter_pick: newSeriesDraft.starter_pick,
        featured: newSeriesDraft.featured,
        status: "published",
        review_status: "mapped"
      });
      await refreshAdminCatalogAfterSeries(result.series.slug);
      setNewSeriesDraft({ title: "", slug: "", description: "", source_series_slug: "yaoigen-originals", tagsText: "", starter_pick: false, featured: true });
      setSeriesMessage("New series created. Move episodes to it visually from Organize Episodes.");
    } catch (err) {
      const detail = err instanceof Error ? err.message : "create failed";
      setSeriesMessage(`Series could not be created: ${detail}`);
    }
  }

  async function createVisualSeries(title: string, coverPath?: string) {
    const cleanTitle = title.trim();
    if (!cleanTitle) {
      setSeriesMessage("Add a series title first.");
      return null;
    }
    if (!admin?.csrf) {
      setSeriesMessage("Admin session is missing. Log in again.");
      return null;
    }
    try {
      const result = await createAdminSeries(admin.csrf, {
        title: cleanTitle,
        description: `Curated YaoiGen releases grouped under ${cleanTitle}.`,
        starter_pick: false,
        featured: true,
        status: "published",
        review_status: "mapped"
      });
      if (coverPath) {
        const nextVisuals = {
          ...siteVisuals,
          seriesCoverOverrides: { ...siteVisuals.seriesCoverOverrides, [result.series.slug]: coverPath }
        };
        setSiteVisuals(nextVisuals);
        if (visualsWritable) await saveAdminSiteVisuals(admin.csrf, nextVisuals);
      }
      await refreshAdminCatalogAfterSeries(result.series.slug);
      setSeriesMessage(`${cleanTitle} created.`);
      return result.series;
    } catch (err) {
      const detail = err instanceof Error ? err.message : "create failed";
      setSeriesMessage(`Series could not be created: ${detail}`);
      return null;
    }
  }

  async function saveSeriesTitle(series: Series, title: string) {
    if (!admin?.csrf) return;
    const cleanTitle = title.trim();
    if (!cleanTitle) {
      setSeriesMessage("Add a series title first.");
      return;
    }
    try {
      const result = await saveAdminSeries(series.slug, admin.csrf, {
        title: cleanTitle,
        description: series.description || `Curated YaoiGen releases grouped under ${cleanTitle}.`,
        updateEpisodes: true
      });
      await refreshAdminCatalogAfterSeries(result.series.slug);
      setSeriesMessage(`${cleanTitle} renamed.`);
    } catch (err) {
      const detail = err instanceof Error ? err.message : "rename failed";
      setSeriesMessage(`Series could not be renamed: ${detail}`);
    }
  }

  function replaceCatalogEpisodes(nextEpisodes: Episode[]) {
    setCatalog((current) => current ? {
      ...current,
      episodes: current.episodes.map((episode) => nextEpisodes.find((item) => item.slug === episode.slug) || episode)
    } : current);
    setSelected((current) => current ? nextEpisodes.find((item) => item.slug === current.slug) || current : current);
  }

  async function moveEpisodesToSeries(slugs: string[], targetSlug: string) {
    if (!catalog) return;
    const target = catalog.series.find((series) => series.slug === targetSlug);
    if (!target) {
      setSeriesMessage("Choose a series first.");
      return;
    }
    const uniqueSlugs = [...new Set(slugs)].filter(Boolean);
    const moving = uniqueSlugs
      .map((slug) => catalog.episodes.find((episode) => episode.slug === slug))
      .filter(Boolean) as Episode[];
    if (!moving.length) {
      setSeriesMessage("Choose at least one episode.");
      return;
    }
    const optimistic = moving.map((episode) => ({ ...episode, ...episodeMovePatch(episode, target, catalog.sourceSeries || []) }));
    replaceCatalogEpisodes(optimistic);
    setSelectedEpisodeSlugs([]);
    removeFromFixCenter(uniqueSlugs);
    try {
      if (!admin?.csrf) throw new Error("missing_admin_session");
      const saved = await Promise.all(moving.map((episode) => saveAdminEpisode(episode.slug, admin.csrf!, episodeMovePatch(episode, target, catalog.sourceSeries || []))));
      replaceCatalogEpisodes(saved.map((item) => item.episode));
      setSeriesMessage(`Moved ${moving.length} episode${moving.length === 1 ? "" : "s"} to ${target.title}.`);
    } catch (err) {
      const detail = err instanceof Error ? err.message : "save failed";
      setSeriesMessage(`Moved on this screen. Production Admin can persist it; this environment returned ${detail}.`);
    }
  }

  function toggleEpisodeSelection(slug: string) {
    setSelectedEpisodeSlugs((current) => current.includes(slug) ? current.filter((item) => item !== slug) : [...current, slug]);
  }

  async function saveDiagnosticEpisode(episode: Episode, updates: Partial<Episode>, successMessage: string) {
    const optimistic = { ...episode, ...updates };
    if (updates.review_status === "mapped" || updates.review_status === "approved") removeFromFixCenter([episode.slug]);
    if (!admin?.csrf) {
      replaceCatalogEpisode(optimistic);
      setMessage("Admin session is missing. The card changed only on this screen.");
      return;
    }
    try {
      const result = await saveAdminEpisode(episode.slug, admin.csrf, updates);
      replaceCatalogEpisode(result.episode);
      setMessage(successMessage);
    } catch (err) {
      replaceCatalogEpisode(optimistic);
      const detail = err instanceof Error ? err.message : "save failed";
      setMessage(`Previewed the change on this screen. Production Admin can persist it; this environment returned ${detail}.`);
    }
  }

  function reviewedUpdates(episode: Episode, mode: "reviewed" | "ignored" | "accepted"): Partial<Episode> {
    const keepFlags = mode === "ignored" ? ["ignored_for_launch"] : [];
    return {
      review_status: mode === "ignored" ? "approved" : "mapped",
      review_flags: keepFlags,
      confidence: Math.max(Number(episode.confidence || 0), mode === "ignored" ? 0.71 : 0.75)
    };
  }

  function startFixEdit(episode: Episode) {
    setEditingFixSlug(episode.slug);
    setFixDrafts((current) => ({
      ...current,
      [episode.slug]: {
        source_series: episode.source_series || "YaoiGen Originals",
        source_series_slug: episode.source_series_slug || "yaoigen-originals",
        charactersText: episode.characters?.join(", ") || "",
        pairing: episode.pairing || ""
      }
    }));
    setMessage("");
  }

  function updateFixDraft(slug: string, next: Partial<FixDraft>) {
    setFixDrafts((current) => ({ ...current, [slug]: { ...(current[slug] || { source_series: "", source_series_slug: "", charactersText: "", pairing: "" }), ...next } }));
  }

  async function saveFixDraft(episode: Episode) {
    const draft = fixDrafts[episode.slug];
    if (!draft) return;
    const source = catalog?.sourceSeries?.find((item) => item.slug === draft.source_series_slug);
    const characters = draft.charactersText.split(",").map((item) => item.trim()).filter(Boolean);
    const next: Partial<Episode> = {
      source_series: source?.title || draft.source_series || "YaoiGen Originals",
      source_series_slug: source?.slug || draft.source_series_slug || slugifyAdmin(draft.source_series || "YaoiGen Originals"),
      characters,
      character_slugs: characters.map(slugifyAdmin),
      pairing: draft.pairing.trim(),
      pairing_slug: draft.pairing.trim() ? slugifyAdmin(draft.pairing) : "",
      review_status: "mapped",
      review_flags: [],
      confidence: 0.82
    };
    await saveDiagnosticEpisode(episode, next, "Manual metadata review saved.");
    setEditingFixSlug("");
  }

  async function copyPath(path: string) {
    await navigator.clipboard?.writeText(path).catch(() => undefined);
    setVisualMessage(path.includes("\n") || path.length > 180 ? "Checklist copied to clipboard." : "Copied.");
  }

  async function signOut() {
    await adminLogout();
    window.location.href = "/admin/login";
  }

  function openAdminTab(next: AdminTab) {
    setTab(next);
    window.history.replaceState(null, "", adminTabHref(next));
  }

  async function refreshPatreonReviewQueue() {
    const [queue, health] = await Promise.all([
      getAdminPatreonReviewQueue(),
      getAdminGithubHealth().catch(() => null)
    ]);
    setPatreonReviewQueue(queue);
    if (health) {
      setGithubHealth(health);
      setGithubHealthError("");
    }
    return queue;
  }

  if (!dashboard || !catalog || !selected) return <div className="loading">Loading admin...</div>;
  const realThumbnailCount = catalog.episodes.filter((episode) => sourceTypeForEpisode(episode, siteVisuals) === "real Patreon asset").length;
  const adminOverrideCount = catalog.episodes.filter((episode) => siteVisuals.episodeArtOverrides[episode.slug]).length;
  const fallbackCount = catalog.episodes.length - realThumbnailCount - adminOverrideCount;
  const assetCount = Number(dashboard.counts.detectedPublicAssets || dashboard.counts.plannedDerivatives || dashboard.counts.staticStagedObjects || 0);
  const selectedSeries = selectedSeriesSlug ? catalog.series.find((series) => series.slug === selectedSeriesSlug) : undefined;
  const selectedSeriesEpisodes = selectedSeries ? catalog.episodes.filter((episode) => episode.series_slug === selectedSeries.slug) : [];
  const selectedPaths = allArtworkPaths(selected);
  const organizeEpisodes = catalog.episodes
    .filter((episode) => episodeMatchesSearch(episode, organizeQuery))
    .slice(0, 120);
  const visualSeries = applySlugOrder(catalog.series.filter((series) => seriesMatchesSearch(series, seriesQuery)), siteVisuals.featuredSeriesSlugs);
  const assetPathBank = [...new Set(catalog.episodes.flatMap(allArtworkPaths).filter((path) => /^\/patreon-assets\//.test(path)))].slice(0, 64);
  const tierAging = dashboard.tierAging;
  const catalogSync = dashboard.catalogSync;
  const buttonHealth = dashboard.buttonActionHealth;
  const pendingPatreonPosts = pendingPostsFromSync(catalogSync);
  const pendingReview = pendingReviewFromSync(catalogSync);
  const pendingDetected = Number(pendingReview.detected || pendingPatreonPosts.length || 0);
  const pendingCount = Number(pendingReview.pending || catalogSync.newPostsPending || 0);
  const detectedCount = Number(catalogSync.detectedPostCount || 0);
  const catalogCount = Number(catalogSync.catalogEpisodeCount || catalog.episodes.length || 0);
  const processedCoverage = detectedCount ? `${Math.min(catalogCount, detectedCount)} / ${detectedCount}` : syncText(catalogCount);
  const browserExtraction = pendingReview.browserExtraction as Record<string, unknown> | undefined;
  const browserExtracted = `${syncText(browserExtraction?.postsWithCandidates)}/${syncText(browserExtraction?.postsChecked)} posts`;
  const safeThumbnailCandidates = Number(patreonReviewQueue?.summary.candidates || 0) || Number(browserExtraction?.imageCandidates || 0) + Number(browserExtraction?.gifCandidates || 0) || pendingPatreonPosts.reduce((total, post) => total + Number(post.image_candidate_count || 0) + Number(post.gif_candidate_count || 0), 0);
  const reviewQueuePosts = patreonReviewQueue?.posts || [];
  const reviewTotalCount = reviewQueuePosts.length || pendingCount;
  const reviewApprovedCount = reviewQueuePosts.filter((post) => post.decision?.approved && post.decision.selected_art_path).length;
  const reviewOpenCount = reviewQueuePosts.length ? Math.max(0, reviewQueuePosts.length - reviewApprovedCount) : pendingCount;
  const reviewReadyToStage = reviewQueuePosts.length > 0 && reviewOpenCount === 0;
  const pendingMissingThumbnailCandidates = pendingPatreonPosts.filter((post) => Number(post.image_candidate_count || 0) + Number(post.gif_candidate_count || 0) === 0).length;
  const localCdpFallbackNeeded = reviewOpenCount > 0 && pendingMissingThumbnailCandidates > 0;
  const thumbnailStatus = pendingCount
    ? safeThumbnailCandidates ? "Candidates need review" : "Needs local CDP"
    : catalogCount >= detectedCount && detectedCount > 0
      ? "CDP thumbnails staged"
      : "No pending thumbnails";
  const tierMapStatus = dashboard.tierMapStatus || {};
  const tierMapSummary = tierMapStatus.summary && typeof tierMapStatus.summary === "object" ? tierMapStatus.summary as Record<string, unknown> : {};
  const patreonAgingStatus = dashboard.patreonAgingStatus || {};
  const activeFixDismissals = new Set(dismissedFixSlugs);
  const lowConfidenceEpisodes = catalog.episodes.filter((episode) => !activeFixDismissals.has(episode.slug) && isLowConfidenceEpisode(episode));
  const sourceReviewEpisodes = catalog.episodes.filter((episode) => !activeFixDismissals.has(episode.slug) && needsSourceSeriesReview(episode));
  const lowConfidenceVisible = lowConfidenceEpisodes.slice(0, 40);
  const sourceReviewVisible = sourceReviewEpisodes.slice(0, 40);
  const agedAccessRows = patreonAgingRows(patreonAgingStatus);
  const needsAttentionCount = reviewOpenCount + pendingMissingThumbnailCandidates + lowConfidenceEpisodes.length + sourceReviewEpisodes.length + Number(patreonAgingStatus.needsUpdate || 0);
  const lastReviewSave = reviewQueuePosts
    .map((post) => post.decision?.reviewed_at || post.decision?.reopened_at || "")
    .filter(Boolean)
    .sort()
    .at(-1) || "";
  const launchModel = getLaunchReadiness({
    catalogCount,
    detectedCount,
    pendingCount: reviewQueuePosts.length ? reviewOpenCount : pendingCount,
    lowConfidenceCount: lowConfidenceEpisodes.length,
    sourceReviewCount: sourceReviewEpisodes.length,
    missingThumbnailCount: Number(dashboard.counts.missingThumbnails || dashboard.missingThumbnails.length || 0),
    fallbackCount: Math.max(0, fallbackCount),
    privateLinksOk: dashboard.kvPrivateLinksBound,
    tierMapOk: syncText(tierMapStatus.status) === "mapped",
    noOpButtons: Number(buttonHealth.knownNoOpButtons || 0),
    patreonAgedNeedsUpdate: Number(patreonAgingStatus.needsUpdate || 0)
  });

  return (
    <section className="admin page-stack">
      <div className="admin-header">
        <div>
          <span className="eyebrow">Visual admin</span>
          <h1>Control Room</h1>
          <p>Pick one task, then use thumbnails and series titles.</p>
        </div>
        <button className="secondary" type="button" onClick={signOut}>Admin logout</button>
      </div>

      <nav className="admin-tabs" aria-label="Admin sections">
        {adminTabs.map((item) => <button key={item.key} type="button" className={tab === item.key ? "active" : ""} onClick={() => openAdminTab(item.key)}>{item.icon}{item.label}</button>)}
      </nav>

      <OperatorStatusBar
        adminConnected={Boolean(admin?.authenticated)}
        githubHealth={githubHealth}
        githubHealthError={githubHealthError}
        pendingCount={reviewOpenCount}
        approvedCount={reviewApprovedCount}
        needsAttentionCount={needsAttentionCount}
        lastSave={lastReviewSave}
        lastDeploy={syncText(catalogSync.lastDeployTime)}
        reviewReadyToStage={reviewReadyToStage}
        thumbnailsMissing={patreonReviewQueue?.summary.thumbnailsMissing ?? 0}
        onOpenTab={openAdminTab}
      />

      {visualMessage ? <div className={visualMessage.startsWith("Saved") || visualMessage.startsWith("Selected") ? "inline-success" : "inline-warning"}>{visualMessage}</div> : null}
      {message ? <div className="inline-success">{message}</div> : null}
      {seriesMessage ? <div className={/saved|created|moved|renamed/i.test(seriesMessage) ? "inline-success" : "inline-warning"}>{seriesMessage}</div> : null}
      {!visualsWritable ? <div className="inline-warning">Visual settings are read-only in this environment.</div> : null}

      {tab === "control" ? (
        <AdminControlCenter
          model={launchModel}
          catalogCount={catalog.episodes.length}
          pendingCount={reviewOpenCount}
          lowConfidenceCount={lowConfidenceEpisodes.length}
          sourceReviewCount={sourceReviewEpisodes.length}
          fallbackCount={Math.max(0, fallbackCount)}
          agedAccessNeedsUpdate={Number(patreonAgingStatus.needsUpdate || 0)}
          privateLinksOk={dashboard.kvPrivateLinksBound}
          visualsWritable={visualsWritable}
          onOpenTab={openAdminTab}
        />
      ) : null}

      {tab === "website" ? (
        <WebsiteBuilderPanel
          visuals={siteVisuals}
          onChange={(patch) => {
            setSiteVisuals((current) => ({ ...current, ...patch }));
            setVisualMessage("");
          }}
          onSave={saveVisuals}
          canSave={Boolean(visualsWritable && admin?.csrf)}
        />
      ) : null}

      {tab === "launch" ? (
        <LaunchControlPanel
          model={launchModel}
          catalogCount={catalog.episodes.length}
          pendingCount={reviewOpenCount}
          lowConfidenceCount={lowConfidenceEpisodes.length}
          sourceReviewCount={sourceReviewEpisodes.length}
          missingThumbnailCount={Number(dashboard.counts.missingThumbnails || dashboard.missingThumbnails.length || 0)}
          fallbackCount={Math.max(0, fallbackCount)}
          tierMapStatus={syncText(tierMapStatus.status)}
          newPostSyncStatus={syncText(catalogSync.status)}
          browserExtractionStatus={syncText(browserExtraction?.status)}
          agedAccessNeedsUpdate={Number(patreonAgingStatus.needsUpdate || 0)}
          manualPatreonUpdatesNeeded={agedAccessRows.filter((row) => !isAgedRowCorrect(row)).length}
          privateLinksOk={dashboard.kvPrivateLinksBound}
          adminAuthenticated={Boolean(admin?.authenticated)}
          onOpenTab={openAdminTab}
          onCopy={copyPath}
        />
      ) : null}

      {tab === "fix" ? (
        <FixCenterPanel
          sourceItems={sourceReviewVisible}
          sourceTotal={sourceReviewEpisodes.length}
          characterItems={lowConfidenceVisible}
          characterTotal={lowConfidenceEpisodes.length}
          visuals={siteVisuals}
          allEpisodes={catalog.episodes}
          series={catalog.series}
          sourceSeries={catalog.sourceSeries || []}
          editingSlug={editingFixSlug}
          drafts={fixDrafts}
          onEdit={startFixEdit}
          onCancelEdit={() => setEditingFixSlug("")}
          onDraftChange={updateFixDraft}
          onSaveDraft={saveFixDraft}
          onCopy={copyPath}
          onAccept={(episode) => saveDiagnosticEpisode(episode, reviewedUpdates(episode, "accepted"), "Suggested fix accepted.")}
          onMarkReviewed={(episode) => saveDiagnosticEpisode(episode, reviewedUpdates(episode, "reviewed"), "Item marked reviewed.")}
          onIgnore={(episode) => saveDiagnosticEpisode(episode, reviewedUpdates(episode, "ignored"), "Item ignored for launch.")}
          onMoveToSeries={moveEpisodesToSeries}
          onCreateSeries={createVisualSeries}
        />
      ) : null}

      {tab === "patreon-aging" ? (
        <PatreonAgedAccessPanel
          rows={agedAccessRows}
          episodes={catalog.episodes}
          visuals={siteVisuals}
          status={patreonAgingStatus}
          tierMapOk={syncText(tierMapStatus.status) === "mapped"}
          onCopy={copyPath}
        />
      ) : null}

      {tab === "dashboard" ? (
        <section className="admin-panel">
          <div className="admin-stats dashboard-stats">
            <Stat label="Catalog episodes" value={String(catalog.episodes.length)} />
            <Stat label="Detected public assets" value={String(assetCount)} />
            <Stat label="Real thumbnails used" value={String(realThumbnailCount)} />
            <Stat label="Fallback images used" value={String(Math.max(0, fallbackCount))} />
            <Stat label="Progressive Cuts" value={String(tierAging.progressiveCuts)} />
            <Stat label="All Paid Members" value={String(tierAging.allPaidMembers)} />
            <Stat label="Patreon posts pending" value={syncText(catalogSync.newPostsPending)} />
            <Stat label="Tier map" value={syncText(tierMapStatus.status)} />
            <Stat label="Patreon-side aged posts" value={syncText(patreonAgingStatus.needsUpdate)} />
            <Stat label="Hero art source" value={imageSourceForPath(siteVisuals.heroBackgroundPath)} />
            <Stat label="Private-link KV" value={dashboard.kvPrivateLinksBound ? "OK" : "Missing"} />
          </div>
          <div className="admin-grid-2">
            <InfoBlock icon={<Image />} title="Current hero art" text={siteVisuals.heroBackgroundPath || "Default asset"} />
            <InfoBlock icon={<RefreshCcw />} title="Catalog sync" text={syncText(pendingReview.nextRequiredAction) || `${syncText(catalogSync.newPostsPending)} Patreon posts are pending review.`} />
            <InfoBlock icon={<Shield />} title="Tier map" text={`${syncText(tierMapStatus.status)}. Members+: ${syncText(tierMapSummary.membersPlus)}; Platinum access tiers: ${syncText(tierMapSummary.platinumAccess)}.`} />
            <InfoBlock icon={<Crown />} title="Tier aging" text={`Catalog date ${tierAging.currentCatalogDate}; Platinum cutoff ${tierAging.platinumAgingCutoff}.`} />
            <InfoBlock icon={<RefreshCcw />} title="Patreon-side aging" text={`${syncText(patreonAgingStatus.needsUpdate)} aged posts need Members+ added on Patreon. Updates applied by this pass: ${syncText(patreonAgingStatus.updated)}.`} />
            <InfoBlock icon={<Star />} title="Featured series" text={siteVisuals.featuredSeriesSlugs.length ? siteVisuals.featuredSeriesSlugs.join(", ") : "Uses catalog defaults until configured."} />
            <InfoBlock icon={<Play />} title="Featured episodes" text={siteVisuals.featuredEpisodeSlugs.length ? siteVisuals.featuredEpisodeSlugs.join(", ") : "Uses starter picks and recent releases until configured."} />
            <div className="quick-links">
              <h2>Quick links</h2>
              <button className="secondary" type="button" onClick={() => openAdminTab("homepage")}><Image size={16} />Edit homepage</button>
              <button className="secondary" type="button" onClick={() => openAdminTab("sync")}><RefreshCcw size={16} />Review pending posts</button>
              <button className="secondary" type="button" onClick={() => openAdminTab("visuals")}><Palette size={16} />Review visuals</button>
              <button className="secondary" type="button" onClick={() => openAdminTab("reorder")}><GripVertical size={16} />Reorder cards</button>
              <button className="secondary" type="button" onClick={() => openAdminTab("manual")}><HelpCircle size={16} />Open manual</button>
              <button className="secondary" type="button" onClick={() => openAdminTab("diagnostics")}><Settings size={16} />Open diagnostics</button>
            </div>
          </div>
        </section>
      ) : null}

      {tab === "sync" ? (
        <section className="admin-panel">
          <div className="review-queue-hero">
            <div className="section-head">
              <div>
                <span className="eyebrow">Review Queue</span>
                <h2>{reviewOpenCount ? `${reviewOpenCount} posts need visual review` : reviewApprovedCount ? `${reviewApprovedCount} posts approved and ready` : "No posts need visual review"}</h2>
                <p>{reviewOpenCount ? `${reviewOpenCount} posts still need a safe thumbnail choice before they can go live.` : reviewApprovedCount ? "All current review cards are approved. Stage approved posts when the workflow panel is ready." : "The website catalog is caught up with the latest reviewed Patreon posts."}</p>
              </div>
            </div>
            <div className="sync-next-action">
              {reviewOpenCount ? <Images size={18} /> : <CheckCircle2 size={18} />}
              <div>
                <strong>{reviewOpenCount ? `${reviewOpenCount} posts need visual review before they can go live.` : reviewReadyToStage ? "Approved posts are ready for staging." : "No pending posts."}</strong>
                <span>{reviewOpenCount ? `${safeThumbnailCandidates} safe thumbnails are available for the cards below.` : reviewReadyToStage ? "Use the staging panel below the review list." : "Nothing is waiting here."}</span>
              </div>
            </div>
            {localCdpFallbackNeeded ? (
              <CollectSafeThumbnailsButton csrf={admin?.csrf || ""} />
            ) : null}
          </div>

          <PendingPatreonReviewPanel
            queue={patreonReviewQueue}
            csrf={admin?.csrf || ""}
            onQueueChange={setPatreonReviewQueue}
            onRefresh={refreshPatreonReviewQueue}
          />

          {reviewReadyToStage ? (
            <PatreonWorkflowDispatchPanel csrf={admin?.csrf || ""} onCopy={copyPath} pendingCount={reviewOpenCount} thumbnailsFound={safeThumbnailCandidates} thumbnailsMissing={patreonReviewQueue?.summary.thumbnailsMissing ?? 0} reviewTotal={reviewTotalCount} approvedReviewCount={reviewApprovedCount} readyToStageReviews={reviewReadyToStage} />
          ) : null}

          <details className="setup-steps sync-developer-details">
            <summary>Developer details</summary>
            <div className="admin-stats dashboard-stats">
              <Stat label="Public catalog posts" value={syncText(catalogSync.catalogEpisodeCount || catalogSync.detectedPostCount)} />
              <Stat label="Patreon detected posts" value={syncText(catalogSync.detectedPostCount)} />
              <Stat label="New posts pending" value={syncText(catalogSync.newPostsPending)} />
              <Stat label="Processed / detected" value={processedCoverage} />
              <Stat label="Thumbnail status" value={thumbnailStatus} />
              <Stat label="Last sync" value={syncText(catalogSync.lastSyncTime)} />
              <Stat label="Last deploy" value={syncText(catalogSync.lastDeployTime)} />
              <Stat label="Failed entries" value={syncText(catalogSync.failedAssetEntries || catalogSync.failedDerivativeEntries)} />
              <Stat label="Missing thumbnails" value={String(patreonReviewQueue?.summary.thumbnailsMissing ?? catalogSync.missingThumbnails ?? 0)} />
              <Stat label="Safe candidates found" value={String(safeThumbnailCandidates)} />
              <Stat label="Local CDP fallback" value={localCdpFallbackNeeded ? "Needed" : "No"} />
              <Stat label="Ready to stage" value={syncText(pendingReview.readyToStage)} />
              <Stat label="Incomplete approvals" value={syncText(pendingReview.incompleteApprovals)} />
              <Stat label="Staged assets" value={String(assetCount)} />
              <Stat label="Tier map status" value={syncText(tierMapStatus.status)} />
            </div>
            {!reviewReadyToStage ? (
              <PatreonWorkflowDispatchPanel csrf={admin?.csrf || ""} onCopy={copyPath} pendingCount={reviewOpenCount} thumbnailsFound={safeThumbnailCandidates} thumbnailsMissing={patreonReviewQueue?.summary.thumbnailsMissing ?? 0} reviewTotal={reviewTotalCount} approvedReviewCount={reviewApprovedCount} readyToStageReviews={reviewReadyToStage} />
            ) : null}
            <details className="setup-steps">
              <summary>Advanced thumbnail collection</summary>
              <LocalCdpFallbackPanel onCopy={copyPath} />
            </details>
          </details>
        </section>
      ) : null}

      {tab === "homepage" ? (
        <AdminForm title="Homepage" subtitle="Controls the public hero headline, positioning copy, and main CTA labels.">
          <TextField label="Hero headline" help="Appears as the homepage H1." value={siteVisuals.heroHeadline} onChange={(value) => updateVisual("heroHeadline", value)} />
          <TextArea label="Hero subheadline" help="Explain the vault in one short paragraph." value={siteVisuals.heroSubheadline} onChange={(value) => updateVisual("heroSubheadline", value)} wide />
          <TextField label="Announcement text" help="Small value line under the hero headline." value={siteVisuals.announcementText} onChange={(value) => updateVisual("announcementText", value)} wide />
          <div className="wide inline-warning">Homepage CTA labels are fixed for clarity: Browse Free Previews, Join Patreon, and Connect Patreon.</div>
          <PathField label="Hero background path" help="Use /patreon-assets/.../poster.webp, /patreon-assets/.../thumb.webp, or /assets/hero-vault.svg." value={siteVisuals.heroBackgroundPath} onChange={(value) => updateVisual("heroBackgroundPath", value)} />
          <PathField label="Hero banner path" help="Fallback hero art if the background is unavailable." value={siteVisuals.heroBannerPath} onChange={(value) => updateVisual("heroBannerPath", value)} />
          <SaveBar onSave={saveVisuals} disabled={!visualsWritable || !admin?.csrf} />
        </AdminForm>
      ) : null}

      {tab === "visuals" ? (
        <AdminForm title="Visuals" subtitle="Fallback paths are only used when a distinct real Patreon asset is unavailable. They do not replace real thumbnails.">
          <PathField label="Fallback episode art" help="Used only when no distinct Patreon asset exists. Safe example: /assets/fallback-episode.svg." value={siteVisuals.fallbackEpisodeArtPath} onChange={(value) => updateVisual("fallbackEpisodeArtPath", value)} />
          <PathField label="Locked preview art" help="Used only when locked entries have no blur/thumb asset." value={siteVisuals.lockedPreviewArtPath} onChange={(value) => updateVisual("lockedPreviewArtPath", value)} />
          <PathField label="Default series cover" help="Used only when a series has no representative episode art." value={siteVisuals.defaultSeriesCoverPath} onChange={(value) => updateVisual("defaultSeriesCoverPath", value)} />
          <TextField label="Accent color" help="Six-digit hex color for primary buttons." value={siteVisuals.accentColor} onChange={(value) => updateVisual("accentColor", value)} />
          <TextField label="Secondary accent" help="Six-digit hex color for supporting surfaces." value={siteVisuals.secondaryAccentColor} onChange={(value) => updateVisual("secondaryAccentColor", value)} />
          <SelectField label="Premium glow" help="Controls intensity of the dark theme glow." value={siteVisuals.premiumGlow} options={["subtle", "medium", "bold"]} onChange={(value) => updateVisual("premiumGlow", value as SiteVisuals["premiumGlow"])} />
          <SelectField label="Card density" help="Controls catalog card spacing." value={siteVisuals.cardDensity} options={["compact", "comfortable", "cinematic"]} onChange={(value) => updateVisual("cardDensity", value as SiteVisuals["cardDensity"])} />
          <div className="visual-preview-panel wide">
            <h3>Preview</h3>
            <VaultArtwork src={siteVisuals.heroBackgroundPath} title={siteVisuals.heroHeadline} subtitle={siteVisuals.announcementText} tier="platinum" className="visual-preview-art" fallbackSrc={siteVisuals.fallbackEpisodeArtPath} />
          </div>
          <SaveBar onSave={saveVisuals} disabled={!visualsWritable || !admin?.csrf} />
        </AdminForm>
      ) : null}

      {tab === "series" ? (
        <VisualSeriesOrganizer
          catalog={catalog}
          visuals={siteVisuals}
          series={visualSeries}
          selectedSeries={selectedSeries}
          selectedSeriesEpisodes={selectedSeriesEpisodes}
          selectedEpisodeSlugs={selectedEpisodeSlugs}
          query={seriesQuery}
          onQueryChange={setSeriesQuery}
          onSelectSeries={(slug) => { setSelectedSeriesSlug(slug); setSeriesMessage(""); }}
          onToggleEpisode={toggleEpisodeSelection}
          onMoveEpisodes={moveEpisodesToSeries}
          onCreateSeries={createVisualSeries}
          onRenameSeries={saveSeriesTitle}
          onSetSeriesCover={setSeriesOverride}
          onClearSeriesCover={clearSeriesOverride}
          onSaveVisuals={saveVisuals}
          canSave={Boolean(admin?.csrf)}
          visualsWritable={visualsWritable}
        />
      ) : null}

      {tab === "episodes" ? (
        <VisualEpisodeOrganizer
          catalog={catalog}
          visuals={siteVisuals}
          episodes={organizeEpisodes}
          selected={selected}
          selectedPaths={selectedPaths}
          query={organizeQuery}
          onQueryChange={setOrganizeQuery}
          onSelect={(episode) => { setSelected(episode); setMessage(""); }}
          onUpdateSelected={setSelected}
          onSaveEpisode={saveEpisode}
          onMoveEpisodes={moveEpisodesToSeries}
          onCreateSeries={createVisualSeries}
          onSetEpisodeCover={setEpisodeOverride}
          onClearEpisodeCover={clearEpisodeOverride}
          onSaveVisuals={saveVisuals}
          canSave={Boolean(admin?.csrf)}
          visualsWritable={visualsWritable}
        />
      ) : null}

      {tab === "membership" ? (
        <AdminForm title="Membership Copy" subtitle="This explains access. It does not change Patreon pricing, billing, tiers, posts, or collections.">
          <TextArea label="Free tier explanation" help="Shown on homepage, login, and account pages." value={siteVisuals.freeTierExplanation} onChange={(value) => updateVisual("freeTierExplanation", value)} wide />
          <TextArea label="Members+ explanation" help="Shown wherever Members+ access is explained." value={siteVisuals.membersTierExplanation} onChange={(value) => updateVisual("membersTierExplanation", value)} wide />
          <TextArea label="Platinum explanation" help="Shown wherever Platinum access is explained." value={siteVisuals.platinumTierExplanation} onChange={(value) => updateVisual("platinumTierExplanation", value)} wide />
          <TextArea label="Login privacy reassurance" help="Shown on login and account pages." value={siteVisuals.loginPrivacyReassurance} onChange={(value) => updateVisual("loginPrivacyReassurance", value)} wide />
          <SaveBar onSave={saveVisuals} disabled={!visualsWritable || !admin?.csrf} />
        </AdminForm>
      ) : null}

      {tab === "featured" ? (
        <AdminForm title="Featured Content" subtitle="Optional slug lists. Empty lists let the catalog use starter picks and recent releases.">
          <TextField label="Featured episode slugs" help="Comma-separated public episode slugs. Example: episode-slug-one, episode-slug-two." value={siteVisuals.featuredEpisodeSlugs.join(", ")} onChange={(value) => updateVisual("featuredEpisodeSlugs", value.split(",").map((item) => item.trim()).filter(Boolean))} wide />
          <TextField label="Featured series slugs" help="Comma-separated series slugs." value={siteVisuals.featuredSeriesSlugs.join(", ")} onChange={(value) => updateVisual("featuredSeriesSlugs", value.split(",").map((item) => item.trim()).filter(Boolean))} wide />
          <SaveBar onSave={saveVisuals} disabled={!visualsWritable || !admin?.csrf} />
        </AdminForm>
      ) : null}

      {tab === "reorder" ? (
        <section className="admin-panel">
          <div className="section-head">
            <div>
              <span className="eyebrow">Visual card order</span>
              <h2>Reorder Mode</h2>
              <p>Use the Admin bar on public pages for direct drag-and-drop. These links open the supported surfaces with reorder mode already enabled.</p>
            </div>
          </div>
          <div className="admin-grid-2">
            <InfoBlock icon={<GripVertical />} title="Homepage shelves" text="Supported: Free Preview Picks, Members+ Progressive Cuts, Platinum Early Access, All Paid Members, Recently Added, and Start Here previews." />
            <InfoBlock icon={<BookOpen />} title="Series episode order" text="Supported per series page. Saved orders use the site visual settings record and leave normal card clicks alone." />
            <InfoBlock icon={<Star />} title="Featured series" text="The homepage series strip can be reordered and saved as Featured Series slugs." />
            <InfoBlock icon={<Database />} title="Storage" text={visualsWritable ? "Visual settings are writable in this environment." : "Settings are read-only here; controls still show the exact supported workflow."} />
          </div>
          <div className="quick-links reorder-links">
            <h2>Open Reorder Mode</h2>
            <a className="secondary" href="/?adminReorder=1"><GripVertical size={16} />Homepage shelves</a>
            <a className="secondary" href="/library?sort=featured&adminReorder=1"><GripVertical size={16} />Library featured order</a>
            {catalog.series.slice(0, 6).map((item) => <a key={item.slug} className="secondary" href={`/series/${item.slug}?adminReorder=1`}><GripVertical size={16} />{item.title}</a>)}
          </div>
        </section>
      ) : null}

      {tab === "manual" ? (
        <section className="admin-panel">
          <div className="section-head">
            <div>
              <span className="eyebrow">Operator guide</span>
              <h2>Admin Manual</h2>
              <p>This is the same guide available at /admin/help.</p>
            </div>
            <a className="secondary small-link" href="/admin/help"><HelpCircle size={16} />Open full page</a>
          </div>
          <AdminManualContent />
        </section>
      ) : null}

      {tab === "diagnostics" ? (
        <section className="admin-panel">
          <div className="diagnostics-section">
            <h2>Button / Action Health</h2>
            <div className="admin-stats dashboard-stats">
              <Stat label="Card CTA models" value={String(buttonHealth.cardPrimaryActions)} />
              <Stat label="Detail CTA models" value={String(buttonHealth.detailPrimaryActions)} />
              <Stat label="Known no-op buttons" value={String(buttonHealth.knownNoOpButtons)} />
              <Stat label="Missing Patreon post URLs" value={String(dashboard.counts.missingPatreonPostUrls || 0)} />
              <Stat label="Admin actions modeled" value={buttonHealth.adminButtonsModeled ? "Yes" : "No"} />
            </div>
            <p>{buttonHealth.model}</p>
          </div>
          <div className="diagnostics-section">
            <h2>Tier Aging Diagnostics</h2>
            <div className="admin-stats dashboard-stats">
              <Stat label="Catalog date" value={tierAging.currentCatalogDate} />
              <Stat label="Platinum cutoff" value={tierAging.platinumAgingCutoff} />
              <Stat label="Progressive Cuts" value={String(tierAging.progressiveCuts)} />
              <Stat label="Fresh Platinum Early Access" value={String(tierAging.freshPlatinumEarlyAccess)} />
              <Stat label="All Paid Members" value={String(tierAging.allPaidMembers)} />
              <Stat label="Missing release dates" value={String(tierAging.missingReleaseDates)} />
            </div>
            <div className="admin-review-grid">
              <ReviewList title="Next to All Paid Members" items={tierAging.nextAllPaidUnlocks.map((item) => ({ href: `/episode/${item.slug}`, title: item.title, meta: item.unlocksOn || "No date" }))} />
              <ReviewList title="Missing release date" items={tierAging.missingReleaseDateEpisodes.map((item) => ({ href: `/episode/${item.slug}`, title: item.title, meta: item.release_date || "Missing" }))} />
            </div>
          </div>
          <div className="diagnostics-section">
            <h2>Patreon Tier Map</h2>
            <div className="admin-stats dashboard-stats">
              <Stat label="Map status" value={syncText(tierMapStatus.status)} />
              <Stat label="API status" value={syncText(tierMapStatus.apiStatus)} />
              <Stat label="Total tiers" value={syncText(tierMapSummary.totalTiers)} />
              <Stat label="Members+ tiers" value={syncText(tierMapSummary.membersPlus)} />
              <Stat label="Platinum access tiers" value={syncText(tierMapSummary.platinumAccess)} />
              <Stat label="Missing mappings" value={syncText(tierMapSummary.missingOrMismatched)} />
            </div>
            <p>{syncText(tierMapStatus.nextAction)}</p>
          </div>
          <div className="diagnostics-section">
            <h2>Patreon-Side Aged Access</h2>
            <div className="admin-stats dashboard-stats">
              <Stat label="Cutoff" value={syncText(patreonAgingStatus.cutoff || tierAging.platinumAgingCutoff)} />
              <Stat label="Aged Platinum posts" value={syncText(patreonAgingStatus.agedPlatinumPosts)} />
              <Stat label="Already correct" value={syncText(patreonAgingStatus.alreadyCorrect)} />
              <Stat label="Needs update" value={syncText(patreonAgingStatus.needsUpdate)} />
              <Stat label="Updated" value={syncText(patreonAgingStatus.updated)} />
              <Stat label="Skipped" value={syncText(patreonAgingStatus.skipped)} />
            </div>
            <p>{syncText(patreonAgingStatus.updateStatus)}. Portal access remains correct even when Patreon-side audience changes are pending.</p>
          </div>
          <div className="diagnostics-section">
            <h2>Catalog Sync Diagnostics</h2>
            <div className="admin-stats dashboard-stats">
              <Stat label="Last sync" value={syncText(catalogSync.lastSyncTime)} />
              <Stat label="Last deploy" value={syncText(catalogSync.lastDeployTime)} />
              <Stat label="Detected posts" value={syncText(catalogSync.detectedPostCount)} />
              <Stat label="New posts pending" value={syncText(catalogSync.newPostsPending)} />
              <Stat label="Missing derivatives" value={syncText(catalogSync.missingDerivativeCount)} />
              <Stat label="Missing thumbnails" value={syncText(catalogSync.missingThumbnails)} />
            </div>
            {catalogSync.lastError ? <div className="inline-warning">Last sync error: {syncText(catalogSync.lastError)}</div> : null}
            <div className="ops-links">
              <code>{syncText(catalogSync.localCommand) || "npm run patreon:auto-sync"}</code>
              <code>{syncText(catalogSync.dryRunCommand) || "npm run patreon:auto-sync:dry-run"}</code>
              <p>{syncText(catalogSync.automationNote) || "Static Pages assets require a build/deploy runner."}</p>
            </div>
          </div>
          <div className="diagnostics-section">
            <h2>Asset Diagnostics</h2>
            <div className="admin-stats dashboard-stats">
              <Stat label="Real Patreon image count" value={String(realThumbnailCount)} />
              <Stat label="Fallback image count" value={String(Math.max(0, fallbackCount))} />
              <Stat label="Repeated generic image count" value={String(catalog.episodes.filter((episode) => allArtworkPaths(episode).some(isRepeatedImportArt)).length)} />
              <Stat label="Missing art count" value={String(dashboard.missingThumbnails.length)} />
            </div>
          </div>
          <div className="admin-grid-2">
            <InfoBlock icon={<Image />} title="Real Patreon asset" text="A distinct /patreon-assets/... derivative selected from the public manifest. Private URLs are never exposed." />
            <InfoBlock icon={<Star />} title="Admin override" text="A safe /patreon-assets/... or /assets/... path explicitly set in Admin." />
            <InfoBlock icon={<Palette />} title="Generated fallback" text="A local fallback card shown only when no distinct public asset is available or the selected candidate is globally repeated." />
            <InfoBlock icon={<Database />} title="Asset pipeline" text={`${realThumbnailCount} real, ${adminOverrideCount} overrides, ${Math.max(0, fallbackCount)} generated fallbacks. Public assets detected: ${assetCount}.`} />
          </div>
          <div className="admin-review-grid">
            <ReviewQueue title="No thumbnail" episodes={dashboard.missingThumbnails} />
            <ReviewQueue title="Missing Patreon post URL" episodes={dashboard.missingPatreonPostUrls} />
            <ReviewQueue title="Placeholder visuals" episodes={dashboard.placeholderImages} />
            <ReviewQueue title="Series needs review" episodes={dashboard.unmapped} />
            <ReviewQueue title="Character / pairing review" episodes={dashboard.lowConfidence} />
          </div>
          <div className="asset-picker-panel wide-panel">
            <h3>Known deployed asset paths</h3>
            <p>These are public derivative paths only. No private Patreon URLs are listed.</p>
            <div className="path-bank">{assetPathBank.map((path) => <button key={path} className="path-chip" type="button" onClick={() => copyPath(path)}><Copy size={14} />{path}</button>)}</div>
          </div>
        </section>
      ) : null}
    </section>
  );
}

type LaunchDecision = "Ready to Launch" | "Ready to Launch With Warnings" | "Not Ready";

interface LaunchReadinessModel {
  decision: LaunchDecision;
  message: string;
  blockers: string[];
  warnings: string[];
}

function adminTabHref(tab: AdminTab) {
  if (tab === "control") return "/admin";
  if (tab === "launch") return "/admin/launch";
  if (tab === "fix") return "/admin/fix";
  if (tab === "patreon-aging") return "/admin/patreon-aged-access";
  return `/admin?tab=${tab}`;
}

function getLaunchReadiness(input: {
  catalogCount: number;
  detectedCount: number;
  pendingCount: number;
  lowConfidenceCount: number;
  sourceReviewCount: number;
  missingThumbnailCount: number;
  fallbackCount: number;
  privateLinksOk: boolean;
  tierMapOk: boolean;
  noOpButtons: number;
  patreonAgedNeedsUpdate: number;
}): LaunchReadinessModel {
  const blockers: string[] = [];
  const warnings: string[] = [];
  if (input.pendingCount > 0) blockers.push(`${input.pendingCount} Patreon post(s) are still pending review.`);
  if (input.detectedCount > input.catalogCount) blockers.push(`Catalog has ${input.catalogCount} posts but Patreon detection found ${input.detectedCount}.`);
  if (!input.privateLinksOk) blockers.push("Private links are not protected in this environment.");
  if (!input.tierMapOk) blockers.push("Membership access map is not verified.");
  if (input.noOpButtons > 0) blockers.push(`${input.noOpButtons} modeled button action(s) are no-op.`);
  if (input.patreonAgedNeedsUpdate > 0) warnings.push(`${input.patreonAgedNeedsUpdate} aged Platinum posts still need Patreon-side Members+ access alignment.`);
  if (input.lowConfidenceCount > 0) warnings.push(`${input.lowConfidenceCount} character or pairing metadata item(s) need review.`);
  if (input.sourceReviewCount > 0) warnings.push(`${input.sourceReviewCount} series item(s) need visual review.`);
  if (input.missingThumbnailCount > 0 || input.fallbackCount > 0) warnings.push(`${input.missingThumbnailCount} missing art item(s), ${input.fallbackCount} fallback art item(s).`);
  if (blockers.length) return { decision: "Not Ready", message: "Do not launch yet. Fix the blockers below first.", blockers, warnings };
  if (warnings.length) return { decision: "Ready to Launch With Warnings", message: "Launch is safe, but finish these cleanup items next.", blockers, warnings };
  return { decision: "Ready to Launch", message: "You can launch now.", blockers, warnings };
}

type NavigationItem = SiteVisuals["navigationItems"][number];
const navIconOptions: NavigationItem["icon"][] = ["home", "sparkles", "book", "crown", "heart", "user"];
const navHrefOptions = [
  { href: "/", label: "Home" },
  { href: "/start-here", label: "Start Here" },
  { href: "/library", label: "Library" },
  { href: "/series", label: "Series" },
  { href: "/account", label: "Account" },
  { href: "/login", label: "Connect/Login" },
  { href: "patreon_join", label: "Join Patreon" }
];
const protectedNavHrefs = new Set(["/", "/library", "/series", "patreon_join", "/account", "/login"]);
const criticalConfirmationHrefs = new Set(["patreon_join", "/account", "/login"]);

function defaultNavigationItems(): NavigationItem[] {
  return [
    { href: "/", label: "Home", icon: "home", enabled: true },
    { href: "/start-here", label: "Start", icon: "sparkles", enabled: true, mobileOptional: true },
    { href: "/library", label: "Browse", icon: "book", enabled: true },
    { href: "/series", label: "Series", icon: "crown", enabled: true },
    { href: "patreon_join", label: "Join", icon: "heart", enabled: true },
    { href: "/account", label: "Account", icon: "user", enabled: true }
  ];
}

function snapshotVisuals(visuals: SiteVisuals): SiteVisuals {
  return {
    ...visuals,
    featuredSeriesSlugs: [...visuals.featuredSeriesSlugs],
    featuredEpisodeSlugs: [...visuals.featuredEpisodeSlugs],
    discoveryShelfOrder: [...(visuals.discoveryShelfOrder || [])],
    hiddenDiscoveryShelves: [...(visuals.hiddenDiscoveryShelves || [])],
    libraryFeaturedOrder: [...visuals.libraryFeaturedOrder],
    homepageShelfOrders: { ...visuals.homepageShelfOrders },
    seriesEpisodeOrders: { ...visuals.seriesEpisodeOrders },
    seriesCoverOverrides: { ...visuals.seriesCoverOverrides },
    episodeArtOverrides: { ...visuals.episodeArtOverrides },
    navigationItems: visuals.navigationItems.map((item) => ({ ...item }))
  };
}

function defaultDiscoveryShelfOrder() {
  return discoveryShelfControls.map((item) => item.shelfKey);
}

function orderedDiscoveryShelfControls(order: string[] = []) {
  const rank = new Map(order.map((key, index) => [key, index]));
  return [...discoveryShelfControls].sort((a, b) => {
    const aRank = rank.has(a.shelfKey) ? rank.get(a.shelfKey)! : Number.MAX_SAFE_INTEGER;
    const bRank = rank.has(b.shelfKey) ? rank.get(b.shelfKey)! : Number.MAX_SAFE_INTEGER;
    if (aRank !== bRank) return aRank - bRank;
    return defaultDiscoveryShelfOrder().indexOf(a.shelfKey) - defaultDiscoveryShelfOrder().indexOf(b.shelfKey);
  });
}

function moveStringInList(list: string[], key: string, direction: -1 | 1) {
  const base = list.length ? [...list] : defaultDiscoveryShelfOrder();
  const index = base.indexOf(key);
  if (index < 0) return base;
  const target = index + direction;
  if (target < 0 || target >= base.length) return base;
  const [item] = base.splice(index, 1);
  base.splice(target, 0, item);
  return base;
}

function WebsiteBuilderPanel({
  visuals,
  onChange,
  onSave,
  canSave
}: {
  visuals: SiteVisuals;
  onChange: (patch: Partial<SiteVisuals>) => void;
  onSave: () => void;
  canSave: boolean;
}) {
  const items = visuals.navigationItems?.length ? visuals.navigationItems : defaultNavigationItems();
  const [undoStack, setUndoStack] = useState<SiteVisuals[]>([]);
  const [pendingAction, setPendingAction] = useState<{ title: string; detail: string; actionLabel: string; run: () => void } | null>(null);
  const [localMessage, setLocalMessage] = useState("");
  const shelfOrder = visuals.discoveryShelfOrder?.length ? visuals.discoveryShelfOrder : defaultDiscoveryShelfOrder();
  const hiddenShelves = new Set(visuals.hiddenDiscoveryShelves || []);

  function applyPatch(patch: Partial<SiteVisuals>) {
    setUndoStack((current) => [snapshotVisuals(visuals), ...current].slice(0, 8));
    onChange(patch);
    setLocalMessage("Preview updated. Review the panel before saving.");
  }

  function applyItems(next: NavigationItem[]) {
    applyPatch({ navigationItems: next });
  }

  function updateItem(index: number, patch: Partial<NavigationItem>) {
    applyItems(items.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item));
  }
  function moveItem(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    applyItems(next);
  }
  function addItem() {
    const existing = new Set(items.map((item) => item.href));
    const option = navHrefOptions.find((candidate) => !existing.has(candidate.href)) || navHrefOptions[1];
    const nextItem: NavigationItem = { href: option.href, label: option.label, icon: "sparkles", enabled: true, mobileOptional: false };
    applyItems([...items, nextItem].slice(0, 8));
  }
  function removeItem(index: number) {
    if (protectedNavHrefs.has(items[index]?.href || "")) return;
    const next = items.filter((_, itemIndex) => itemIndex !== index);
    applyItems(next.length ? next : defaultNavigationItems());
  }
  function requestVisibility(index: number, enabled: boolean) {
    const item = items[index];
    if (!enabled && criticalConfirmationHrefs.has(item.href)) {
      setPendingAction({
        title: `Hide ${item.label || "this tab"}?`,
        detail: "This hides a conversion or account route from the top navigation. The route still works and Restore safe defaults can recover it.",
        actionLabel: "Hide Tab",
        run: () => updateItem(index, { enabled })
      });
      return;
    }
    updateItem(index, { enabled });
  }
  function restoreDefaults() {
    applyPatch({
      navigationItems: defaultNavigationItems(),
      homepagePrimaryCtaLabel: defaultVisuals.homepagePrimaryCtaLabel,
      libraryCtaHeadline: defaultVisuals.libraryCtaHeadline,
      seriesCtaHeadline: defaultVisuals.seriesCtaHeadline,
      loginJoinLabel: defaultVisuals.loginJoinLabel,
      loginConnectLabel: defaultVisuals.loginConnectLabel,
      discoveryShelfOrder: defaultVisuals.discoveryShelfOrder,
      hiddenDiscoveryShelves: defaultVisuals.hiddenDiscoveryShelves
    });
  }
  function restoreCtaDefaults() {
    applyPatch({
      homepagePrimaryCtaLabel: defaultVisuals.homepagePrimaryCtaLabel,
      libraryCtaHeadline: defaultVisuals.libraryCtaHeadline,
      seriesCtaHeadline: defaultVisuals.seriesCtaHeadline,
      loginJoinLabel: defaultVisuals.loginJoinLabel,
      loginConnectLabel: defaultVisuals.loginConnectLabel
    });
  }
  function undoLastChange() {
    const [previous, ...rest] = undoStack;
    if (!previous) return;
    setUndoStack(rest);
    onChange(previous);
    setLocalMessage("Last Website change reverted.");
  }
  function saveWithConfirmation() {
    setPendingAction({
      title: "Save Website changes?",
      detail: "This saves the previewed navigation, CTA copy, and discovery shelf controls to the protected visual settings record.",
      actionLabel: "Save Website",
      run: onSave
    });
  }
  function setShelfVisible(key: string, visible: boolean) {
    const nextHidden = new Set(hiddenShelves);
    if (visible) nextHidden.delete(key);
    else nextHidden.add(key);
    if (nextHidden.size >= discoveryShelfControls.length) {
      setLocalMessage("At least one discovery shelf must stay visible.");
      return;
    }
    applyPatch({ hiddenDiscoveryShelves: [...nextHidden] });
  }
  function moveShelf(key: string, direction: -1 | 1) {
    applyPatch({ discoveryShelfOrder: moveStringInList(shelfOrder, key, direction) });
  }
  function confirmPendingAction() {
    pendingAction?.run();
    setPendingAction(null);
  }
  return (
    <section className="admin-panel website-builder-panel">
      <div className="section-head">
        <div>
          <span className="eyebrow">Website builder</span>
          <h2>Website Tabs</h2>
          <p>Preview safe navigation, conversion copy, and discovery shelves before saving.</p>
        </div>
        <div className="button-row compact-actions">
          <button className="primary" type="button" onClick={saveWithConfirmation} disabled={!canSave}><Save size={17} />Preview / Save</button>
          <button className="secondary" type="button" onClick={addItem} disabled={items.length >= 8}><Plus size={17} />Add Tab</button>
          <button className="secondary" type="button" onClick={undoLastChange} disabled={!undoStack.length}><RotateCcw size={16} />Undo</button>
          <button className="secondary" type="button" onClick={restoreDefaults}><RotateCcw size={16} />Restore safe defaults</button>
        </div>
      </div>
      {localMessage ? <div className={localMessage.includes("reverted") ? "inline-success" : "inline-warning"}>{localMessage}</div> : null}
      {pendingAction ? (
        <div className="move-confirm-panel drop-confirm-panel">
          <AlertTriangle size={26} />
          <div>
            <strong>{pendingAction.title}</strong>
            <span>{pendingAction.detail}</span>
          </div>
          <div className="button-row compact-actions">
            <button className="primary" type="button" onClick={confirmPendingAction}>{pendingAction.actionLabel}</button>
            <button className="secondary" type="button" onClick={() => setPendingAction(null)}>Cancel</button>
          </div>
        </div>
      ) : null}
      <div className="website-tab-preview" aria-label="Navigation preview">
        {items.filter((item) => item.enabled !== false).map((item) => <span key={`${item.href}-${item.label}`}>{item.label}</span>)}
      </div>
      <div className="website-preview-panel">
        <div>
          <span className="eyebrow">CTA preview</span>
          <h3>{visuals.libraryCtaHeadline}</h3>
          <div className="button-row compact-actions">
            <a className="primary small-link" href={PATREON_JOIN_URL} onClick={(event) => event.preventDefault()}><PatreonExternalIcon />{visuals.homepagePrimaryCtaLabel}</a>
            <a className="secondary small-link" href={PATREON_CONNECT_URL} onClick={(event) => event.preventDefault()}><Shield size={16} />{visuals.loginConnectLabel}</a>
          </div>
        </div>
        <div>
          <span className="eyebrow">Shelf preview</span>
          <p>{orderedDiscoveryShelfControls(shelfOrder).filter((item) => !hiddenShelves.has(item.shelfKey)).map((item) => item.title).join(" / ")}</p>
        </div>
      </div>
      <div className="website-tab-grid">
        {items.map((item, index) => (
          <article className={`website-tab-card ${item.enabled ? "" : "is-disabled"}`} key={`${item.href}-${index}`}>
            <div className="card-badge-row">
              <StatusBadge status={item.enabled ? "Visible" : "Hidden"} />
              {item.mobileOptional ? <CategoryBadge category="Mobile Optional" /> : null}
              {protectedNavHrefs.has(item.href) ? <CategoryBadge category="Protected" /> : null}
            </div>
            <label>Tab name<input value={item.label} onChange={(event) => updateItem(index, { label: event.target.value.slice(0, 24) })} /></label>
            <label>Destination<select value={item.href} onChange={(event) => {
              const option = navHrefOptions.find((candidate) => candidate.href === event.target.value);
              updateItem(index, { href: event.target.value, label: item.label || option?.label || "Tab" });
            }}>{navHrefOptions.map((option) => <option key={option.href} value={option.href}>{option.label}</option>)}</select></label>
            <label>Icon<select value={item.icon} onChange={(event) => updateItem(index, { icon: event.target.value as NavigationItem["icon"] })}>{navIconOptions.map((icon) => <option key={icon} value={icon}>{icon}</option>)}</select></label>
            <label className="switch-field"><input type="checkbox" checked={item.enabled} onChange={(event) => requestVisibility(index, event.target.checked)} /><span>Visible</span></label>
            <label className="switch-field"><input type="checkbox" checked={Boolean(item.mobileOptional)} onChange={(event) => updateItem(index, { mobileOptional: event.target.checked })} /><span>Hide first on tight mobile</span></label>
            <div className="button-row compact-actions">
              <button className="secondary small-link" type="button" onClick={() => moveItem(index, -1)} disabled={index === 0}><MoveUp size={16} />Left</button>
              <button className="secondary small-link" type="button" onClick={() => moveItem(index, 1)} disabled={index === items.length - 1}><MoveDown size={16} />Right</button>
              <button className="secondary small-link" type="button" onClick={() => removeItem(index)} disabled={protectedNavHrefs.has(item.href)}>Remove</button>
            </div>
          </article>
        ))}
      </div>
      <div className="website-control-section">
        <div className="section-head compact-section-head">
          <div>
            <span className="eyebrow">Conversion copy</span>
            <h3>CTA Text</h3>
          </div>
          <button className="secondary small-link" type="button" onClick={restoreCtaDefaults}><RotateCcw size={16} />Restore CTA defaults</button>
        </div>
        <div className="admin-form-grid">
          <TextField label="Homepage primary CTA label" help="Label only. Destination stays the built-in Patreon join action." value={visuals.homepagePrimaryCtaLabel} onChange={(value) => applyPatch({ homepagePrimaryCtaLabel: value.slice(0, 34) })} />
          <TextField label="Library CTA headline" help="Headline above the Join/Connect controls on the Library." value={visuals.libraryCtaHeadline} onChange={(value) => applyPatch({ libraryCtaHeadline: value.slice(0, 96) })} wide />
          <TextField label="Series CTA headline" help="Use {series} where the series title should appear." value={visuals.seriesCtaHeadline} onChange={(value) => applyPatch({ seriesCtaHeadline: value.slice(0, 96) })} wide />
          <TextField label="Login Join label" help="Label only. Destination stays Patreon join." value={visuals.loginJoinLabel} onChange={(value) => applyPatch({ loginJoinLabel: value.slice(0, 34) })} />
          <TextField label="Login Connect label" help="Label only. Destination stays the OAuth connect route." value={visuals.loginConnectLabel} onChange={(value) => applyPatch({ loginConnectLabel: value.slice(0, 44) })} />
        </div>
      </div>
      <div className="website-control-section">
        <div className="section-head compact-section-head">
          <div>
            <span className="eyebrow">Discovery shelves</span>
            <h3>Featured Shelf Controls</h3>
          </div>
          <button className="secondary small-link" type="button" onClick={() => applyPatch({ discoveryShelfOrder: defaultVisuals.discoveryShelfOrder, hiddenDiscoveryShelves: defaultVisuals.hiddenDiscoveryShelves })}><RotateCcw size={16} />Restore shelf order</button>
        </div>
        <div className="website-tab-grid">
          {orderedDiscoveryShelfControls(shelfOrder).map((shelf, index) => (
            <article className={`website-tab-card ${hiddenShelves.has(shelf.shelfKey) ? "is-disabled" : ""}`} key={shelf.shelfKey}>
              <div className="card-badge-row">
                <StatusBadge status={hiddenShelves.has(shelf.shelfKey) ? "Hidden" : "Visible"} />
              </div>
              <h3>{shelf.title}</h3>
              <p>{shelf.subtitle}</p>
              <label className="switch-field"><input type="checkbox" checked={!hiddenShelves.has(shelf.shelfKey)} onChange={(event) => setShelfVisible(shelf.shelfKey, event.target.checked)} /><span>Visible</span></label>
              <div className="button-row compact-actions">
                <button className="secondary small-link" type="button" onClick={() => moveShelf(shelf.shelfKey, -1)} disabled={index === 0}><MoveUp size={16} />Up</button>
                <button className="secondary small-link" type="button" onClick={() => moveShelf(shelf.shelfKey, 1)} disabled={index === discoveryShelfControls.length - 1}><MoveDown size={16} />Down</button>
              </div>
            </article>
          ))}
        </div>
      </div>
      <div className="inline-warning">
        <Shield size={17} /> Tabs can only point to safe portal routes or the built-in Join Patreon action. Join, Account/Connect, Home, Browse, and Series have protected recovery.
      </div>
    </section>
  );
}

function PendingPatreonReviewPanel({
  queue,
  csrf,
  onQueueChange,
  onRefresh
}: {
  queue: Awaited<ReturnType<typeof getAdminPatreonReviewQueue>> | null;
  csrf: string;
  onQueueChange: (queue: Awaited<ReturnType<typeof getAdminPatreonReviewQueue>>) => void;
  onRefresh: () => Promise<Awaited<ReturnType<typeof getAdminPatreonReviewQueue>>>;
}) {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [githubHealth, setGithubHealth] = useState<Awaited<ReturnType<typeof getAdminGithubHealth>> | null>(null);
  const [healthError, setHealthError] = useState("");

  async function refreshGithubHealth() {
    setHealthError("");
    try {
      setGithubHealth(await getAdminGithubHealth());
    } catch (err) {
      setGithubHealth(null);
      const body = err && typeof err === "object" && "body" in err ? (err as { body?: { error?: string; message?: string } }).body : null;
      setHealthError(body?.message || body?.error || (err instanceof Error ? err.message : "Review saving health check failed."));
    }
  }

  useEffect(() => {
    void refreshGithubHealth();
  }, []);

  const savingStatusMessage = githubHealth?.ready
    ? (githubHealth.message || "Review saving ready.")
    : (githubHealth || healthError)
      ? reviewSavingUnavailableMessage
      : "";
  const totalPosts = queue?.posts.length || queue?.summary.pendingPosts || 0;
  const approvedPosts = queue?.posts.filter((post) => post.decision?.approved && post.decision.selected_art_path).length || 0;
  const openPosts = queue?.posts.length ? Math.max(0, queue.posts.length - approvedPosts) : totalPosts;
  const reviewHeadline = openPosts
    ? `${openPosts} posts need visual review`
    : approvedPosts
      ? `${approvedPosts} posts approved and ready`
      : "No pending posts";
  const reviewSubcopy = openPosts
    ? `${openPosts} posts need a safe thumbnail choice before they can go live.`
    : approvedPosts
      ? "All current cards are approved. Stage approved posts when ready."
      : "No pending posts.";

  async function refresh() {
    setLoading(true);
    setMessage("");
    try {
      await onRefresh();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Review queue could not refresh.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="diagnostics-section pending-review-workflow" id="pending-patreon-review">
      <div className="section-head">
        <div>
          <span className="eyebrow">Visual review</span>
          <h2>Pending Patreon Posts</h2>
          <p>{reviewHeadline}. {reviewSubcopy}</p>
        </div>
        <button className="secondary" type="button" onClick={() => void refresh()} disabled={loading}><RefreshCcw size={16} />Refresh</button>
      </div>
      {message ? <div className="inline-warning">{message}</div> : null}
      {savingStatusMessage ? (
        <div className={githubHealth?.ready ? "inline-success" : "inline-warning"}>
          {savingStatusMessage}
        </div>
      ) : null}
      {!queue ? <EmptyState title="Review queue unavailable" message="The protected Admin review queue could not be loaded." /> : null}
      {queue && !queue.posts.length ? <EmptyState title="No pending Patreon posts" message="The latest sync status does not list new posts waiting for review." /> : null}
      {queue?.posts.length ? (
        <div className="pending-review-list">
          {queue.posts.map((post) => (
            <PendingPatreonReviewCard
              key={`${post.post_id}-${post.decision?.approved ? "approved" : "pending"}-${post.decision?.reviewed_at || post.decision?.reopened_at || ""}`}
              post={post}
              queue={queue}
              csrf={csrf}
              githubHealth={githubHealth}
              onSaved={(nextQueue) => {
                onQueueChange(nextQueue);
                setMessage("Review saved. Approved posts are ready for the staging workflow.");
                void refreshGithubHealth();
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function PendingPatreonReviewCard({
  post,
  queue,
  csrf,
  githubHealth,
  onSaved
}: {
  post: Awaited<ReturnType<typeof getAdminPatreonReviewQueue>>["posts"][number];
  queue: Awaited<ReturnType<typeof getAdminPatreonReviewQueue>>;
  csrf: string;
  githubHealth: Awaited<ReturnType<typeof getAdminGithubHealth>> | null;
  onSaved: (queue: Awaited<ReturnType<typeof getAdminPatreonReviewQueue>>) => void;
}) {
  const autoSelectedArtPath = useMemo(() => {
    if (post.decision?.selected_art_path) return post.decision.selected_art_path;
    const syncChoice = post.candidates.find((candidate) => {
      const width = Number(candidate.width || 0);
      const height = Number(candidate.height || 0);
      return candidate.selectedBySync && candidate.artPath && width >= 160 && height >= 120;
    });
    return syncChoice?.artPath || "";
  }, [post.decision?.selected_art_path, post.candidates]);
  const [selectedArtPath, setSelectedArtPath] = useState(autoSelectedArtPath);
  const [seriesSlug, setSeriesSlug] = useState(post.decision?.series_slug || post.suggestedSeriesSlug || queue.seriesOptions[0]?.slug || "");
  const [tierRequired, setTierRequired] = useState((post.decision?.tier_required || post.suggested_tier_required || "platinum") as "free" | "paid" | "platinum");
  const [tags, setTags] = useState<string[]>(post.decision?.tags?.length ? post.decision.tags : post.suggestedTags);
  const [characters, setCharacters] = useState<string[]>(post.decision?.characters?.length ? post.decision.characters : post.suggestedCharacters);
  const [customTagsText, setCustomTagsText] = useState("");
  const [customCharactersText, setCustomCharactersText] = useState("");
  const [descriptionShort, setDescriptionShort] = useState(post.decision?.description_short || post.suggestedDescriptionShort || "");
  const [showSeriesChooser, setShowSeriesChooser] = useState(false);
  const [previewCandidate, setPreviewCandidate] = useState<(typeof post.candidates)[number] | null>(null);
  const [confirmingApproval, setConfirmingApproval] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [errorDetails, setErrorDetails] = useState<unknown>(null);
  const [notice, setNotice] = useState("");
  const selectedCandidate = post.candidates.find((candidate) => candidate.artPath === selectedArtPath) || null;
  const visibleCandidates = post.candidates.filter((candidate) => {
    const width = Number(candidate.width || 0);
    const height = Number(candidate.height || 0);
    return candidate.artPath === selectedArtPath || candidate.selectedBySync || (width >= 160 && height >= 120);
  });
  const reviewCandidates = visibleCandidates.length ? visibleCandidates : post.candidates;
  const characterSlugs = characters.length ? characters.map(slugifyAdmin) : post.suggestedCharacterSlugs;
  const hasImages = reviewCandidates.length > 0;
  const approved = Boolean(post.decision?.approved && post.decision.selected_art_path);
  const savingReady = githubHealth?.ready === true;
  const savingBlock = githubHealth && !githubHealth.ready ? reviewSavingUnavailableMessage : !githubHealth ? "Checking review saving setup." : "";
  const complete = Boolean(hasImages && selectedArtPath && selectedCandidate && seriesSlug && tierRequired && tags.length);
  const seriesTiles = queue.seriesOptions.filter((series) => series.slug === post.suggestedSeriesSlug || series.slug === seriesSlug).slice(0, 2);
  const cardTagSuggestions = [...new Set([
    ...post.suggestedTags,
    "platinum",
    "platinum-exclusive",
    "premium",
    tierRequired === "paid" ? "members-plus" : "",
    post.suggestedSeriesSlug === "creator-updates" ? "creator-update" : "",
    post.suggestedSeriesSlug !== "platinum-exclusives" && post.suggestedSeriesSlug !== "creator-updates" ? "anime" : ""
  ].filter(Boolean))];
  const provenanceLabels = Object.values(post.metadataProvenance || {})
    .flatMap((value) => Array.isArray(value) ? value : [value])
    .map((value) => String(value || "").replace(/_/g, " "))
    .filter(Boolean);
  const selectedSeriesTitle = queue.seriesOptions.find((series) => series.slug === seriesSlug)?.title || post.suggestedSeriesTitle || "Series";
  const cardStatus = approved ? "Approved" : hasImages ? "Needs approval" : "Needs images";
  const confidenceLabel = post.metadataConfidence ? `${Math.round(post.metadataConfidence * 100)}%` : "Low";

  useEffect(() => {
    setSelectedArtPath(autoSelectedArtPath);
  }, [autoSelectedArtPath]);

  const actionHint = approved
    ? "Approved for staging."
    : !hasImages
    ? "Run Refresh thumbnails, then come back to this card."
    : savingBlock
      ? savingBlock
    : !selectedCandidate
      ? "Choose one image."
      : complete
        ? "Ready to approve."
        : "Check the details.";

  function addTag(tag: string) {
    setTags((current) => [...new Set([...current, tag])]);
  }

  function removeTag(tag: string) {
    setTags((current) => current.filter((item) => item !== tag));
  }

  function addCustomTags() {
    const next = splitAdminTags(customTagsText);
    if (!next.length) return;
    setTags((current) => [...new Set([...current, ...next])]);
    setCustomTagsText("");
  }

  function removeCharacter(character: string) {
    setCharacters((current) => current.filter((item) => item !== character));
  }

  function addCustomCharacters() {
    const next = splitAdminTags(customCharactersText);
    if (!next.length) return;
    setCharacters((current) => [...new Set([...current, ...next])]);
    setCustomCharactersText("");
  }

  function skipForNow() {
    setNotice("Skipped for now. Nothing was changed or saved.");
  }

  function needsBetterMatch() {
    setNotice("Marked on this screen as needing a better match. Nothing was saved.");
  }

  async function approve() {
    if (!complete || !csrf) return;
    if (!savingReady) {
      setError(savingBlock || "Review saving is not ready.");
      setErrorDetails(githubHealth);
      return;
    }
    setSaving(true);
    setError("");
    setErrorDetails(null);
    try {
      const result = await saveAdminPatreonReview(csrf, {
        action: "approve",
        post_id: post.post_id,
        selectedArtPath,
        seriesSlug,
        tierRequired,
        tags,
        characters,
        characterSlugs,
        pairing: characters.length === 1 ? characters[0] : characters.join(" / "),
        descriptionShort,
        reviewApproved: true
      });
      setConfirmingApproval(false);
      onSaved(result.queue);
    } catch (err) {
      const body = err && typeof err === "object" && "body" in err ? (err as { body?: { error?: string; message?: string } }).body : null;
      setError(body?.error === "github_review_save_blocked" || body?.error === "github_review_save_not_configured" ? reviewSavingUnavailableMessage : body?.message || body?.error || (err instanceof Error ? err.message : "Review save failed."));
      setErrorDetails(body || (err instanceof Error ? { message: err.message } : err));
    } finally {
      setSaving(false);
    }
  }

  async function reopen() {
    if (!csrf) return;
    setSaving(true);
    setError("");
    setErrorDetails(null);
    try {
      const result = await saveAdminPatreonReview(csrf, { action: "reopen", post_id: post.post_id });
      setConfirmingApproval(false);
      onSaved(result.queue);
    } catch (err) {
      const body = err && typeof err === "object" && "body" in err ? (err as { body?: { error?: string; message?: string } }).body : null;
      setError(body?.error === "github_review_save_blocked" || body?.error === "github_review_save_not_configured" ? reviewSavingUnavailableMessage : body?.message || body?.error || (err instanceof Error ? err.message : "Reopen failed."));
      setErrorDetails(body || (err instanceof Error ? { message: err.message } : err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <article className={`pending-review-card ${hasImages ? "" : "missing-images"}`}>
      <div className="pending-review-head">
        <div>
          <span className={`status-badge status-${slugifyAdmin(cardStatus)}`}>{cardStatus}</span>
          <h3>{post.title}</h3>
          <div className="review-meta-row">
            <span>{post.publish_date || "No date"}</span>
            <span>{post.suggestedTierLabel || "Platinum Early Access"}</span>
            <span>{selectedSeriesTitle}</span>
          </div>
        </div>
        <div className="button-row compact-actions">
          <a className="secondary" href={post.safePatreonUrl} target="_blank" rel="noreferrer"><ExternalLink size={16} />Open Patreon</a>
          {approved ? <button className="secondary" type="button" onClick={() => void reopen()} disabled={saving}><RotateCcw size={16} />Undo last approval</button> : null}
        </div>
      </div>

      {!hasImages ? (
        <div className="review-missing-images" role="status">
          <Images size={28} />
          <div>
            <strong>Images not ready yet</strong>
            <span>{actionHint}</span>
          </div>
        </div>
      ) : null}

      <div className="pending-review-layout">
        <div className="review-gallery-panel">
          <div className="review-step-title">
            <strong>1. Choose image</strong>
            <span>{selectedCandidate ? "Selected" : "Required"}</span>
          </div>
          <button className="selected-preview selected-preview-button" type="button" onClick={() => selectedCandidate ? setPreviewCandidate(selectedCandidate) : undefined} disabled={!selectedCandidate}>
            {selectedCandidate ? <img src={selectedCandidate.previewPath || selectedCandidate.artPath} alt="" /> : <div className="empty-preview">Choose one image below</div>}
          </button>
          <div className="button-row compact-actions review-gallery-actions">
            <button className="secondary" type="button" onClick={() => selectedCandidate ? setPreviewCandidate(selectedCandidate) : undefined} disabled={!selectedCandidate}><Eye size={16} />Open large preview</button>
            <button className="secondary" type="button" onClick={skipForNow}><RotateCcw size={16} />Skip for now</button>
            <button className="secondary" type="button" onClick={needsBetterMatch}><AlertTriangle size={16} />Needs better match</button>
          </div>
          <div className="candidate-gallery" aria-label={`${post.title} safe thumbnail candidates`}>
            {reviewCandidates.map((candidate, index) => (
              <button key={candidate.id} className={`candidate-tile ${selectedArtPath === candidate.artPath ? "selected" : ""}`} type="button" aria-pressed={selectedArtPath === candidate.artPath} onClick={() => setSelectedArtPath(candidate.artPath)}>
                <img src={candidate.previewPath || candidate.artPath} alt="" loading="lazy" />
                <span>Image {index + 1}</span>
                <small>Found on this Patreon post</small>
              </button>
            ))}
          </div>
        </div>

        <div className="review-decision-panel">
          <div className="review-step-title">
            <strong>2. Check details</strong>
            <span>{confidenceLabel}</span>
          </div>

          {post.warnings.length ? post.warnings.map((warning) => <div className="simple-warning" key={warning}><AlertTriangle size={16} />{warning}</div>) : null}

          <div className="field-grid compact-field-grid">
            <div className="review-current-series">
              <span>Series</span>
              <strong>{selectedSeriesTitle}</strong>
              <button className="secondary" type="button" onClick={() => setShowSeriesChooser((current) => !current)}>{showSeriesChooser ? "Hide choices" : "Change"}</button>
            </div>
            <label>
              Tier
              <select value={tierRequired} onChange={(event) => setTierRequired(event.target.value as "free" | "paid" | "platinum")}>
                {queue.tierOptions.map((tier) => <option key={tier.value} value={tier.value}>{tier.label}</option>)}
              </select>
            </label>
          </div>

          {showSeriesChooser ? (
            <label className="wide-field">
              Choose series
              <select value={seriesSlug} onChange={(event) => setSeriesSlug(event.target.value)}>
                {queue.seriesOptions.map((series) => <option key={series.slug} value={series.slug}>{series.title}</option>)}
              </select>
            </label>
          ) : null}

          <div className="series-tile-selector" aria-label="Series shortcuts">
            {seriesTiles.slice(0, 4).map((series) => (
              <button key={series.slug} type="button" className={seriesSlug === series.slug ? "selected" : ""} onClick={() => setSeriesSlug(series.slug)}>
                <BookOpen size={16} />
                <span>{series.title}</span>
              </button>
            ))}
          </div>

          <div className="review-chip-panel">
            <strong>Tags</strong>
            <div className="review-chip-row editable">
              {tags.map((tag) => <button key={tag} type="button" onClick={() => removeTag(tag)} aria-label={`Remove ${tag}`}>{tag}<span aria-hidden="true"> X</span></button>)}
            </div>
          </div>
          <div className="tag-suggestion-row" aria-label="Add tag">
            {cardTagSuggestions.filter((tag) => !tags.includes(tag)).slice(0, 8).map((tag) => <button key={tag} type="button" onClick={() => addTag(tag)}>{tag}</button>)}
          </div>

          <div className="review-chip-panel">
            <strong>Characters</strong>
            <div className="review-chip-row editable">
              {characters.length ? characters.map((character) => <button key={character} type="button" onClick={() => removeCharacter(character)} aria-label={`Remove ${character}`}>{character}<span aria-hidden="true"> X</span></button>) : <span>No character filled in</span>}
            </div>
          </div>

          <label className="wide-field">
            Description
            <textarea value={descriptionShort} onChange={(event) => setDescriptionShort(event.target.value)} rows={2} />
          </label>

          <details className="setup-steps quiet-details">
            <summary>Type a correction</summary>
            <label className="wide-field">
              Add tags
              <input value={customTagsText} onChange={(event) => setCustomTagsText(event.target.value)} onBlur={addCustomTags} placeholder="tag-one, tag-two" />
            </label>
            <label className="wide-field">
              Add characters
              <input value={customCharactersText} onChange={(event) => setCustomCharactersText(event.target.value)} onBlur={addCustomCharacters} placeholder="Character name" />
            </label>
            <div className="button-row compact-actions">
              <button className="secondary" type="button" onClick={addCustomTags}>Add tags</button>
              <button className="secondary" type="button" onClick={addCustomCharacters}>Add characters</button>
            </div>
          </details>

          <div className="review-approve-strip">
            <span>{actionHint}</span>
            {approved ? (
              <div className="button-row compact-actions approved-action-row">
                <button className="primary" type="button" disabled><CheckCircle2 size={17} />Approved for website staging</button>
                <button className="secondary" type="button" onClick={() => void reopen()} disabled={saving || !csrf}><RotateCcw size={16} />Undo last approval</button>
              </div>
            ) : (
              <button className="primary" type="button" aria-label="Approve selected for website staging" onClick={() => setConfirmingApproval(true)} disabled={!complete || saving || !csrf || !savingReady}><CheckCircle2 size={17} />{saving ? "Saving..." : savingReady ? "Approve selected for website staging" : "Approve blocked"}</button>
            )}
          </div>
          {notice ? <div className="inline-warning">{notice}</div> : null}
          {error ? <div className="inline-warning">{error}</div> : null}
        </div>
      </div>

      {previewCandidate ? (
        <div className="review-modal-backdrop" role="dialog" aria-modal="true" aria-label={`${post.title} thumbnail preview`}>
          <div className="review-preview-modal">
            <img src={previewCandidate.previewPath || previewCandidate.artPath} alt="" />
            <div className="button-row compact-actions">
              <button className="primary" type="button" onClick={() => {
                setSelectedArtPath(previewCandidate.artPath);
                setPreviewCandidate(null);
              }}>Select this thumbnail</button>
              <button className="secondary" type="button" onClick={() => setPreviewCandidate(null)}>Close preview</button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmingApproval ? (
        <div className="review-modal-backdrop" role="dialog" aria-modal="true" aria-label="Approve Patreon post for website staging">
          <div className="review-approval-modal">
            <CheckCircle2 size={28} />
            <div>
              <strong>This approves the post for the YaoiGen website.</strong>
              <span>It does not edit Patreon, pricing, billing, or private media.</span>
            </div>
            <div className="button-row compact-actions">
              <button className="primary" type="button" onClick={() => void approve()} disabled={saving || !complete || !savingReady}>{saving ? "Saving..." : "Confirm approval"}</button>
              <button className="secondary" type="button" onClick={() => setConfirmingApproval(false)} disabled={saving}>Cancel</button>
            </div>
          </div>
        </div>
      ) : null}

      <details className="setup-steps advanced-review-details">
        <summary>Advanced diagnostics</summary>
        <pre>{JSON.stringify({
          post_id: post.post_id,
          candidateCount: post.candidateCount,
          selectedCandidate: selectedCandidate ? { hash: selectedCandidate.hash, artPath: selectedCandidate.artPath, width: selectedCandidate.width, height: selectedCandidate.height } : null,
          metadataConfidence: post.metadataConfidence,
          metadataSources: [...new Set(provenanceLabels)],
          metadataProvenance: post.metadataProvenance,
          warnings: post.warnings,
          diagnostics: post.diagnostics,
          savingHealth: githubHealth,
          saveError: errorDetails
        }, null, 2)}</pre>
      </details>
    </article>
  );
}

function CollectSafeThumbnailsButton({ csrf }: { csrf: string }) {
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState("");

  async function collect() {
    if (!csrf) return;
    setRunning(true);
    setMessage("");
    try {
      const result = await triggerAdminPatreonSync(csrf, { confirm: true, dryRun: true, mode: "dry-run" });
      setMessage(result.message || "Thumbnail collection started.");
    } catch (err) {
      const body = err && typeof err === "object" && "body" in err ? (err as { body?: { error?: string; message?: string } }).body : null;
      setMessage(body?.message || body?.error || (err instanceof Error ? err.message : "Thumbnail collection could not start."));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="collect-thumbnails-action">
      <button className="primary" type="button" onClick={() => void collect()} disabled={!csrf || running}>
        <RefreshCcw size={17} />{running ? "Collecting..." : "Collect safe thumbnails"}
      </button>
      {message ? <span>{message}</span> : null}
    </div>
  );
}

function PatreonWorkflowDispatchPanel({
  csrf,
  onCopy,
  pendingCount = 0,
  thumbnailsFound = 0,
  thumbnailsMissing = 0,
  reviewTotal = 0,
  approvedReviewCount = 0,
  readyToStageReviews = false
}: {
  csrf: string;
  onCopy: (value: string) => void;
  pendingCount?: number;
  thumbnailsFound?: number;
  thumbnailsMissing?: number;
  reviewTotal?: number;
  approvedReviewCount?: number;
  readyToStageReviews?: boolean;
}) {
  const [status, setStatus] = useState<Awaited<ReturnType<typeof getAdminPatreonSyncStatus>> | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showSetup, setShowSetup] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);

  async function refreshStatus() {
    setError("");
    try {
      setStatus(await getAdminPatreonSyncStatus());
    } catch (err) {
      const body = err && typeof err === "object" && "body" in err ? (err as { body?: { error?: string; message?: string } }).body : null;
      setError(body?.message || body?.error || (err instanceof Error ? err.message : "Workflow status request failed."));
    }
  }

  useEffect(() => {
    void refreshStatus();
  }, []);

  async function triggerPatreonSync(dryRun: boolean) {
    if (!csrf) return;
    setRunning(true);
    setMessage("");
    setError("");
    try {
      const result = await triggerAdminPatreonSync(csrf, { confirm: true, dryRun, mode: dryRun ? "dry-run" : "sync-deploy" });
      setStatus(result);
      if (result.status && ["workflow_discovery_failed", "workflow_not_found", "workflow_disabled", "dispatch_failed"].includes(result.status)) {
        setError(result.error || result.message || "GitHub workflow dispatch failed. Check setup steps and retry.");
      } else {
        setMessage(result.started ? (result.message || "Website update request accepted. Run status will appear in this panel.") : "Automation setup needed.");
        if (result.started) window.setTimeout(() => void refreshStatus(), 6000);
      }
      setConfirming(false);
    } catch (err) {
      const body = err && typeof err === "object" && "body" in err ? (err as { body?: Awaited<ReturnType<typeof getAdminPatreonSyncStatus>> & { message?: string } }).body : null;
      if (body?.status) setStatus(body);
      setError(body?.message || body?.error || (err instanceof Error ? err.message : "Workflow dispatch failed. Check setup steps and retry."));
    } finally {
      setRunning(false);
    }
  }

  const configured = Boolean(status?.configured);
  const missing = status?.missing || ["GITHUB_WORKFLOW_DISPATCH_TOKEN", "GITHUB_REPO_OWNER", "GITHUB_REPO_NAME"];
  const setupSteps = status?.setupSteps || [
    "Create a fine-grained GitHub token with Actions: write and Contents: write.",
    "Add GITHUB_WORKFLOW_DISPATCH_TOKEN, GITHUB_REPO_OWNER, and GITHUB_REPO_NAME to Cloudflare Pages."
  ];
  const run = status?.latestRun && typeof status.latestRun === "object" ? status.latestRun : null;
  const failedStep = run?.failedStep;
  const runConclusion = syncText(run?.conclusion);
  const runStatus = syncText(run?.status);
  const runFailed = runConclusion === "failure";
  const workflowIssue = status?.status && ["workflow_discovery_failed", "workflow_not_found", "workflow_disabled", "dispatch_failed"].includes(status.status);
  const technicalError = error || (workflowIssue ? status?.error || status?.message || "Workflow discovery failed." : "");
  const visibleError = technicalError
    ? status?.status === "workflow_disabled"
      ? "The website update workflow is disabled."
      : "The website update connection is not ready."
    : "";
  const runWarning = runFailed ? `Last website update failed${failedStep?.name ? ` at step: ${failedStep.name}` : ""}.` : "";
  const runCardTitle = runFailed ? "Last update failed" : run ? "Run status" : message ? "Waiting for run status" : "Run status";
  const runCardText = run
    ? `${runStatus || "unknown"} / ${runConclusion || "pending"}${failedStep?.name ? ` at ${failedStep.name}` : ""}.`
    : message
      ? "GitHub accepted the request. The run can take a minute to appear."
      : "No recent run found yet.";
  const approvalsNeeded = reviewTotal > 0 && !readyToStageReviews;
  const imagesMissing = pendingCount > 0 && thumbnailsMissing > 0;
  const readyToRun = configured && !workflowIssue && !approvalsNeeded && !imagesMissing;
  const panelClass = visibleError ? "is-error" : configured ? "is-configured" : "is-missing";
  const title = visibleError
    ? (status?.status === "dispatch_failed" ? "Dispatch failed." : status?.status === "workflow_disabled" ? "Workflow is disabled." : "Update could not start.")
    : configured
      ? imagesMissing
        ? "Refresh thumbnails."
        : approvalsNeeded
          ? "Approve posts."
          : "Ready to stage."
      : "Automation setup needed.";
  const statusLine = visibleError || (configured
    ? imagesMissing
      ? `${thumbnailsMissing} post${thumbnailsMissing === 1 ? "" : "s"} still need images. Use Refresh thumbnails first.`
      : approvalsNeeded
        ? `${approvedReviewCount} of ${reviewTotal} posts approved. Finish the cards below, then stage.`
        : "All pending posts are approved. Stage approved posts when ready."
    : "The website connection needs setup before this button can run.");
  const actionUrl = status?.actionUrl || (status?.owner && status?.repo && status?.workflowFile ? `https://github.com/${status.owner}/${status.repo}/actions/workflows/${status.workflowFile}` : "");
  const developerDetails = status ? {
    status: status.status,
    state: status.state,
    errorStatus: status.errorStatus,
    githubError: status.githubError,
    workflowId: status.workflowId,
    workflowFile: status.workflowFile,
    workflowPath: status.workflowPath,
    workflowState: status.workflowState,
    githubStage: status.githubStage,
    githubRequestPath: status.githubRequestPath,
    latestRun: status.latestRun,
    ref: status.ref,
    inputKeys: status.inputKeys,
    actionUrl: status.actionUrl,
    technicalError
  } : null;
  return (
    <div id="stage-approved-workflow" className={`workflow-dispatch-panel ${panelClass}`}>
      <div>
        <span className="eyebrow">One-click update</span>
        <h3>{title}</h3>
        <p>{statusLine}</p>
        {runWarning ? <p>{runWarning}</p> : null}
        <p>Last update status: {run ? `${runStatus || "unknown"} / ${runConclusion || "pending"}` : "No recent run found"}.</p>
        {pendingCount ? <p>{pendingCount} waiting. {thumbnailsFound} images ready. {thumbnailsMissing} without images.</p> : null}
        {run ? <p>Latest run: {runStatus} / {runConclusion || "pending"} {run.html_url ? <a href={String(run.html_url)} target="_blank" rel="noreferrer">Open run</a> : null}</p> : null}
        {run?.nextAction ? <p>Workflow next action: {run.nextAction}</p> : null}
      </div>
      <div className="button-row compact-actions">
        {visibleError ? <button className="primary" type="button" onClick={() => void refreshStatus()} disabled={running}><RefreshCcw size={17} />Retry</button> : null}
        {configured && !visibleError && imagesMissing ? <button className="primary" type="button" onClick={() => void triggerPatreonSync(true)} disabled={!csrf || running}><RefreshCcw size={17} />{running ? "Starting..." : "Refresh thumbnails"}</button> : null}
        {configured && !visibleError && !imagesMissing ? <button className="primary" type="button" onClick={() => setConfirming(true)} disabled={!readyToRun || !csrf || running}><Rocket size={17} />{running ? "Starting..." : readyToRun ? "Stage approved posts" : "Approve posts first"}</button> : null}
        {configured && !visibleError && approvalsNeeded && !imagesMissing ? <button className="secondary" type="button" onClick={() => void triggerPatreonSync(true)} disabled={!csrf || running}>Refresh queue</button> : null}
        {!configured || visibleError ? <button className="secondary" type="button" onClick={() => setShowSetup((current) => !current)}><HelpCircle size={16} />{showSetup ? "Hide setup steps" : visibleError ? "Show setup steps" : "Open setup steps"}</button> : null}
      </div>
      {confirming ? (
        <div className="move-confirm-panel drop-confirm-panel">
          <RefreshCcw size={26} />
          <div>
            <strong>Stage approved posts and build the website?</strong>
            <span>This dispatches the GitHub workflow in sync-deploy mode. It stages approved review rows, builds, and deploys the website when validation passes. It does not edit Patreon posts, pricing, billing, or private media.</span>
          </div>
          <div className="button-row compact-actions">
            <button className="primary" type="button" onClick={() => void triggerPatreonSync(false)} disabled={running}>Build and deploy website</button>
            <button className="secondary" type="button" onClick={() => void triggerPatreonSync(true)} disabled={running}>Refresh queue</button>
            <button className="secondary" type="button" onClick={() => setConfirming(false)}>Cancel</button>
          </div>
        </div>
      ) : null}
      {message ? <div className={message.includes("started") || message.includes("accepted") ? "inline-success" : "inline-warning"}>{message}</div> : null}
      <div className={`workflow-run-status ${runFailed ? "is-error" : run ? "is-ready" : ""}`} aria-live="polite">
        <div>
          <strong>{runCardTitle}</strong>
          <span>{runCardText}</span>
          {run?.nextAction ? <span>{run.nextAction}</span> : null}
        </div>
        <div className="button-row compact-actions">
          {run?.html_url ? <a className="secondary" href={String(run.html_url)} target="_blank" rel="noreferrer">Open run</a> : null}
          {run?.failedJob?.html_url ? <a className="secondary" href={String(run.failedJob.html_url)} target="_blank" rel="noreferrer">Open failed job</a> : null}
          <button className="secondary" type="button" onClick={() => void refreshStatus()} disabled={running}>Refresh status</button>
        </div>
      </div>
      {developerDetails && visibleError ? (
        <details className="setup-steps">
          <summary>Advanced / Developer Details</summary>
          <pre>{JSON.stringify(developerDetails, null, 2)}</pre>
          {actionUrl ? <p><a href={actionUrl} target="_blank" rel="noreferrer">Open GitHub Actions workflow</a></p> : null}
          {failedStep?.name ? <p>Failed step: <code>{failedStep.name}</code>. {run?.failedJob?.html_url ? <a href={run.failedJob.html_url} target="_blank" rel="noreferrer">Open failed job</a> : null}</p> : null}
        </details>
      ) : null}
      {showSetup ? (
        <div className="setup-steps">
          {!configured ? (
            <ul className="missing-name-list" aria-label="Missing automation settings">
              {missing.map((name) => <li key={name}><code>{name}</code></li>)}
            </ul>
          ) : null}
          {configured && status?.workflowId ? <p>Workflow: <code>{status.workflowId}</code> / <code>{status.workflowPath || status.workflowFile}</code>. Inputs: <code>{(status.inputKeys || ["mode", "source"]).join(", ")}</code>.</p> : null}
          {setupSteps.map((step) => <p key={step}>{step}</p>)}
          <p>Full setup doc: <code>docs/GITHUB_WORKFLOW_DISPATCH_SETUP.md</code></p>
        </div>
      ) : null}
    </div>
  );
}

const localThumbnailSyncCommand = "powershell -NoProfile -ExecutionPolicy Bypass -File tools/run-local-safe-thumbnail-sync.ps1";
const openCdpChromeCommand = "powershell -NoProfile -ExecutionPolicy Bypass -File tools/run-local-safe-thumbnail-sync.ps1 -OpenBrowserOnly";

function LocalCdpFallbackPanel({ onCopy }: { onCopy: (value: string) => void }) {
  const [detail, setDetail] = useState("This fallback is only needed when Patreon API data has no safe image candidates.");

  function showLocalFallbackNote() {
    setDetail("Production Admin cannot control David's local Chrome directly. The local helper uses the already-open Patreon browser when this fallback is needed.");
  }

  const title = "Local thumbnail fallback";
  const primaryCommand = localThumbnailSyncCommand;
  const primaryLabel = "Copy local helper";

  return (
    <div className="workflow-dispatch-panel local-cdp-panel is-missing">
      <div>
        <span className="eyebrow">Safe thumbnail fallback</span>
        <h3>{title}</h3>
        <p>{detail}</p>
        <code className="command-chip">{primaryCommand}</code>
      </div>
      <div className="button-row compact-actions">
        <button className="primary" type="button" onClick={() => onCopy(primaryCommand)}><Copy size={16} />{primaryLabel}</button>
        <button className="secondary" type="button" onClick={() => onCopy(openCdpChromeCommand)}><ExternalLink size={16} />Open CDP Chrome</button>
        <button className="secondary" type="button" onClick={showLocalFallbackNote}><RefreshCcw size={16} />Explain</button>
      </div>
    </div>
  );
}

function operatorTimeLabel(value?: string) {
  if (!value) return "Not yet";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function OperatorStatusBar({
  adminConnected,
  githubHealth,
  githubHealthError,
  pendingCount,
  approvedCount,
  needsAttentionCount,
  lastSave,
  lastDeploy,
  reviewReadyToStage,
  thumbnailsMissing,
  onOpenTab
}: {
  adminConnected: boolean;
  githubHealth: Awaited<ReturnType<typeof getAdminGithubHealth>> | null;
  githubHealthError: string;
  pendingCount: number;
  approvedCount: number;
  needsAttentionCount: number;
  lastSave: string;
  lastDeploy: string;
  reviewReadyToStage: boolean;
  thumbnailsMissing: number;
  onOpenTab: (tab: AdminTab) => void;
}) {
  const saveReady = githubHealth?.ready === true;
  const saveBlocked = Boolean((githubHealth && !githubHealth.ready) || githubHealthError);
  const saveLabel = saveReady ? "Saving ready" : saveBlocked ? "Saving not ready" : "Checking save";
  const nextAction = saveBlocked && pendingCount > 0
    ? {
        title: "Repair saving before approvals",
        text: reviewSavingUnavailableMessage,
        label: "Open Update",
        tab: "sync" as AdminTab,
        icon: <Wrench size={22} />
      }
    : thumbnailsMissing > 0
      ? {
          title: "Collect safe thumbnails",
          text: `${thumbnailsMissing} post${thumbnailsMissing === 1 ? "" : "s"} need usable images before approval.`,
          label: "Open Update",
          tab: "sync" as AdminTab,
          icon: <Images size={22} />
        }
      : pendingCount > 0
        ? {
            title: "Review pending posts",
            text: `${pendingCount} post${pendingCount === 1 ? "" : "s"} still need a selected thumbnail and metadata check.`,
            label: "Review posts",
            tab: "sync" as AdminTab,
            icon: <CheckCircle2 size={22} />
          }
    : reviewReadyToStage
      ? {
          title: "Stage approved posts",
          text: `${approvedCount} approved post${approvedCount === 1 ? "" : "s"} can now be staged for the website build.`,
          label: "Open staging panel",
          tab: "sync" as AdminTab,
          icon: <Rocket size={22} />,
          targetId: "stage-approved-workflow"
        }
          : needsAttentionCount > 0
            ? {
                title: "Clear needs-attention cards",
                text: `${needsAttentionCount} item${needsAttentionCount === 1 ? "" : "s"} need review before final launch confidence is clean.`,
                label: "Open Fix Center",
                tab: "fix" as AdminTab,
                icon: <Wrench size={22} />
              }
            : {
                title: "Verify production",
                text: "No visual review cards are waiting. Use Launch to verify the public site and protected access.",
                label: "Open Launch",
                tab: "launch" as AdminTab,
                icon: <Rocket size={22} />
              };
  const steps = [
    { label: "Sync Patreon", status: approvedCount || pendingCount || reviewReadyToStage ? "done" : "current" },
    { label: "Review pending posts", status: pendingCount > 0 ? "current" : approvedCount ? "done" : "waiting" },
    { label: "Approve thumbnails/metadata", status: pendingCount > 0 ? "current" : approvedCount ? "done" : "waiting" },
    { label: "Stage approved posts", status: reviewReadyToStage ? "current" : approvedCount ? "waiting" : "waiting" },
    { label: "Build/deploy", status: reviewReadyToStage ? "waiting" : "waiting" },
    { label: "Verify production", status: reviewReadyToStage ? "waiting" : "waiting" }
  ];

  function openNextAction() {
    onOpenTab(nextAction.tab);
    if (!("targetId" in nextAction) || !nextAction.targetId) return;
    window.setTimeout(() => {
      const target = document.getElementById(nextAction.targetId || "");
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      const button = target.querySelector<HTMLButtonElement>("button.primary:not(:disabled)");
      button?.focus();
    }, 80);
  }

  return (
    <section className="operator-mode-panel" aria-label="Disabled-safe Operator Mode" aria-live="polite">
      <div className="operator-status-grid">
        <span className={adminConnected ? "operator-chip is-ready" : "operator-chip is-warning"}><strong>Connection</strong>{adminConnected ? "Admin connected" : "Checking"}</span>
        <span className={saveReady ? "operator-chip is-ready" : saveBlocked ? "operator-chip is-blocked" : "operator-chip is-warning"}><strong>Save readiness</strong>{saveLabel}</span>
        <span className="operator-chip"><strong>Pending</strong>{pendingCount}</span>
        <span className="operator-chip is-ready"><strong>Approved</strong>{approvedCount}</span>
        <span className={needsAttentionCount ? "operator-chip is-warning" : "operator-chip is-ready"}><strong>Needs attention</strong>{needsAttentionCount}</span>
        <span className="operator-chip"><strong>Last save</strong>{operatorTimeLabel(lastSave)}</span>
        <span className="operator-chip"><strong>Last deploy</strong>{operatorTimeLabel(lastDeploy)}</span>
      </div>
      <div className="operator-next-action">
        <div className="operator-next-icon" aria-hidden="true">{nextAction.icon}</div>
        <div>
          <span className="eyebrow">Next required action</span>
          <h2>{nextAction.title}</h2>
          <p>{nextAction.text}</p>
        </div>
        <button className="primary operator-primary-action" type="button" onClick={openNextAction}>{nextAction.icon}{nextAction.label}</button>
      </div>
      <ol className="operator-stepper" aria-label="Production workflow">
        {steps.map((step, index) => (
          <li key={step.label} className={`step-${step.status}`}>
            <span>{index + 1}</span>
            <strong>{step.label}</strong>
          </li>
        ))}
      </ol>
    </section>
  );
}

function AdminControlCenter({
  model,
  catalogCount,
  pendingCount,
  lowConfidenceCount,
  sourceReviewCount,
  fallbackCount,
  agedAccessNeedsUpdate,
  privateLinksOk,
  visualsWritable,
  onOpenTab
}: {
  model: LaunchReadinessModel;
  catalogCount: number;
  pendingCount: number;
  lowConfidenceCount: number;
  sourceReviewCount: number;
  fallbackCount: number;
  agedAccessNeedsUpdate: number;
  privateLinksOk: boolean;
  visualsWritable: boolean;
  onOpenTab: (tab: AdminTab) => void;
}) {
  const cleanupCount = Math.min(lowConfidenceCount, 40) + Math.min(sourceReviewCount, 40);
  return (
    <section className="admin-panel easy-control-panel">
      <div className={`launch-decision ${model.decision === "Not Ready" ? "danger" : model.decision.includes("Warnings") ? "warning" : "healthy"}`}>
        <span className="eyebrow">Start here</span>
        <h2>{model.decision}</h2>
        <p>{model.message}</p>
      </div>

      <div className="admin-stats dashboard-stats visual-status-row">
        <Stat label="Catalog episodes" value={String(catalogCount)} />
        <Stat label="Pending Patreon posts" value={String(pendingCount)} />
        <Stat label="Cleanup warnings" value={String(cleanupCount)} />
        <Stat label="Access tasks" value={String(agedAccessNeedsUpdate)} />
        <Stat label="Fallback art candidates" value={String(fallbackCount)} />
        <Stat label="Private links protected" value={privateLinksOk ? "Yes" : "Check"} />
      </div>

      <div className="control-card-grid">
        <button className="control-card priority-card" type="button" onClick={() => onOpenTab("website")}>
          <Palette size={24} />
          <strong>Website Tabs</strong>
          <span>Rename, show, hide, reorder, and add top navigation tabs.</span>
        </button>
        <button className="control-card priority-card" type="button" onClick={() => onOpenTab("sync")}>
          <RefreshCcw size={24} />
          <strong>Update From Patreon</strong>
          <span>Pull latest posts, collect safe thumbnail candidates, and review before publishing.</span>
        </button>
        <button className="control-card priority-card" type="button" onClick={() => onOpenTab("launch")}>
          <Rocket size={24} />
          <strong>Launch Health</strong>
          <span>{model.decision}</span>
        </button>
        <button className="control-card" type="button" onClick={() => onOpenTab("series")}>
          <BookOpen size={24} />
          <strong>Visual Series Organizer</strong>
          <span>Add, rename, cover, and move by title.</span>
        </button>
        <button className="control-card" type="button" onClick={() => onOpenTab("episodes")}>
          <Play size={24} />
          <strong>Organize Episodes</strong>
          <span>Move, rename, and choose thumbnails.</span>
        </button>
        <button className="control-card" type="button" onClick={() => onOpenTab("fix")}>
          <Wrench size={24} />
          <strong>Fix Center</strong>
          <span>{cleanupCount ? `${cleanupCount} visual cleanup cards` : "No metadata cleanup cards"}</span>
        </button>
        <button className="control-card" type="button" onClick={() => onOpenTab("patreon-aging")}>
          <ClipboardList size={24} />
          <strong>Patreon Access</strong>
          <span>{agedAccessNeedsUpdate ? `${agedAccessNeedsUpdate} manual Patreon checks` : "No manual access tasks"}</span>
        </button>
        <button className="control-card" type="button" onClick={() => onOpenTab("reorder")}>
          <GripVertical size={24} />
          <strong>Reorder Shelves</strong>
          <span>Choose what visitors see first on supported pages.</span>
        </button>
        <button className="control-card" type="button" onClick={() => onOpenTab("manual")}>
          <HelpCircle size={24} />
          <strong>Manual</strong>
          <span>Short operator guide.</span>
        </button>
      </div>

      <div className="quick-links visual-shortcuts">
        <h2>Visual Shortcuts</h2>
        <button className="secondary" type="button" onClick={() => onOpenTab("website")}><Palette size={16} />Edit website tabs</button>
        <button className="secondary" type="button" onClick={() => onOpenTab("sync")}><RefreshCcw size={16} />Update from Patreon</button>
        <button className="secondary" type="button" onClick={() => onOpenTab("episodes")}><Play size={16} />Move an episode</button>
        <button className="secondary" type="button" onClick={() => onOpenTab("series")}><Plus size={16} />Add a series</button>
        <a className="secondary" href="/library?tier=free" target="_blank" rel="noreferrer"><Eye size={16} />Preview visitor path</a>
        <button className="secondary" type="button" onClick={() => onOpenTab("fix")}><Wrench size={16} />Fix warnings</button>
        <a className="secondary" href="/admin/help#control-room"><HelpCircle size={16} />Help for this task</a>
      </div>

      <div className="inline-warning">
        <Shield size={17} /> Admin changes use protected admin save. Patreon pricing, billing, and post publishing still stay outside this portal.
        {!visualsWritable ? " Visual settings are read-only in this environment." : ""}
      </div>
    </section>
  );
}

function LaunchControlPanel({
  model,
  catalogCount,
  pendingCount,
  lowConfidenceCount,
  sourceReviewCount,
  missingThumbnailCount,
  fallbackCount,
  tierMapStatus,
  newPostSyncStatus,
  browserExtractionStatus,
  agedAccessNeedsUpdate,
  manualPatreonUpdatesNeeded,
  privateLinksOk,
  adminAuthenticated,
  onOpenTab,
  onCopy
}: {
  model: LaunchReadinessModel;
  catalogCount: number;
  pendingCount: number;
  lowConfidenceCount: number;
  sourceReviewCount: number;
  missingThumbnailCount: number;
  fallbackCount: number;
  tierMapStatus: string;
  newPostSyncStatus: string;
  browserExtractionStatus: string;
  agedAccessNeedsUpdate: number;
  manualPatreonUpdatesNeeded: number;
  privateLinksOk: boolean;
  adminAuthenticated: boolean;
  onOpenTab: (tab: AdminTab) => void;
  onCopy: (value: string) => void;
}) {
  const decisionClass = model.decision === "Not Ready" ? "danger" : model.decision.includes("Warnings") ? "warning" : "healthy";
  return (
    <section className="admin-panel launch-control">
      <div className={`launch-decision ${decisionClass}`}>
        <span className="eyebrow">Launch Readiness Decision</span>
        <h2>{model.decision}</h2>
        <p>{model.message}</p>
        <div className="button-row">
          <button className="primary" type="button" onClick={() => onOpenTab("fix")}><Wrench size={17} />Open Fix Center</button>
          <button className="secondary" type="button" onClick={() => onOpenTab("patreon-aging")}><ClipboardList size={17} />Open Patreon Access</button>
          <a className="secondary" href="/admin/help#launch-control"><HelpCircle size={16} />Help for this task</a>
        </div>
      </div>

      <div className="launch-readiness-grid">
        <ReadinessGroup title="Public Site" rows={[
          ["Homepage", "OK", "Public entry route renders and has Join, Connect, Library, Start Here, and membership paths."],
          ["Library", "OK", `${catalogCount} catalog posts available through the Library.`],
          ["Series", "OK", "Series pages use catalog routes and safe preview cards."],
          ["Episode pages", "OK", "Episode detail pages model Join, Connect, View Patreon Post, and protected private-link actions."],
          ["Mobile", "OK", "Browser QA covers mobile Join, Connect, Library card click, and episode CTA."],
          ["Join Patreon", "OK", "Sends visitors to the YaoiGen Patreon join page."],
          ["Connect Patreon", "OK", "Starts the Patreon OAuth route."],
          ["Private links", privateLinksOk ? "OK" : "Launch Blocker", privateLinksOk ? "Visitor private-link requests stay protected." : "Private-link KV is not healthy in this environment."]
        ]} />
        <ReadinessGroup title="Catalog" rows={[
          ["Total posts", String(catalogCount), "Expected launch catalog count."],
          ["Pending Patreon posts", String(pendingCount), pendingCount ? "New posts still need review before launch." : "No new Patreon posts are waiting."],
          ["Missing thumbnails", String(missingThumbnailCount), missingThumbnailCount ? "Fallback or review needed." : "No missing thumbnails reported."],
          ["Fallback thumbnails", String(fallbackCount), fallbackCount ? "Acceptable launch warning; real art can be improved later." : "No fallback thumbnails counted."],
          ["Character / pairing review", String(lowConfidenceCount), "Fix Center has large-card actions for review, edit, ignore, and Patreon links."],
          ["Series needs review", String(sourceReviewCount), "Fix Center has visual series cards and suggested moves."]
        ]} />
        <ReadinessGroup title="Patreon Operations" rows={[
          ["Membership access map", tierMapStatus === "mapped" ? "Verified" : tierMapStatus, "Membership access should be verified before Patreon-side access edits."],
          ["New post check", newPostSyncStatus, "Latest Patreon post check status."],
          ["Browser thumbnail check", browserExtractionStatus, "Thumbnail check status for recent posts."],
          ["Aged access", String(agedAccessNeedsUpdate), "Patreon-side posts that still need Members+ / All Paid access alignment."],
          ["Manual Patreon updates", String(manualPatreonUpdatesNeeded), "Admin exports a one-post-at-a-time checklist when automation is unsafe."]
        ]} />
        <ReadinessGroup title="Admin" rows={[
          ["Admin login", adminAuthenticated ? "OK" : "Launch Blocker", "Admin session is required for Launch."],
          ["Toolbar", "OK", "Admin bar is authenticated-only and verified by browser QA."],
          ["Manual", "OK", "Manual links directly to Control Room, Organize Episodes, Series, Fix, Launch, Patreon Access, and safety checks."],
          ["Reorder Mode", "OK", "Drag/drop and Move up/down controls remain available."],
          ["Diagnostics", "Actionable", "Diagnostics now render as cards with severity, status, explanation, and actions."]
        ]} />
      </div>

      <div className="diagnostic-card-grid normal-action-grid">
        <article className="launch-diagnostic-card">
          <div className="card-badge-row">
            <SeverityBadge severity={model.blockers.length ? "Launch Blocker" : model.warnings.length ? "Important" : "Info"} />
            <CategoryBadge category="Next Action" />
            <StatusBadge status={model.decision} />
          </div>
          <h2>What to check next</h2>
          <p>{model.blockers[0] || model.warnings[0] || "No blocking launch issues are visible in normal mode."}</p>
          <div className="admin-card-actions">
            <button className="primary" type="button" onClick={() => onOpenTab(model.blockers.length || model.warnings.length ? "fix" : "series")}>{model.blockers.length || model.warnings.length ? "Open Fix Center" : "Open Series"}</button>
            <a className="secondary" href="/admin/help#launch-control"><HelpCircle size={16} />Help for this task</a>
          </div>
        </article>
      </div>

      <details className="advanced-fields developer-details">
        <summary>Advanced / Developer Details - only open this if something is broken.</summary>
        <div className="diagnostic-card-grid">
        <DiagnosticSummaryCard
          severity={pendingCount ? "Launch Blocker" : "Info"}
          category="Sync"
          status={pendingCount ? "Needs Review" : "Healthy"}
          title="Patreon Sync Queue"
          explanation={pendingCount ? "New Patreon posts are detected but not ready for the public catalog." : "No Patreon posts are pending."}
          impact={pendingCount ? "Ignoring this could launch with missing fresh posts." : "No launch risk."}
          primary={<button className="primary" type="button" onClick={() => onOpenTab("fix")}>Review Item</button>}
          secondary={<button className="secondary" type="button" onClick={() => onCopy("npm run patreon:sync-status")}><Copy size={16} />Copy Developer Command</button>}
        />
        <DiagnosticSummaryCard
          severity={agedAccessNeedsUpdate ? "Important" : "Info"}
          category="Patreon Access"
          status={agedAccessNeedsUpdate ? "Needs Manual Patreon Update" : "Healthy"}
          title="Patreon-Side Aged Access"
          explanation={agedAccessNeedsUpdate ? "The portal treats aged Platinum posts as All Paid Members, but Patreon still reports Platinum-only access." : "Patreon-side aged access is aligned."}
          impact={agedAccessNeedsUpdate ? "Portal access works, but Patreon-only browsing may still look Platinum-only until updated." : "No launch risk."}
          primary={<button className="primary" type="button" onClick={() => onOpenTab("patreon-aging")}>Export Checklist</button>}
          secondary={<button className="secondary" type="button" onClick={() => onCopy("npm run patreon:aging-report")}><Copy size={16} />Copy Developer Command</button>}
        />
        <DiagnosticSummaryCard
          severity="Cleanup"
          category="Character/Pairing"
          status={lowConfidenceCount ? "Needs Review" : "Healthy"}
          title="Character / Pairing Metadata"
          explanation={lowConfidenceCount ? "Some entries have low-confidence character or pairing mapping." : "Character and pairing mapping is clean."}
          impact={lowConfidenceCount ? "Search and grouping may be less polished, but access and payment flow are not blocked." : "No launch risk."}
          primary={<button className="primary" type="button" onClick={() => onOpenTab("fix")}>Review Item</button>}
          secondary={<a className="secondary" href="/admin/help"><HelpCircle size={16} />Open Admin Manual section</a>}
        />
        <DiagnosticSummaryCard
          severity="Cleanup"
          category="Series Mapping"
          status={sourceReviewCount ? "Needs Review" : "Healthy"}
          title="Series Needs Review"
          explanation={sourceReviewCount ? "Some entries need a visible series title confirmed." : "Series mapping is clean."}
          impact={sourceReviewCount ? "Users may see broad or original labels until reviewed." : "No launch risk."}
          primary={<button className="primary" type="button" onClick={() => onOpenTab("fix")}>Open Editor</button>}
          secondary={<button className="secondary" type="button" onClick={() => onCopy("/admin/fix")}><Copy size={16} />Copy Link</button>}
        />
        <DiagnosticSummaryCard
          severity={privateLinksOk ? "Info" : "Launch Blocker"}
          category="Private Links"
          status={privateLinksOk ? "Healthy" : "Needs Review"}
          title="Private-Link Protection"
          explanation={privateLinksOk ? "Private destinations are resolved only through the protected endpoint." : "The KV binding is missing or unhealthy."}
          impact={privateLinksOk ? "No public exposure risk found." : "Do not launch until private-link protection is healthy."}
          primary={<a className="primary" href="/api/health/private-links" target="_blank" rel="noreferrer">View Details</a>}
          secondary={<button className="secondary" type="button" onClick={() => onCopy("npm run verify:access-matrix")}><Copy size={16} />Copy Developer Command</button>}
        />
        <DiagnosticSummaryCard
          severity="Cleanup"
          category="Thumbnail/Art"
          status={missingThumbnailCount ? "Needs Review" : fallbackCount ? "Needs Review" : "Healthy"}
          title="Thumbnail / Art"
          explanation={missingThumbnailCount || fallbackCount ? "Some entries use fallback art or need art review." : "Art diagnostics are clean."}
          impact="Fallback art is a polish warning, not a launch blocker when private URLs remain protected."
          primary={<button className="primary" type="button" onClick={() => onOpenTab("episodes")}>Review Item</button>}
          secondary={<a className="secondary" href="/admin/help"><HelpCircle size={16} />Open Admin Manual section</a>}
        />
        </div>
      </details>
    </section>
  );
}

function ReadinessGroup({ title, rows }: { title: string; rows: Array<[string, string, string]> }) {
  return (
    <section className="readiness-group">
      <h2>{title}</h2>
      {rows.map(([label, status, detail]) => (
        <div className="readiness-row" key={`${title}-${label}`}>
          <strong>{label}</strong>
          <StatusBadge status={status} />
          <span>{detail}</span>
        </div>
      ))}
    </section>
  );
}

function DiagnosticSummaryCard({ severity, category, status, title, explanation, impact, primary, secondary }: { severity: string; category: string; status: string; title: string; explanation: string; impact: string; primary: React.ReactNode; secondary: React.ReactNode }) {
  return (
    <article className="launch-diagnostic-card">
      <div className="card-badge-row">
        <SeverityBadge severity={severity} />
        <CategoryBadge category={category} />
        <StatusBadge status={status} />
      </div>
      <h2>{title}</h2>
      <p><strong>What is wrong?</strong> {explanation}</p>
      <p><strong>Why it matters.</strong> {impact}</p>
      <div className="admin-card-actions">
        {primary}
        {secondary}
      </div>
    </article>
  );
}

function VisualSeriesOrganizer({
  catalog,
  visuals,
  series,
  selectedSeries,
  selectedSeriesEpisodes,
  selectedEpisodeSlugs,
  query,
  onQueryChange,
  onSelectSeries,
  onToggleEpisode,
  onMoveEpisodes,
  onCreateSeries,
  onRenameSeries,
  onSetSeriesCover,
  onClearSeriesCover,
  onSaveVisuals,
  canSave,
  visualsWritable
}: {
  catalog: CatalogResponse;
  visuals: SiteVisuals;
  series: Series[];
  selectedSeries?: Series;
  selectedSeriesEpisodes: Episode[];
  selectedEpisodeSlugs: string[];
  query: string;
  onQueryChange: (value: string) => void;
  onSelectSeries: (slug: string) => void;
  onToggleEpisode: (slug: string) => void;
  onMoveEpisodes: (slugs: string[], seriesSlug: string) => void | Promise<void>;
  onCreateSeries: (title: string, coverPath?: string) => Promise<Series | null>;
  onRenameSeries: (series: Series, title: string) => void;
  onSetSeriesCover: (slug: string, path: string) => void;
  onClearSeriesCover: (slug: string) => void;
  onSaveVisuals: () => void;
  canSave: boolean;
  visualsWritable: boolean;
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [addTitle, setAddTitle] = useState("");
  const [addCover, setAddCover] = useState("");
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTitle, setRenameTitle] = useState("");
  const [coverOpen, setCoverOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState<{ slugs: string[]; series: Series } | null>(null);
  const [dragSlug, setDragSlug] = useState("");
  const [dropHoverSlug, setDropHoverSlug] = useState("");
  const [toast, setToast] = useState("");
  const [pulseSeriesSlug, setPulseSeriesSlug] = useState("");
  const [undoMove, setUndoMove] = useState<{ previous: Array<{ slug: string; seriesSlug: string }>; message: string } | null>(null);
  const [liveMessage, setLiveMessage] = useState("");
  const [ignoredSuggestions, setIgnoredSuggestions] = useState<string[]>([]);
  const episodeBoard = catalog.episodes.filter((episode) => episodeMatchesSearch(episode, query)).slice(0, 96);
  const selectedEpisodes = selectedEpisodeSlugs.map((slug) => catalog.episodes.find((episode) => episode.slug === slug)).filter(Boolean) as Episode[];
  const suggested = catalog.episodes
    .filter((episode) => !ignoredSuggestions.includes(episode.slug))
    .map((episode) => ({ episode, suggestion: suggestedSeriesForEpisode(episode, catalog.series) }))
    .filter((item) => item.suggestion && item.suggestion.slug !== item.episode.series_slug)
    .slice(0, 6) as Array<{ episode: Episode; suggestion: SeriesSuggestion }>;
  const addCoverChoices = bestCoverChoices(catalog.episodes.filter((episode) => {
    const title = addTitle.toLowerCase().trim();
    return !title || episodeMatchesSearch(episode, title);
  })).slice(0, 8);
  const selectedCoverChoices = bestCoverChoices(selectedSeriesEpisodes).slice(0, 12);

  useEffect(() => {
    function cancelDrag(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      setDragSlug("");
      setDropHoverSlug("");
      setLiveMessage("Move cancelled.");
      setToast("Move cancelled.");
    }
    window.addEventListener("keydown", cancelDrag);
    return () => window.removeEventListener("keydown", cancelDrag);
  }, []);

  useEffect(() => {
    if (!undoMove) return;
    const timeout = window.setTimeout(() => setUndoMove(null), 10000);
    return () => window.clearTimeout(timeout);
  }, [undoMove]);

  function slugsForDrag(slug: string) {
    if (selectedEpisodeSlugs.includes(slug)) return selectedEpisodeSlugs;
    return [slug].filter(Boolean);
  }

  function canMoveToSeries(slugs: string[], targetSlug: string) {
    return slugs.some((slug) => catalog.episodes.find((episode) => episode.slug === slug)?.series_slug !== targetSlug);
  }

  function beginMove(slugs: string[], target: Series) {
    const uniqueSlugs = [...new Set(slugs)].filter(Boolean);
    if (!uniqueSlugs.length) {
      setToast("Select episodes to move.");
      return;
    }
    if (!canMoveToSeries(uniqueSlugs, target.slug)) {
      setToast("Already here. Choose a different series.");
      return;
    }
    setPendingMove({ slugs: uniqueSlugs, series: target });
  }

  async function confirmMove() {
    if (!pendingMove) return;
    const previous = pendingMove.slugs.map((slug) => {
      const episode = catalog.episodes.find((item) => item.slug === slug);
      return { slug, seriesSlug: episode?.series_slug || "" };
    }).filter((item) => item.seriesSlug);
    const targetTitle = publicSeriesTitle(pendingMove.series.slug, pendingMove.series.title);
    const movedTitle = pendingMove.slugs.length === 1
      ? catalog.episodes.find((episode) => episode.slug === pendingMove.slugs[0])?.title || "Episode"
      : `${pendingMove.slugs.length} episodes`;
    await onMoveEpisodes(pendingMove.slugs, pendingMove.series.slug);
    setUndoMove({ previous, message: `Moved ${movedTitle} to ${targetTitle}.` });
    setToast(`Moved ${movedTitle} to ${targetTitle}.`);
    setPulseSeriesSlug(pendingMove.series.slug);
    window.setTimeout(() => setPulseSeriesSlug(""), 900);
    setPendingMove(null);
  }

  async function undoLastMove() {
    if (!undoMove) return;
    const groups = undoMove.previous.reduce<Record<string, string[]>>((result, item) => {
      result[item.seriesSlug] = [...(result[item.seriesSlug] || []), item.slug];
      return result;
    }, {});
    for (const [seriesSlug, slugs] of Object.entries(groups)) {
      await onMoveEpisodes(slugs, seriesSlug);
    }
    setToast("Move undone.");
    setUndoMove(null);
  }

  async function createFromPanel() {
    const created = await onCreateSeries(addTitle, addCover);
    if (created) {
      setAddOpen(false);
      setAddTitle("");
      setAddCover("");
      onSelectSeries(created.slug);
    }
  }

  async function applySuggestion(episode: Episode, suggestion: SeriesSuggestion) {
    let target = suggestion.existing;
    if (!target) {
      const created = await onCreateSeries(suggestion.title, representativeArtwork([episode]));
      if (!created) return;
      target = created;
    }
    if (!target) return;
    beginMove([episode.slug], target);
  }

  return (
    <section className="admin-panel visual-series-organizer">
      <div className="section-head">
        <div>
          <span className="eyebrow">Series title</span>
          <h2>Visual Series Organizer</h2>
          <p>Search, select episode thumbnails, then choose the target series tile.</p>
        </div>
        <div className="button-row compact-actions">
          <a className="secondary small-link" href="/admin/help#visual-series-organizer"><HelpCircle size={16} />Help for this task</a>
          <button className="primary add-series-tile" type="button" onClick={() => setAddOpen(true)}><Plus size={18} />Add Series</button>
        </div>
      </div>

      <label className="search-box visual-search"><Search size={18} /><input value={query} onChange={(event) => onQueryChange(event.target.value)} placeholder="Search episodes or series title" /></label>

      <div className="sr-live" aria-live="polite">{liveMessage || toast}</div>
      {dragSlug ? (
        <div className="drag-status-strip">
          Moving {catalog.episodes.find((episode) => episode.slug === dragSlug)?.title || "episode"}. Drop on a series tile, or press Esc to cancel.
        </div>
      ) : null}
      {toast ? (
        <div className={toast.includes("Already") || toast.includes("Select") ? "inline-warning" : "inline-success"}>
          {toast}
          {undoMove ? <button className="secondary small-link toast-undo" type="button" onClick={() => void undoLastMove()}>Undo</button> : null}
        </div>
      ) : null}

      <div className="selected-episode-tray">
        {selectedEpisodes.length === 0 ? (
          <strong>Select episodes to move</strong>
        ) : selectedEpisodes.length === 1 ? (
          <>
            <VaultArtwork episode={selectedEpisodes[0]} src={visualEpisodeOverride(selectedEpisodes[0], visuals)} title={selectedEpisodes[0].title} unlocked={false} tier={episodeBadgeTier(selectedEpisodes[0])} variant="thumb" className="mini-thumb" fallbackSrc={visuals.fallbackEpisodeArtPath} seed={selectedEpisodes[0].slug} />
            <span><strong>{selectedEpisodes[0].title}</strong><em>Selected</em></span>
          </>
        ) : (
          <span><strong>{selectedEpisodes.length} episodes selected</strong><em>Move them together to a series tile.</em></span>
        )}
        {selectedEpisodes.length ? <button className="secondary small-link" type="button" onClick={() => selectedEpisodeSlugs.forEach(onToggleEpisode)}>Clear selection</button> : null}
      </div>

      {selectedSeries ? (
        <div className="selected-series-panel top-selected-series-panel">
          <div className="visual-preview-panel">
            <VaultArtwork src={visualSeriesArtwork(selectedSeries.slug, selectedSeriesEpisodes, visuals)} title={publicSeriesTitle(selectedSeries.slug, selectedSeries.title)} subtitle={`${selectedSeriesEpisodes.length} episodes`} tier="platinum" className="visual-preview-art" fallbackSrc={visuals.defaultSeriesCoverPath} seed={selectedSeries.slug} />
            <h3>{publicSeriesTitle(selectedSeries.slug, selectedSeries.title)}</h3>
            <div className="button-row compact-actions">
              <button className="secondary" type="button" onClick={() => { setRenameOpen(true); setRenameTitle(selectedSeries.title); }}><Pencil size={16} />Rename</button>
              <button className="secondary" type="button" onClick={() => setCoverOpen((current) => !current)}><Image size={16} />Change Cover</button>
              <a className="secondary" href={`/series/${selectedSeries.slug}`} target="_blank" rel="noreferrer"><Eye size={16} />Preview</a>
            </div>
          </div>

          {renameOpen ? (
            <div className="simple-modal-panel inline-panel">
              <TextField label="New series title" help="Only the visible title changes." value={renameTitle} onChange={setRenameTitle} wide />
              <div className="rename-preview"><strong>{publicSeriesTitle(selectedSeries.slug, selectedSeries.title)}</strong><MoveRight size={16} /><strong>{renameTitle.trim() || "New title"}</strong></div>
              <div className="button-row">
                <button className="primary" type="button" onClick={() => { onRenameSeries(selectedSeries, renameTitle); setRenameOpen(false); }} disabled={!canSave || !renameTitle.trim()}><Save size={17} />Rename to {renameTitle.trim() || "new title"}</button>
                <button className="secondary" type="button" onClick={() => setRenameOpen(false)}>Cancel</button>
              </div>
            </div>
          ) : null}

          {coverOpen ? (
            <div className="simple-modal-panel inline-panel">
              <div className="section-head compact-head">
                <div><h2>Choose Cover</h2><p>Pick from episodes already in this series.</p></div>
                <button className="secondary small-link" type="button" onClick={() => onClearSeriesCover(selectedSeries.slug)}><RotateCcw size={16} />Auto-pick</button>
              </div>
              <div className="cover-choice-grid">
                {selectedCoverChoices.map((path) => (
                  <button className={visuals.seriesCoverOverrides[selectedSeries.slug] === path ? "cover-choice active" : "cover-choice"} key={path} type="button" onClick={() => onSetSeriesCover(selectedSeries.slug, path)}>
                    <img src={path} alt="" />
                    <span>Use cover</span>
                  </button>
                ))}
              </div>
              <div className="button-row">
                <button className="primary" type="button" onClick={onSaveVisuals} disabled={!visualsWritable || !canSave}><Save size={17} />Save Cover</button>
                <button className="secondary" type="button" onClick={() => setCoverOpen(false)}>Cancel</button>
              </div>
              <p className="action-note">Saving changes only the public cover for this series.</p>
            </div>
          ) : null}

          <details className="advanced-fields developer-details">
            <summary>Advanced / Developer Details - only open this if something is broken.<span className="visually-hidden">Advanced technical fields</span></summary>
            <div className="visual-form-grid">
              <TextArea label="Description" help="Public series page text." value={selectedSeries.description} onChange={() => undefined} wide />
              <TextField label="Slug" help="Internal route. Leave unchanged." value={selectedSeries.slug} onChange={() => undefined} />
              <TextField label="Source series slug" help="Internal compatibility field." value={selectedSeries.source_series_slug || ""} onChange={() => undefined} />
              <TextField label="Tags" help="Internal chips." value={selectedSeries.tags.join(", ")} onChange={() => undefined} />
              <PathField label="Cover override path" help="Advanced raw public path." value={visuals.seriesCoverOverrides[selectedSeries.slug] || ""} onChange={(value) => onSetSeriesCover(selectedSeries.slug, value)} />
            </div>
          </details>
        </div>
      ) : null}

      {suggested.length ? (
        <details className="suggestion-details">
          <summary><Sparkles size={17} />Suggested moves ({suggested.length})</summary>
        <div className="suggestion-strip">
          {suggested.map(({ episode, suggestion }) => (
            <article className="visual-suggestion-card" key={episode.slug}>
              <VaultArtwork episode={episode} src={visualEpisodeOverride(episode, visuals)} title={episode.title} unlocked={false} tier={episodeBadgeTier(episode)} variant="thumb" className="mini-thumb" fallbackSrc={visuals.fallbackEpisodeArtPath} seed={episode.slug} />
              <div>
                <strong>This looks like {suggestion.title}</strong>
                <span>{episode.title}</span>
              </div>
              <button className="primary small-link" type="button" onClick={() => void applySuggestion(episode, suggestion)}>{suggestion.existing ? "Move to Suggested Series" : `Create ${suggestion.title}`}</button>
              <button className="secondary small-link" type="button" onClick={() => { setIgnoredSuggestions((current) => [...current, episode.slug]); setToast("Suggestion hidden."); }}>Ignore suggestion</button>
            </article>
          ))}
        </div>
        </details>
      ) : null}

      {addOpen ? (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Add Series">
        <div className="simple-modal-panel modal-sheet">
          <div className="section-head compact-head">
            <div><h2>Add Series</h2><p>Only the visible title is required.</p></div>
            <button className="secondary small-link" type="button" onClick={() => setAddOpen(false)}>Cancel</button>
          </div>
          <TextField label="Series title" help="Example: Love Stage" value={addTitle} onChange={setAddTitle} wide />
          <div className="cover-choice-grid">
            <button className={!addCover ? "cover-choice active" : "cover-choice"} type="button" onClick={() => setAddCover("")}>
              <Images size={22} />
              <strong>Auto-pick best cover</strong>
            </button>
            {addCoverChoices.map((path) => (
              <button className={addCover === path ? "cover-choice active" : "cover-choice"} key={path} type="button" onClick={() => setAddCover(path)}>
                <img src={path} alt="" />
                <span>Use this cover</span>
              </button>
            ))}
          </div>
          <div className="button-row">
            <button className="primary" type="button" onClick={() => void createFromPanel()} disabled={!canSave || !addTitle.trim()}><Save size={17} />Save</button>
            <button className="secondary" type="button" onClick={() => setAddOpen(false)}>Cancel</button>
          </div>
        </div>
        </div>
      ) : null}

      {pendingMove ? (
        <div className="modal-backdrop move-confirm-backdrop" role="dialog" aria-modal="true" aria-label="Confirm move to series">
          <div className="move-confirm-panel drop-confirm-panel modal-sheet move-confirm-dialog">
            <div className="confirm-art-row">
              {pendingMove.slugs.slice(0, 3).map((slug) => {
                const episode = catalog.episodes.find((item) => item.slug === slug);
                return episode ? <VaultArtwork key={slug} episode={episode} src={visualEpisodeOverride(episode, visuals)} title={episode.title} unlocked={false} tier={episodeBadgeTier(episode)} variant="thumb" className="mini-thumb" fallbackSrc={visuals.fallbackEpisodeArtPath} seed={episode.slug} /> : null;
              })}
              <MoveRight size={18} />
              <VaultArtwork src={visualSeriesArtwork(pendingMove.series.slug, catalog.episodes.filter((episode) => episode.series_slug === pendingMove.series.slug), visuals)} title={publicSeriesTitle(pendingMove.series.slug, pendingMove.series.title)} subtitle="Target series" tier="paid" variant="cover" className="mini-thumb" seed={pendingMove.series.slug} />
            </div>
            <div>
              <strong>Move {pendingMove.slugs.length === 1 ? catalog.episodes.find((episode) => episode.slug === pendingMove.slugs[0])?.title || "this episode" : `${pendingMove.slugs.length} episodes`} to {publicSeriesTitle(pendingMove.series.slug, pendingMove.series.title)}?</strong>
              <span>You can undo right after this.</span>
            </div>
            <div className="button-row compact-actions">
              <button className="primary" type="button" autoFocus onClick={() => void confirmMove()}><MoveRight size={17} />Move to {publicSeriesTitle(pendingMove.series.slug, pendingMove.series.title)}</button>
              <button className="secondary" type="button" onClick={() => setPendingMove(null)}>Cancel</button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="series-tile-grid">
        {series.map((item) => {
          const episodes = catalog.episodes.filter((episode) => episode.series_slug === item.slug);
          const selected = selectedSeries?.slug === item.slug;
          const displayTitle = publicSeriesTitle(item.slug, item.title);
          const activeSlugs = dragSlug ? slugsForDrag(dragSlug) : selectedEpisodeSlugs;
          const moveReady = activeSlugs.length > 0;
          const validTarget = moveReady && canMoveToSeries(activeSlugs, item.slug);
          const dropHover = dropHoverSlug === item.slug;
          return (
            <article
              className={`visual-series-tile ${selected ? "active" : ""} ${dragSlug ? validTarget ? "is-valid-drop" : "is-invalid-drop" : ""} ${dropHover ? "is-drop-hover" : ""} ${pulseSeriesSlug === item.slug ? "pulse-once" : ""}`}
              key={item.slug}
              onDragEnter={() => dragSlug ? setDropHoverSlug(item.slug) : undefined}
              onDragLeave={() => setDropHoverSlug((current) => current === item.slug ? "" : current)}
              onDragOver={(event) => {
                if (dragSlug) event.preventDefault();
              }}
              onDrop={(event) => {
                event.preventDefault();
                if (dragSlug) beginMove(slugsForDrag(dragSlug), item);
                setDragSlug("");
                setDropHoverSlug("");
              }}
            >
              <button className="visual-series-main" type="button" onClick={() => onSelectSeries(item.slug)}>
                <VaultArtwork src={visualSeriesArtwork(item.slug, episodes, visuals)} title={displayTitle} subtitle={`${episodes.length} episodes`} tier={episodes.some((episode) => episodeBadgeTier(episode) === "platinum") ? "platinum" : "paid"} variant="cover" className="series-list-art" seed={item.slug} />
                <strong>{displayTitle}</strong>
                <span>{episodes.length} episodes</span>
              </button>
              {dragSlug ? (
                <div className="drop-target-overlay">
                  <strong>{validTarget ? dropHover ? "Release to move" : "Drop here" : "Already here"}</strong>
                  <span>{validTarget ? `Move ${activeSlugs.length > 1 ? `${activeSlugs.length} episodes` : "episode"} to ${displayTitle}` : "Choose a different series"}</span>
                </div>
              ) : null}
              {selectedEpisodeSlugs.length ? (
                <button className="secondary small-link move-selected-button" type="button" onClick={() => beginMove(selectedEpisodeSlugs, item)}>
                  {selectedEpisodeSlugs.length > 1 ? `Move ${selectedEpisodeSlugs.length} Episodes Here` : "Move Selected Here"}
                </button>
              ) : null}
            </article>
          );
        })}
      </div>

      <div className="episode-move-board">
        <div className="section-head compact-head">
          <div>
            <h2>Episodes</h2>
            <p>{selectedEpisodeSlugs.length ? `${selectedEpisodeSlugs.length} selected` : "Drag a thumbnail onto a series title."}</p>
          </div>
        </div>
        <div className="visual-episode-grid">
          {episodeBoard.map((episode) => {
            const selected = selectedEpisodeSlugs.includes(episode.slug);
            return (
              <button
                className={`visual-episode-card ${selected ? "active is-selected" : ""} ${dragSlug === episode.slug ? "is-dragging" : ""}`}
                draggable
                key={episode.slug}
                type="button"
                onClick={() => onToggleEpisode(episode.slug)}
                onDragStart={(event) => {
                  setDragSlug(episode.slug);
                  setLiveMessage(`Moving ${episode.title}.`);
                  event.dataTransfer.effectAllowed = "move";
                }}
                onDragEnd={() => {
                  setDragSlug("");
                  setDropHoverSlug("");
                }}
              >
                {selected ? <span className="selected-badge"><CheckCircle2 size={14} />Selected</span> : null}
                <VaultArtwork episode={episode} src={visualEpisodeOverride(episode, visuals)} title={episode.title} unlocked={false} tier={episodeBadgeTier(episode)} variant="thumb" className="mini-thumb" fallbackSrc={visuals.fallbackEpisodeArtPath} seed={episode.slug} />
                <strong>{episode.title}</strong>
                <span>{publicEpisodeSeriesTitle(episode)}</span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function VisualEpisodeOrganizer({
  catalog,
  visuals,
  episodes,
  selected,
  selectedPaths,
  query,
  onQueryChange,
  onSelect,
  onUpdateSelected,
  onSaveEpisode,
  onMoveEpisodes,
  onCreateSeries,
  onSetEpisodeCover,
  onClearEpisodeCover,
  onSaveVisuals,
  canSave,
  visualsWritable
}: {
  catalog: CatalogResponse;
  visuals: SiteVisuals;
  episodes: Episode[];
  selected: Episode;
  selectedPaths: string[];
  query: string;
  onQueryChange: (value: string) => void;
  onSelect: (episode: Episode) => void;
  onUpdateSelected: (episode: Episode) => void;
  onSaveEpisode: () => void;
  onMoveEpisodes: (slugs: string[], seriesSlug: string) => void | Promise<void>;
  onCreateSeries: (title: string, coverPath?: string) => Promise<Series | null>;
  onSetEpisodeCover: (slug: string, path: string) => void;
  onClearEpisodeCover: (slug: string) => void;
  onSaveVisuals: () => void;
  canSave: boolean;
  visualsWritable: boolean;
}) {
  const [mode, setMode] = useState<"" | "move" | "thumbnail" | "rename" | "description">("");
  const [seriesSearch, setSeriesSearch] = useState("");
  const [newSeriesTitle, setNewSeriesTitle] = useState("");
  const [pendingMove, setPendingMove] = useState<Series | null>(null);
  const [moveNotice, setMoveNotice] = useState("");
  const [undoEpisodeMove, setUndoEpisodeMove] = useState<{ slug: string; seriesSlug: string; message: string } | null>(null);
  const currentSeries = catalog.series.find((series) => series.slug === selected.series_slug);
  const seriesChoices = catalog.series.filter((series) => seriesMatchesSearch(series, seriesSearch));
  const postHref = safePatreonPostHref(selected.post_id);
  const publicState = selected.visibility === "public_preview" || selected.tier_required === "free" ? "Public preview" : "Private unlock";

  useEffect(() => {
    if (!undoEpisodeMove) return;
    const timeout = window.setTimeout(() => setUndoEpisodeMove(null), 10000);
    return () => window.clearTimeout(timeout);
  }, [undoEpisodeMove]);

  async function createAndMove() {
    const created = await onCreateSeries(newSeriesTitle, representativeArtwork([selected]));
    if (created) {
      setPendingMove(created);
      setNewSeriesTitle("");
    }
  }

  async function confirmEpisodeMove() {
    if (!pendingMove) return;
    const previousSeriesSlug = selected.series_slug;
    await onMoveEpisodes([selected.slug], pendingMove.slug);
    const targetTitle = publicSeriesTitle(pendingMove.slug, pendingMove.title);
    setMoveNotice(`Moved ${selected.title} to ${targetTitle}.`);
    setUndoEpisodeMove({ slug: selected.slug, seriesSlug: previousSeriesSlug, message: `Moved ${selected.title} to ${targetTitle}.` });
    setPendingMove(null);
    setMode("");
  }

  async function undoEpisodeMoveAction() {
    if (!undoEpisodeMove) return;
    await onMoveEpisodes([undoEpisodeMove.slug], undoEpisodeMove.seriesSlug);
    setMoveNotice("Move undone.");
    setUndoEpisodeMove(null);
  }

  function cancelEdit() {
    const original = catalog.episodes.find((episode) => episode.slug === selected.slug);
    if (original) onUpdateSelected(original);
    setMode("");
  }

  return (
    <section className="admin-panel visual-episode-organizer">
      <div className="section-head">
        <div>
          <span className="eyebrow">Organize Episodes</span>
          <h2>Organize Episodes</h2>
          <p>Pick one episode, then move, rename, or choose a thumbnail.</p>
        </div>
        <a className="secondary small-link" href="/admin/help#organize-episodes"><HelpCircle size={16} />Help for this task</a>
      </div>
      {moveNotice ? <div className="inline-success">{moveNotice}{undoEpisodeMove ? <button className="secondary small-link toast-undo" type="button" onClick={() => void undoEpisodeMoveAction()}>Undo</button> : null}</div> : null}

      <div className="visual-organizer-layout">
        <aside className="visual-episode-sidebar">
          <label className="search-box"><Search size={18} /><input value={query} onChange={(event) => onQueryChange(event.target.value)} placeholder="Search episodes by title" /></label>
          <div className="visual-episode-list">
            {episodes.map((episode) => (
              <button className={episode.slug === selected.slug ? "active" : ""} key={episode.slug} type="button" onClick={() => onSelect(episode)}>
                <VaultArtwork episode={episode} src={visualEpisodeOverride(episode, visuals)} title={episode.title} unlocked={false} tier={episodeBadgeTier(episode)} variant="thumb" className="mini-thumb" fallbackSrc={visuals.fallbackEpisodeArtPath} seed={episode.slug} />
                <span><strong>{episode.title}</strong><em>{publicEpisodeSeriesTitle(episode)}</em></span>
              </button>
            ))}
          </div>
        </aside>

        <form className="visual-episode-editor" onSubmit={(event) => { event.preventDefault(); void onSaveEpisode(); }}>
          <VaultArtwork episode={selected} src={visualEpisodeOverride(selected, visuals)} title={selected.title} unlocked={false} tier={episodeBadgeTier(selected)} variant="poster" className="episode-editor-art" fallbackSrc={visuals.fallbackEpisodeArtPath} seed={selected.slug} />
          <div className="episode-editor-summary">
            <h2>{selected.title}</h2>
            <div className="card-badge-row">
              <StatusBadge status={currentSeries ? publicSeriesTitle(currentSeries.slug, currentSeries.title) : publicEpisodeSeriesTitle(selected)} />
              <span className={`tier tier-${episodeBadgeTier(selected)}`}>{episodeBadgeLabel(selected)}</span>
              <StatusBadge status={publicState} />
            </div>
            <div className="button-row">
              {postHref ? <a className="secondary" href={postHref} target="_blank" rel="noreferrer"><ExternalLink size={16} />Patreon Post</a> : null}
              <button className="primary" type="button" onClick={() => setMode("move")}><MoveRight size={17} />Move to Series</button>
              <button className="secondary" type="button" onClick={() => setMode("thumbnail")}><Image size={16} />Change Thumbnail</button>
              <button className="secondary" type="button" onClick={() => setMode("rename")}><Pencil size={16} />Rename</button>
              <button className="secondary" type="button" onClick={() => setMode("description")}><ListChecks size={16} />Short Description</button>
            </div>
          </div>

          {mode === "move" ? (
            <div className="simple-modal-panel inline-panel">
              <div className="section-head compact-head">
                <div><h2>Move to Series</h2><p>Choose a title tile, then move here.</p></div>
                <button className="secondary small-link" type="button" onClick={() => setMode("")}>Cancel</button>
              </div>
              <label className="search-box"><Search size={18} /><input value={seriesSearch} onChange={(event) => setSeriesSearch(event.target.value)} placeholder="Search series title" /></label>
              <div className="series-picker-grid">
                {seriesChoices.map((series) => {
                  const seriesEpisodes = catalog.episodes.filter((episode) => episode.series_slug === series.slug);
                  return (
                    <button className="series-picker-tile" key={series.slug} type="button" onClick={() => setPendingMove(series)}>
                      <VaultArtwork src={visualSeriesArtwork(series.slug, seriesEpisodes, visuals)} title={publicSeriesTitle(series.slug, series.title)} subtitle={`${seriesEpisodes.length} episodes`} tier="paid" variant="cover" className="series-list-art" seed={series.slug} />
                      <strong>{publicSeriesTitle(series.slug, series.title)}</strong>
                      <span>Move Selected Here</span>
                    </button>
                  );
                })}
              </div>
              <div className="quick-create-row">
                <TextField label="Add New Series" help="Only type the visible series title." value={newSeriesTitle} onChange={setNewSeriesTitle} />
                <button className="primary" type="button" onClick={() => void createAndMove()} disabled={!canSave || !newSeriesTitle.trim()}><Plus size={17} />Add and Move</button>
              </div>
              {pendingMove ? (
                <div className="modal-backdrop move-confirm-backdrop" role="dialog" aria-modal="true" aria-label="Confirm episode move">
                  <div className="move-confirm-panel drop-confirm-panel modal-sheet move-confirm-dialog">
                    <div className="confirm-art-row">
                      <VaultArtwork episode={selected} src={visualEpisodeOverride(selected, visuals)} title={selected.title} unlocked={false} tier={episodeBadgeTier(selected)} variant="thumb" className="mini-thumb" fallbackSrc={visuals.fallbackEpisodeArtPath} seed={selected.slug} />
                      <MoveRight size={18} />
                      <VaultArtwork src={visualSeriesArtwork(pendingMove.slug, catalog.episodes.filter((episode) => episode.series_slug === pendingMove.slug), visuals)} title={publicSeriesTitle(pendingMove.slug, pendingMove.title)} subtitle="Target series" tier="paid" variant="cover" className="mini-thumb" seed={pendingMove.slug} />
                    </div>
                    <div>
                      <strong>Move {selected.title} to {publicSeriesTitle(pendingMove.slug, pendingMove.title)}?</strong>
                      <span>You can undo right after this.</span>
                    </div>
                    <div className="button-row compact-actions">
                      <button className="primary" type="button" autoFocus onClick={() => void confirmEpisodeMove()}><MoveRight size={17} />Move to {publicSeriesTitle(pendingMove.slug, pendingMove.title)}</button>
                      <button className="secondary" type="button" onClick={() => setPendingMove(null)}>Cancel</button>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {mode === "thumbnail" ? (
            <div className="simple-modal-panel inline-panel">
              <div className="section-head compact-head">
                <div><h2>Change Thumbnail</h2><p>Pick from safe public episode art.</p></div>
                <button className="secondary small-link" type="button" onClick={() => onClearEpisodeCover(selected.slug)}><RotateCcw size={16} />Auto</button>
              </div>
              <div className="cover-choice-grid">
                {selectedPaths.map((path) => (
                  <button className={visuals.episodeArtOverrides[selected.slug] === path ? "cover-choice active" : "cover-choice"} key={path} type="button" onClick={() => onSetEpisodeCover(selected.slug, path)}>
                    <img src={path} alt="" />
                    <span>Use thumbnail</span>
                  </button>
                ))}
              </div>
              <div className="button-row">
                <button className="primary" type="button" onClick={onSaveVisuals} disabled={!visualsWritable || !canSave}><Save size={17} />Save</button>
                <button className="secondary" type="button" onClick={() => setMode("")}>Cancel</button>
              </div>
            </div>
          ) : null}

          {mode === "rename" ? (
            <div className="simple-modal-panel inline-panel">
              <TextField label="Title" help="Visible episode title." value={selected.title} onChange={(value) => onUpdateSelected({ ...selected, title: value })} wide />
              <div className="button-row">
                <button className="primary" type="submit" disabled={!canSave}><Save size={17} />Save</button>
                <button className="secondary" type="button" onClick={cancelEdit}>Cancel</button>
              </div>
            </div>
          ) : null}

          {mode === "description" ? (
            <div className="simple-modal-panel inline-panel">
              <TextArea label="Short Description" help="Short public preview copy." value={selected.description_short} onChange={(value) => onUpdateSelected({ ...selected, description_short: value })} wide />
              <div className="button-row">
                <button className="primary" type="submit" disabled={!canSave}><Save size={17} />Save</button>
                <button className="secondary" type="button" onClick={cancelEdit}>Cancel</button>
              </div>
            </div>
          ) : null}

          <div className="save-bar">
            <button className="primary" type="submit" disabled={!canSave}><Save size={17} />Save</button>
            <button className="secondary" type="button" onClick={cancelEdit}>Cancel</button>
          </div>

          <details className="advanced-fields">
            <summary>Advanced / Developer Details - only open this if something is broken.<span className="visually-hidden">Advanced technical fields</span></summary>
            <div className="visual-form-grid">
              <TextField label="Slug" help="Internal route. Leave unchanged." value={selected.slug} onChange={() => undefined} />
              <SelectField label="Technical status" help="Internal publishing state." value={selected.status} options={["published", "needs_review", "draft"]} onChange={(value) => onUpdateSelected({ ...selected, status: value as Episode["status"] })} />
              <SelectField label="Review status" help="Internal mapping state." value={selected.review_status || "needs_review"} options={["needs_review", "mapped", "approved"]} onChange={(value) => onUpdateSelected({ ...selected, review_status: value as Episode["review_status"] })} />
              <TextField label="Source series" help="Internal compatibility label." value={selected.source_series || ""} onChange={(value) => onUpdateSelected({ ...selected, source_series: value })} />
              <TextField label="Source series slug" help="Internal compatibility slug." value={selected.source_series_slug || ""} onChange={(value) => onUpdateSelected({ ...selected, source_series_slug: value })} />
              <TextField label="Tags" help="Comma-separated internal chips." value={selected.tags.join(", ")} onChange={(value) => onUpdateSelected({ ...selected, tags: splitAdminTags(value) })} />
              <PathField label="Raw thumbnail override path" help="Advanced safe public path only." value={visuals.episodeArtOverrides[selected.slug] || ""} onChange={(value) => onSetEpisodeCover(selected.slug, value)} />
            </div>
          </details>
        </form>
      </div>
    </section>
  );
}

function FixCenterPanel({
  sourceItems,
  sourceTotal,
  characterItems,
  characterTotal,
  visuals,
  allEpisodes,
  series,
  sourceSeries,
  editingSlug,
  drafts,
  onEdit,
  onCancelEdit,
  onDraftChange,
  onSaveDraft,
  onCopy,
  onAccept,
  onMarkReviewed,
  onIgnore,
  onMoveToSeries,
  onCreateSeries
}: {
  sourceItems: Episode[];
  sourceTotal: number;
  characterItems: Episode[];
  characterTotal: number;
  visuals: SiteVisuals;
  allEpisodes: Episode[];
  series: Series[];
  sourceSeries: NonNullable<CatalogResponse["sourceSeries"]>;
  editingSlug: string;
  drafts: Record<string, FixDraft>;
  onEdit: (episode: Episode) => void;
  onCancelEdit: () => void;
  onDraftChange: (slug: string, next: Partial<FixDraft>) => void;
  onSaveDraft: (episode: Episode) => void;
  onCopy: (value: string) => void;
  onAccept: (episode: Episode) => void;
  onMarkReviewed: (episode: Episode) => void;
  onIgnore: (episode: Episode) => void;
  onMoveToSeries: (slugs: string[], seriesSlug: string) => void | Promise<void>;
  onCreateSeries: (title: string, coverPath?: string) => Promise<Series | null>;
}) {
  const combined = uniqueEpisodes([...sourceItems, ...characterItems]).slice(0, 40);
  const checklist = reviewChecklistMarkdown(combined, sourceTotal, characterTotal);
  return (
    <section className="admin-panel fix-center">
      <div className="section-head">
        <div>
          <span className="eyebrow">Visual fixes</span>
          <h2>Fix Center</h2>
          <p>Look at the thumbnail, accept the suggested series, or choose a series tile.</p>
        </div>
        <div className="button-row compact-actions">
          <a className="secondary small-link" href="/admin/help#fix-center"><HelpCircle size={16} />Help for this task</a>
          <button className="secondary" type="button" onClick={() => onCopy(checklist)}><ClipboardList size={16} />Export Review Checklist</button>
        </div>
      </div>
      <div className="admin-stats dashboard-stats">
        <Stat label="Series needs review" value={`${Math.min(sourceItems.length, 40)} / ${sourceTotal}`} />
        <Stat label="Character/pairing needs review" value={`${Math.min(characterItems.length, 40)} / ${characterTotal}`} />
        <Stat label="Typing needed" value="Only new title" />
        <Stat label="File editing needed" value="No" />
      </div>
      <div className="diagnostic-card-grid">
        {combined.length ? combined.map((episode) => (
          <FixItemCard
            key={episode.slug}
            episode={episode}
            visuals={visuals}
            allEpisodes={allEpisodes}
            series={series}
            onCopy={onCopy}
            onMarkReviewed={() => onMarkReviewed(episode)}
            onIgnore={() => onIgnore(episode)}
            onMoveToSeries={onMoveToSeries}
            onCreateSeries={onCreateSeries}
          />
        )) : <EmptyState title="No metadata diagnostics" message="Series and character/pairing diagnostics are clean." />}
      </div>
    </section>
  );
}

function FixItemCard({
  episode,
  visuals,
  allEpisodes,
  series,
  onCopy,
  onMarkReviewed,
  onIgnore,
  onMoveToSeries,
  onCreateSeries
}: {
  episode: Episode;
  visuals: SiteVisuals;
  allEpisodes: Episode[];
  series: Series[];
  onCopy: (value: string) => void;
  onMarkReviewed: () => void;
  onIgnore: () => void;
  onMoveToSeries: (slugs: string[], seriesSlug: string) => void | Promise<void>;
  onCreateSeries: (title: string, coverPath?: string) => Promise<Series | null>;
}) {
  const [choosing, setChoosing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [seriesSearch, setSeriesSearch] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [pendingConfirm, setPendingConfirm] = useState<{ title: string; detail: string; actionLabel: string; run: () => void | Promise<void> } | null>(null);
  const postHref = safePatreonPostHref(episode.post_id);
  const suggestion = suggestedSeriesForEpisode(episode, series);
  const visibleSeries = series.filter((item) => seriesMatchesSearch(item, seriesSearch));

  function requestAcceptSuggestion() {
    if (!suggestion) {
      setPendingConfirm({
        title: `Mark ${episode.title} reviewed?`,
        detail: "This clears the warning for launch.",
        actionLabel: "Mark Reviewed",
        run: onMarkReviewed
      });
      return;
    }
    setPendingConfirm({
      title: `Move ${episode.title} to ${suggestion.title}?`,
      detail: "The episode card will use this series after you confirm.",
      actionLabel: `Move to ${suggestion.title}`,
      run: async () => {
        let target = suggestion.existing;
        if (!target) {
          const created = await onCreateSeries(suggestion.title, representativeArtwork([episode]));
          if (!created) return;
          target = created;
        }
        if (!target) return;
        await onMoveToSeries([episode.slug], target.slug);
      }
    });
  }

  function requestCreateAndMove() {
    const title = newTitle.trim();
    if (!title) return;
    setPendingConfirm({
      title: `Create ${title} and move this episode?`,
      detail: "A new series shelf will be created before the episode moves.",
      actionLabel: `Create ${title}`,
      run: async () => {
        const created = await onCreateSeries(title, representativeArtwork([episode]));
        if (!created) return;
        await onMoveToSeries([episode.slug], created.slug);
        setCreating(false);
        setNewTitle("");
      }
    });
  }

  async function confirmPendingAction() {
    if (!pendingConfirm) return;
    await pendingConfirm.run();
    setPendingConfirm(null);
  }

  return (
    <article className="fix-item-card">
      <div className="card-badge-row">
        <SeverityBadge severity="Cleanup" />
        <CategoryBadge category={needsSourceSeriesReview(episode) ? "Series needs review" : "Character/pairing needs review"} />
        <StatusBadge status={suggestion ? "Suggested series available" : "Choose series"} />
      </div>
      <div className="fix-item-main">
        <VaultArtwork episode={episode} src={visualEpisodeOverride(episode, visuals)} title={episode.title} unlocked={false} variant="thumb" className="diagnostic-art" fallbackSrc={visuals.fallbackEpisodeArtPath} seed={episode.slug} />
        <div className="fix-item-copy">
          <h2>{episode.title}</h2>
          <div className="fix-series-lines">
            <p><strong>Current series</strong><span>{publicEpisodeSeriesTitle(episode) || "Unassigned"}</span></p>
            <p><strong>Suggested series</strong><span>{suggestion?.title || "Choose visually"}</span></p>
          </div>
        </div>
      </div>
      {choosing ? (
        <div className="simple-modal-panel inline-panel">
          <label className="search-box"><Search size={18} /><input value={seriesSearch} onChange={(event) => setSeriesSearch(event.target.value)} placeholder="Search series title" /></label>
          <div className="series-picker-grid">
            {visibleSeries.map((item) => (
              <button className="series-picker-tile" key={item.slug} type="button" onClick={() => {
                setPendingConfirm({
                  title: `Move ${episode.title} to ${item.title}?`,
                  detail: "The episode card will use this series after you confirm.",
                  actionLabel: `Move to ${item.title}`,
                  run: async () => {
                    await onMoveToSeries([episode.slug], item.slug);
                    setChoosing(false);
                  }
                });
              }}>
                <VaultArtwork src={visualSeriesArtwork(item.slug, allEpisodes.filter((candidate) => candidate.series_slug === item.slug), visuals)} title={item.title} subtitle="Series" tier="paid" variant="cover" className="series-list-art" seed={item.slug} />
                <strong>{item.title}</strong>
                <span>Move Selected Here</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {creating ? (
        <div className="simple-modal-panel inline-panel">
          <TextField label="Series title" help="Only type the visible title." value={newTitle} onChange={setNewTitle} wide />
          <div className="button-row">
            <button className="primary" type="button" onClick={requestCreateAndMove} disabled={!newTitle.trim()}><Plus size={17} />Create Series</button>
            <button className="secondary" type="button" onClick={() => setCreating(false)}>Cancel</button>
          </div>
        </div>
      ) : null}
      {pendingConfirm ? (
        <div className="move-confirm-panel drop-confirm-panel">
          <VaultArtwork episode={episode} src={visualEpisodeOverride(episode, visuals)} title={episode.title} unlocked={false} variant="thumb" className="mini-thumb" fallbackSrc={visuals.fallbackEpisodeArtPath} seed={episode.slug} />
          <div>
            <strong>{pendingConfirm.title}</strong>
            <span>{pendingConfirm.detail}</span>
          </div>
          <div className="button-row compact-actions">
            <button className="primary" type="button" onClick={() => void confirmPendingAction()}>{pendingConfirm.actionLabel}</button>
            <button className="secondary" type="button" onClick={() => setPendingConfirm(null)}>Cancel</button>
          </div>
        </div>
      ) : null}
      <div className="admin-card-actions">
        <button className="primary" type="button" aria-label={suggestion ? "Accept Suggested Series" : "Choose Series"} onClick={() => suggestion ? requestAcceptSuggestion() : setChoosing(true)}>{suggestion ? "Move to Suggested Series" : "Choose Series"}</button>
        {suggestion ? <button className="secondary" type="button" onClick={() => setChoosing((current) => !current)}>Choose Series</button> : null}
        <button className="secondary" type="button" onClick={() => setCreating((current) => !current)}>Create Series</button>
        <button className="secondary" type="button" onClick={() => setPendingConfirm({
          title: `Mark ${episode.title} manually changed?`,
          detail: "This clears the Fix Center warning after you have already corrected the post.",
          actionLabel: "Mark Manually Changed",
          run: onMarkReviewed
        })}>Mark Manually Changed</button>
        <button className="secondary" type="button" aria-label="Ignore" onClick={() => setPendingConfirm({
          title: `Ignore ${episode.title} for launch?`,
          detail: "This hides the warning from launch checks without moving the episode.",
          actionLabel: "Ignore for Launch",
          run: onIgnore
        })}>Ignore for Launch</button>
        <a className="secondary" href={`/episode/${episode.slug}`} target="_blank" rel="noreferrer">Open Episode</a>
        {postHref ? <a className="secondary" href={postHref} target="_blank" rel="noreferrer">Open Patreon Post</a> : null}
      </div>
    </article>
  );
}

function PatreonAgedAccessPanel({ rows, episodes, visuals, status, tierMapOk, onCopy }: { rows: Array<Record<string, unknown>>; episodes: Episode[]; visuals: SiteVisuals; status: Record<string, unknown>; tierMapOk: boolean; onCopy: (value: string) => void }) {
  const [localStatuses, setLocalStatuses] = useState<Record<string, string>>({});
  const [pendingStatus, setPendingStatus] = useState<{ postId: string; next: string; title: string } | null>(null);
  const [statusUndo, setStatusUndo] = useState<{ postId: string; previous?: string; message: string } | null>(null);
  const [wizardIndex, setWizardIndex] = useState(0);
  const manualRows = rows.filter((row) => !isAgedRowCorrect(row));
  const checklist = agedAccessChecklistMarkdown(rows);
  const episodesByPostId = useMemo(() => new Map(episodes.map((episode) => [String(episode.post_id || ""), episode])), [episodes]);
  const wizardRows = manualRows.length ? manualRows : [];
  const wizardRow = wizardRows[wizardIndex] || null;
  useEffect(() => {
    if (!statusUndo) return;
    const timeout = window.setTimeout(() => setStatusUndo(null), 10000);
    return () => window.clearTimeout(timeout);
  }, [statusUndo]);

  function confirmLocalStatus() {
    if (!pendingStatus) return;
    const previous = localStatuses[pendingStatus.postId];
    setLocalStatuses((current) => ({ ...current, [pendingStatus.postId]: pendingStatus.next }));
    setStatusUndo({ postId: pendingStatus.postId, previous, message: `${pendingStatus.title} marked ${pendingStatus.next.toLowerCase()}.` });
    setPendingStatus(null);
  }

  function undoLocalStatus() {
    if (!statusUndo) return;
    setLocalStatuses((current) => {
      const next = { ...current };
      if (statusUndo.previous) next[statusUndo.postId] = statusUndo.previous;
      else delete next[statusUndo.postId];
      return next;
    });
    setStatusUndo(null);
  }

  function requestWizardStatus(next: string) {
    if (!wizardRow) return;
    setPendingStatus({ postId: syncText(wizardRow.postId), next, title: syncText(wizardRow.title) });
  }

  return (
    <section className="admin-panel patreon-aging-panel">
      <div className="section-head">
        <div>
          <span className="eyebrow">Patreon Access</span>
          <h2>Patreon Access</h2>
          <p>These are the portal's All Paid Members collection items that aged out of Platinum Early Access. The portal placement is already correct; this page only confirms Patreon-side audience alignment without changing prices, billing, media, or post text.</p>
        </div>
        <div className="button-row compact-actions">
          <a className="secondary small-link" href="/admin/help#patreon-access"><HelpCircle size={16} />Help for this task</a>
          <a className="secondary small-link" href="/library?tier=all_paid" target="_blank" rel="noreferrer"><Images size={16} />Open All Paid Members</a>
          <button className="secondary" type="button" onClick={() => onCopy(checklist)}><ClipboardList size={16} />Export Manual Checklist</button>
        </div>
      </div>
      <div className="admin-stats dashboard-stats">
        <Stat label="All Paid Members collection" value={syncText(status.agedPlatinumPosts || rows.length)} />
        <Stat label="Already correct" value={syncText(status.alreadyCorrect)} />
        <Stat label="Needs update" value={syncText(status.needsUpdate || manualRows.length)} />
        <Stat label="Updated in report" value={syncText(status.updated)} />
        <Stat label="Skipped safely" value={syncText(status.skipped)} />
        <Stat label="Membership access map" value={tierMapOk ? "Verified" : "Not verified"} />
      </div>
      <div className="inline-warning">
        <AlertTriangle size={17} /> This portal does not change Patreon pricing, billing, media, or post text. Use one post at a time.
      </div>
      {statusUndo ? <div className="inline-success">{statusUndo.message}<button className="secondary small-link toast-undo" type="button" onClick={undoLocalStatus}>Undo</button></div> : null}
      {pendingStatus ? (
        <div className="move-confirm-panel drop-confirm-panel">
          <ClipboardList size={26} />
          <div>
            <strong>Mark {pendingStatus.title} as {pendingStatus.next.toLowerCase()}?</strong>
            <span>This only changes the local checklist state on this screen.</span>
          </div>
          <div className="button-row compact-actions">
            <button className="primary" type="button" onClick={confirmLocalStatus}>Confirm</button>
            <button className="secondary" type="button" onClick={() => setPendingStatus(null)}>Cancel</button>
          </div>
        </div>
      ) : null}
      <div className="aged-access-wizard">
        <div>
          <span className="eyebrow">Guided Access Wizard</span>
          <h3>{wizardRow ? `Task ${wizardIndex + 1} of ${wizardRows.length}` : "No manual Patreon access tasks remain"}</h3>
          <p>{wizardRow ? "Open one post, confirm audience, then mark the task without changing pricing, billing, media, or post text." : "The current API report says every aged Platinum post already includes Members+ and Platinum access on Patreon."}</p>
        </div>
        {wizardRow ? (() => {
          const postId = syncText(wizardRow.postId);
          const postHref = patreonPostHrefFromRow(wizardRow);
          const episode = episodesByPostId.get(postId) || episodes.find((item) => syncText(wizardRow.title) && item.title === syncText(wizardRow.title));
          return (
            <article className="aged-access-guided-card guided-card">
              <a className="aged-access-art-link" href={episode ? `/episode/${episode.slug}` : postHref} target={episode ? undefined : "_blank"} rel={episode ? undefined : "noreferrer"} aria-label={episode ? `Open ${episode.title}` : `Open Patreon post ${postId}`}>
                <VaultArtwork
                  episode={episode}
                  src={episode ? visualEpisodeOverride(episode, visuals) : ""}
                  title={episode?.title || syncText(wizardRow.title) || "All Paid Members item"}
                  unlocked={false}
                  tier={episode ? episodeBadgeTier(episode) : "paid"}
                  variant="thumb"
                  className="aged-access-art"
                  fallbackSrc={visuals.fallbackEpisodeArtPath}
                  lockedFallbackSrc={visuals.lockedPreviewArtPath}
                  seed={`aged-access-wizard-${postId}`}
                />
              </a>
              <div className="card-badge-row">
                <CategoryBadge category="All Paid Members" />
                <StatusBadge status={localStatuses[postId] || syncText(wizardRow.status) || "Needs Manual Patreon Update"} />
              </div>
              <h2>{syncText(wizardRow.title)}</h2>
              <p><strong>Target Patreon access:</strong> {syncText(wizardRow.targetPatreonAccess || status.targetPatreonAccess || "Members+ and Platinum")}</p>
              <p><strong>Current Patreon access:</strong> {syncText(wizardRow.patreonAccess)}</p>
              <div className="admin-card-actions">
                <a className="primary" href={postHref} target="_blank" rel="noreferrer">Open in Patreon</a>
                <button className="secondary" type="button" onClick={() => window.open(postHref, "_blank", "noopener,noreferrer")}>Open next post in CDP browser</button>
                <button className="secondary" type="button" onClick={() => requestWizardStatus("Marked Manually Updated")}>Mark Updated</button>
                <button className="secondary" type="button" onClick={() => requestWizardStatus("Skipped Safely")}>Skip</button>
                <button className="secondary" type="button" onClick={() => requestWizardStatus("Needs Manual Review")}>Needs Review</button>
              </div>
              <div className="button-row compact-actions">
                <button className="secondary small-link" type="button" onClick={() => setWizardIndex((current) => Math.max(0, current - 1))} disabled={wizardIndex === 0}>Previous</button>
                <button className="secondary small-link" type="button" onClick={() => setWizardIndex((current) => Math.min(wizardRows.length - 1, current + 1))} disabled={wizardIndex >= wizardRows.length - 1}>Next</button>
              </div>
              <details className="advanced-fields developer-details">
                <summary>Advanced / Developer Details - only open this if something is broken.</summary>
                <p><strong>Patreon post:</strong> {postId}</p>
                <p><strong>Developer command:</strong> <code>npm run patreon:aging-report</code></p>
              </details>
            </article>
          );
        })() : null}
      </div>
      <div className="diagnostic-card-grid">
        {rows.map((row) => {
          const postId = syncText(row.postId);
          const postHref = patreonPostHrefFromRow(row);
          const episode = episodesByPostId.get(postId) || episodes.find((item) => syncText(row.title) && item.title === syncText(row.title));
          const local = localStatuses[postId];
          return (
            <article className="aged-access-card" key={postId}>
              <a className="aged-access-art-link" href={episode ? `/episode/${episode.slug}` : postHref} target={episode ? undefined : "_blank"} rel={episode ? undefined : "noreferrer"} aria-label={episode ? `Open ${episode.title}` : `Open Patreon post ${postId}`}>
                <VaultArtwork
                  episode={episode}
                  src={episode ? visualEpisodeOverride(episode, visuals) : ""}
                  title={episode?.title || syncText(row.title) || "All Paid Members item"}
                  unlocked={false}
                  tier={episode ? episodeBadgeTier(episode) : "paid"}
                  variant="thumb"
                  className="aged-access-art"
                  fallbackSrc={visuals.fallbackEpisodeArtPath}
                  lockedFallbackSrc={visuals.lockedPreviewArtPath}
                  seed={`aged-access-${postId}`}
                />
              </a>
              <div className="card-badge-row">
                <SeverityBadge severity={isAgedRowCorrect(row) ? "Info" : "Important"} />
                <CategoryBadge category="All Paid Members" />
                <StatusBadge status={local || (isAgedRowCorrect(row) ? "Healthy" : "Needs Manual Patreon Update")} />
              </div>
              <h2>{syncText(row.title)}</h2>
              <p><strong>Portal collection:</strong> All Paid Members</p>
              <p><strong>Publish date:</strong> {syncText(row.publishDate)}</p>
              <p><strong>Portal access label:</strong> {syncText(row.portalClassification)}</p>
              <p><strong>Current Patreon access:</strong> {syncText(row.patreonAccess)}</p>
              <p><strong>Target access:</strong> {syncText(row.targetPatreonAccess || status.targetPatreonAccess || "Members+ and Platinum")}</p>
              <p><strong>Status:</strong> {local || syncText(row.status)}</p>
              <div className="admin-card-actions">
                <button className="secondary" type="button" onClick={() => onCopy(`Open ${postHref}, confirm current access, then target Members+ and Platinum only.`)}>Copy access check note</button>
                <a className="secondary" href={`${postHref}/edit`} target="_blank" rel="noreferrer">Update on Patreon if safe</a>
                {episode ? <a className="secondary" href={`/episode/${episode.slug}`} target="_blank" rel="noreferrer">Open Portal Episode</a> : null}
                <a className="primary" href={postHref} target="_blank" rel="noreferrer">Open Patreon Post</a>
                <button className="secondary" type="button" onClick={() => setPendingStatus({ postId, next: "Marked Manually Updated", title: syncText(row.title) })}>Mark Manually Updated</button>
                <button className="secondary" type="button" onClick={() => setPendingStatus({ postId, next: "Needs Manual Review", title: syncText(row.title) })}>Skip / Needs Manual Review</button>
              </div>
              <details className="advanced-fields developer-details">
                <summary>Advanced / Developer Details - only open this if something is broken.</summary>
                <p><strong>Patreon post:</strong> {postId}</p>
                <p><strong>Developer command:</strong> <code>npm run patreon:aging-report</code></p>
              </details>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  return <span className={`status-badge severity-${slugifyAdmin(severity)}`}>{severity}</span>;
}

function CategoryBadge({ category }: { category: string }) {
  return <span className="status-badge category-badge">{category}</span>;
}

function StatusBadge({ status }: { status: string }) {
  return <span className={`status-badge status-${slugifyAdmin(status)}`}>{status}</span>;
}

interface SeriesSuggestion {
  title: string;
  slug: string;
  existing?: Series;
}

function splitAdminTags(value: string) {
  return value.split(",").map((item) => item.trim()).filter(Boolean).slice(0, 18);
}

function episodeMatchesSearch(episode: Episode, query: string) {
  const q = query.toLowerCase().trim();
  if (!q) return true;
  return [episode.title, episode.series, episode.pairing, ...(episode.characters || [])].join(" ").toLowerCase().includes(q);
}

function seriesMatchesSearch(series: Series, query: string) {
  const q = query.toLowerCase().trim();
  if (!q) return true;
  return series.title.toLowerCase().includes(q);
}

function bestCoverChoices(episodes: Episode[]) {
  return [...new Set(episodes.flatMap((episode) => [
    visualEpisodeAsset(episode),
    episode.series_cover_image,
    episode.poster_image,
    episode.thumbnail_image,
    episode.public_thumbnail,
    episode.member_thumbnail
  ].filter(realPath) as string[]))];
}

function visualEpisodeAsset(episode: Episode) {
  return episode.thumbnail_image || episode.poster_image || episode.series_cover_image || episode.public_thumbnail || "";
}

function suggestedSeriesForEpisode(episode: Episode, series: Series[]): SeriesSuggestion | null {
  const haystack = [episode.title, episode.series, episode.source_series, episode.pairing, ...(episode.characters || []), ...(episode.tags || [])].join(" ").toLowerCase();
  let title = "";
  if (haystack.includes("izumi sena") || haystack.includes("love stage")) title = "Love Stage";
  if (!title) {
    const direct = series.find((item) => item.slug !== episode.series_slug && haystack.includes(item.title.toLowerCase()));
    if (direct) title = direct.title;
  }
  if (!title || title === episode.series) return null;
  const slug = slugifyAdmin(title);
  return { title, slug, existing: series.find((item) => item.slug === slug) };
}

function sourceTitleForSeries(series: Series, sourceSeries: NonNullable<CatalogResponse["sourceSeries"]> = []) {
  const sourceSlug = series.source_series_slug || series.slug;
  if (sourceSlug === "yaoigen-originals") return "YaoiGen Originals";
  return sourceSeries.find((item) => item.slug === sourceSlug)?.title || series.title;
}

function episodeMovePatch(episode: Episode, series: Series, sourceSeries: NonNullable<CatalogResponse["sourceSeries"]> = []): Partial<Episode> {
  const sourceSlug = series.source_series_slug || series.slug;
  return {
    series: series.title,
    series_slug: series.slug,
    yaoigen_series: series.title,
    yaoigen_series_slug: series.slug,
    source_series: sourceTitleForSeries(series, sourceSeries),
    source_series_slug: sourceSlug,
    collection: series.title,
    review_status: "mapped",
    review_flags: [],
    confidence: Math.max(Number(episode.confidence || 0), 0.82)
  };
}

function isLowConfidenceEpisode(episode: Episode) {
  return (episode.confidence || 0) < 0.7 || episode.review_status === "needs_review" || Boolean(episode.review_flags?.includes("low_confidence_character"));
}

function needsSourceSeriesReview(episode: Episode) {
  return Boolean(episode.review_flags?.includes("unmapped_character_or_source") || episode.review_flags?.includes("low_confidence_source"));
}

function reviewReason(episode: Episode) {
  const flags = episode.review_flags?.length ? episode.review_flags.join(", ") : "confidence check";
  return `${flags}; confidence ${Number(episode.confidence || 0).toFixed(2)}.`;
}

function suggestedMetadataFix(episode: Episode) {
  if (episode.source_series_slug === "yaoigen-originals" && !episode.characters?.length) {
    return { safe: true, text: "Confirm this as YaoiGen Originals with no named character/pairing." };
  }
  if (episode.source_series || episode.characters?.length || episode.pairing) {
    return { safe: true, text: "Keep the current visible mapping and mark this item reviewed." };
  }
  return { safe: false, text: "No unambiguous suggestion. Choose a series visually or ignore for launch." };
}

function uniqueEpisodes(items: Episode[]) {
  const seen = new Set<string>();
  return items.filter((episode) => {
    if (seen.has(episode.slug)) return false;
    seen.add(episode.slug);
    return true;
  });
}

function slugifyAdmin(value: string) {
  return String(value || "")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "item";
}

function patreonAgingRows(status: Record<string, unknown>) {
  return Array.isArray(status.rows) ? status.rows as Array<Record<string, unknown>> : [];
}

function isAgedRowCorrect(row: Record<string, unknown>) {
  return /already correct|updated/i.test(syncText(row.status));
}

function patreonPostHrefFromRow(row: Record<string, unknown>) {
  const path = syncText(row.postPath);
  if (/^https:\/\/www\.patreon\.com\/posts\//i.test(path)) return path;
  if (/^\/posts\/\d+/i.test(path)) return `https://www.patreon.com${path}`;
  return safePatreonPostHref(syncText(row.postId)) || "https://www.patreon.com/YaoiGen";
}

function reviewChecklistMarkdown(items: Episode[], sourceTotal: number, characterTotal: number) {
  return [
    "# YaoiGen Fix Center Checklist",
    "",
    `Series needs review total: ${sourceTotal}`,
    `Character / pairing review total: ${characterTotal}`,
    "",
    "| Title | Portal URL | Patreon Post | Current Series | Characters | Reason | Action |",
    "| --- | --- | --- | --- | --- | --- | --- |",
    ...items.map((episode) => `| ${escapeTable(episode.title)} | /episode/${episode.slug} | ${safePatreonPostHref(episode.post_id) || "missing"} | ${escapeTable(episode.series || "Unassigned")} | ${escapeTable(episode.characters?.join(", ") || "None")} | ${escapeTable(reviewReason(episode))} | Choose a series, accept, mark reviewed, or ignore |`)
  ].join("\n");
}

function agedAccessChecklistMarkdown(rows: Array<Record<string, unknown>>) {
  return [
    "# Patreon Access Manual Checklist",
    "",
    "For each row: open Patreon, edit only audience/access, add Members+ while keeping Platinum access, save, then rerun `npm run patreon:aging-report`.",
    "",
    "| Post ID | Title | Current Access | Target Access | Link | Status |",
    "| --- | --- | --- | --- | --- | --- |",
    ...rows.map((row) => `| ${escapeTable(syncText(row.postId))} | ${escapeTable(syncText(row.title))} | ${escapeTable(syncText(row.patreonAccess))} | ${escapeTable(syncText(row.targetPatreonAccess))} | ${patreonPostHrefFromRow(row)} | ${escapeTable(syncText(row.status))} |`)
  ].join("\n");
}

function escapeTable(value: string) {
  return String(value || "").replaceAll("|", "\\|").replaceAll("\n", " ");
}

function imageSourceForPath(path: string) {
  if (!path) return "default";
  if (/^\/patreon-assets\//.test(path)) return isRepeatedImportArt(path) ? "generated fallback" : "real Patreon asset";
  if (/^\/assets\//.test(path)) return "site asset";
  return "invalid";
}

function Stat({ label, value }: { label: string; value: string }) {
  return <div><strong>{value}</strong><span>{label}</span></div>;
}

function AdminForm({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <section className="admin-panel admin-form-panel">
      <div className="section-head">
        <div>
          <span className="eyebrow">{title}</span>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
      </div>
      <div className="visual-form-grid">{children}</div>
    </section>
  );
}

function TextField({ label, help, value, onChange, wide }: { label: string; help: string; value: string; onChange: (value: string) => void; wide?: boolean }) {
  return <label className={wide ? "wide" : ""}>{label}<small>{help}</small><input value={value} onChange={(event) => onChange(event.target.value)} /></label>;
}

function TextArea({ label, help, value, onChange, wide }: { label: string; help: string; value: string; onChange: (value: string) => void; wide?: boolean }) {
  return <label className={wide ? "wide" : ""}>{label}<small>{help}</small><textarea value={value} onChange={(event) => onChange(event.target.value)} /></label>;
}

function SelectField({ label, help, value, options, optionLabels, onChange }: { label: string; help: string; value: string; options: string[]; optionLabels?: Record<string, string>; onChange: (value: string) => void }) {
  return <label>{label}<small>{help}</small><select value={value} onChange={(event) => onChange(event.target.value)}>{options.map((option) => <option key={option} value={option}>{optionLabels?.[option] || option}</option>)}</select></label>;
}

function SwitchField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <label className="switch-field">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function PathField({ label, help, value, onChange }: { label: string; help: string; value: string; onChange: (value: string) => void }) {
  const safe = isSafeAdminPath(value);
  return (
    <label className={safe ? "" : "field-error"}>{label}<small>{help}</small><input value={value} onChange={(event) => onChange(event.target.value)} placeholder="/patreon-assets/.../thumb.webp or /assets/name.svg" />{safe ? null : <em>Unsafe path. Use only /patreon-assets/... or /assets/...</em>}</label>
  );
}

function SaveBar({ onSave, disabled }: { onSave: () => void; disabled?: boolean }) {
  return <div className="save-bar wide"><button className="primary" type="button" onClick={onSave} disabled={disabled}><Save size={17} />Save settings</button></div>;
}

function ReviewQueue({ title, episodes }: { title: string; episodes: Episode[] }) {
  return (
    <div className="review-panel">
      <h2>{title}</h2>
      {episodes.length ? episodes.slice(0, 5).map((episode) => <a key={episode.slug} href={`/episode/${episode.slug}`}>{episode.title}<span>{episode.review_status || episode.tier_required}</span></a>) : <p>No current items.</p>}
    </div>
  );
}

function ReviewList({ title, items }: { title: string; items: Array<{ href: string; title: string; meta: string }> }) {
  return (
    <div className="review-panel">
      <h2>{title}</h2>
      {items.length ? items.slice(0, 10).map((item) => <a key={`${item.href}-${item.meta}`} href={item.href}>{item.title}<span>{item.meta}</span></a>) : <p>No current items.</p>}
    </div>
  );
}

function pendingPostsFromSync(sync: Record<string, unknown>) {
  return Array.isArray(sync.pendingPosts) ? sync.pendingPosts as PendingPatreonPost[] : [];
}

function pendingReviewFromSync(sync: Record<string, unknown>) {
  return sync.pendingPostReview && typeof sync.pendingPostReview === "object"
    ? sync.pendingPostReview as Record<string, unknown>
    : {};
}

function patreonAdminPostHref(postId?: string) {
  return safePatreonPostHref(postId) || "#";
}

function syncText(value: unknown) {
  if (value === null || value === undefined || value === "") return "Unknown";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? String(value.length) : "0";
  return String(value);
}

function InfoBlock({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return <div className="info-block">{icon}<h2>{title}</h2><p>{text}</p></div>;
}

function EmptyState({ title, message, action }: { title: string; message: string; action?: React.ReactNode }) {
  return <div className="empty-state"><h2>{title}</h2><p>{message}</p>{action ? <div className="button-row">{action}</div> : null}</div>;
}

function ErrorBlock({ message }: { message: string }) {
  return <section className="narrow"><div className="notice"><h1>Request failed</h1><p>{message}</p><a className="secondary" href="/">Return home</a></div></section>;
}

function NotFound() {
  return <section className="narrow"><div className="notice"><h1>Not found</h1><p>This route does not exist.</p><a className="secondary" href="/">Go home</a></div></section>;
}

document.body.classList.add("app-mounted");
createRoot(document.getElementById("root")!).render(<App />);
